import { createFileRoute } from "@tanstack/react-router";
import { CheckCircleIcon } from "@heroicons/react/24/outline";
import useTranslation from "../../hooks/useTranslation";

export type ReleaseEntry = {
  id: string;
  timestamp: string;
  title: string;
  summary: string;
  userHighlights: string[];
  technicalNotes: string[];
};

type ReleaseCardEntry = ReleaseEntry & {
  entryNumber: number;
};

export const releaseEntries: ReleaseEntry[] = [
  {
    id: "vocabulary-progress-counter-now-shows-stored-total-for-selected-languages",
    timestamp: "2026-05-01T22:07:18+02:00",
    title: "Vocabulary Progress counter now shows stored total for selected languages",
    summary:
      "The top list counter now reflects the full stored vocabulary total for the selected languages, instead of only currently loaded client rows.",
    userHighlights: [
      "`Angezeigt` now stays aligned with account-side totals for the active language selection.",
      "The number no longer depends on how many pages were loaded in the current session.",
      "List rendering and filtering behavior remain unchanged.",
    ],
    technicalNotes: [
      "Updated the list summary count source in `src/routes/app/vocabulary_progress.tsx` to use `totalVocabularyCount` from paginated API metadata.",
      "Kept empty-state behavior based on the actual visible filtered rows.",
      "No backend API changes were required.",
    ],
  },
  {
    id: "vocabulary-progress-language-selection-now-resets-focus-to-all-selected",
    timestamp: "2026-05-01T22:04:55+02:00",
    title: "Vocabulary Progress now resets view focus after language selection changes",
    summary:
      "When selected languages are changed, the list view now automatically resets to `all selected languages` so the shown count and rows update as expected.",
    userHighlights: [
      "Adding another language now immediately updates the visible list scope instead of staying silently pinned to the previous single-language focus.",
      "`Angezeigt` now changes consistently when language selection changes.",
      "Users can still manually focus a single language afterwards if needed.",
    ],
    technicalNotes: [
      "Added an effect in `src/routes/app/vocabulary_progress.tsx` that sets `activeWorkspaceLanguage` to `ALL_SELECTED` whenever `effectiveLanguageFilters` changes.",
      "Kept existing filtering, sorting, and pagination logic unchanged.",
      "Preserved explicit manual focus selection behavior after auto-reset.",
    ],
  },
  {
    id: "vocabulary-progress-showing-counter-now-matches-rendered-list",
    timestamp: "2026-05-01T22:02:52+02:00",
    title: "Vocabulary Progress `Angezeigt` counter now matches rendered rows",
    summary:
      "The shown-items counter now uses the same derived list that is rendered in the table/cards, preventing mismatches in the header summary.",
    userHighlights: [
      "`Angezeigt` now reflects exactly what is currently visible in the vocabulary list.",
      "Empty-state behavior now follows the same visible-list source as the rendered rows.",
      "List sorting and filtering behavior remains unchanged.",
    ],
    technicalNotes: [
      "Added a `visibleVocabularyCount` derived from `sortedVocabulary.length` in `src/routes/app/vocabulary_progress.tsx`.",
      "Updated the summary counter and empty-state condition to use `visibleVocabularyCount`.",
      "Kept pagination, language scope, and filter logic unchanged.",
    ],
  },
  {
    id: "vocabulary-progress-language-selector-no-longer-shows-misleading-counts",
    timestamp: "2026-05-01T22:00:57+02:00",
    title: "Vocabulary Progress language selector now avoids misleading counts",
    summary:
      "Language selection controls no longer show per-language numbers that could be inconsistent with the current filtered dataset.",
    userHighlights: [
      "Language selection now focuses on clear scope choice instead of potentially inaccurate per-language count labels.",
      "Language focus dropdown now shows clean language names only.",
      "Reliable totals remain visible in the dedicated list summary area.",
    ],
    technicalNotes: [
      "Removed per-language count rendering from workspace language checklist and focus dropdown in `src/routes/app/vocabulary_progress.tsx`.",
      "Adjusted active-language summary copy for the `all languages` case to avoid count coupling to partial datasets.",
      "Kept existing filtering and sorting behavior unchanged.",
    ],
  },
  {
    id: "vocabulary-progress-redesign-for-clear-configuration-and-better-responsiveness",
    timestamp: "2026-05-01T21:59:43+02:00",
    title: "Vocabulary Progress now has a clearer, more configurable layout",
    summary:
      "The Vocabulary Progress page was redesigned to keep existing behavior while improving visual structure, language configuration clarity, and mobile/desktop usability.",
    userHighlights: [
      "Page sections are now ordered with clearer hierarchy from overview to configuration and then vocabulary list.",
      "Language setup is easier to manage with a dedicated selection panel and a separate view-focus control.",
      "List context counters are grouped in a focused summary block for faster interpretation.",
    ],
    technicalNotes: [
      "Refactored `src/routes/app/vocabulary_progress.tsx` layout and responsive structure without changing vocabulary filtering, sorting, or action logic.",
      "Split language configuration from active view focus controls to avoid duplicate state-changing chips.",
      "Added E2E coverage in `e2e/vocabulary-progress-statistics.spec.ts` for desktop/mobile layout usability checks.",
    ],
  },
  {
    id: "reeprise-hook-order-fixed-for-loading-to-loaded-transition",
    timestamp: "2026-05-01T21:34:11+02:00",
    title: "Reeprise loading transition no longer triggers hook-order errors",
    summary:
      "A hook-order issue in Reeprise was fixed so the page no longer crashes with `Rendered more hooks than during the previous render` when data transitions from loading to loaded.",
    userHighlights: [
      "Reeprise now loads reliably without hook-order runtime errors.",
      "The loading-to-content transition is stable again.",
      "No user-facing flow or layout behavior was intentionally changed.",
    ],
    technicalNotes: [
      "Updated `src/routes/app/reeprise/index.tsx` to compute feed data from a safe fallback (`data?.pages ?? []`) before loading checks.",
      "Moved the loading early-return below hook declarations so hook execution order remains stable across renders.",
      "Kept existing feed/filter behavior unchanged.",
    ],
  },
  {
    id: "small-screen-layout-now-uses-page-scroll-instead-of-nested-app-scroller",
    timestamp: "2026-05-01T21:32:55+02:00",
    title: "Small-screen app layout now uses normal page scrolling",
    summary:
      "On small-screen layouts (including narrow desktop windows), the app now uses regular document scrolling instead of a nested internal scroll container to prevent trackpad scroll lockups.",
    userHighlights: [
      "Scrolling in narrow-window/mobile-style layouts now follows normal browser page scroll behavior.",
      "Trackpad scroll no longer depends on hitting a specific inner container.",
      "Desktop layout keeps its existing independent in-app scroll regions.",
    ],
    technicalNotes: [
      "Updated `src/routes/app/route.tsx` so `app-content-scroll` uses `overflow-y-visible`/`min-h-full` below `md` breakpoints.",
      "Kept existing desktop behavior via `md:overflow-y-auto` and `md:overscroll-contain`.",
      "No route-level navigation or data-loading behavior was changed.",
    ],
  },
  {
    id: "copy-quality-guardrails-added-for-app-context-wording",
    timestamp: "2026-05-01T21:11:12+02:00",
    title: "Copy-quality guardrails now enforce app-context wording checks",
    summary:
      "The repo now includes explicit process and test guardrails so user-facing wording is validated against app context and recurring German terminology issues are caught earlier.",
    userHighlights: [
      "PRs now include a checklist item to verify that copy matches real feature behavior and screen intent.",
      "A shared terminology guideline now documents preferred German UI wording and disallowed terms.",
      "A regression test now blocks `Sprachraum` from reappearing in critical vocabulary empty-state messages.",
    ],
    technicalNotes: [
      "Added `.github/pull_request_template.md` with copy-quality and review checklist items.",
      "Added `docs/ui-terminology-guidelines.md` with German terminology guidance and required app-context checks.",
      "Added `src/assets/translations.copy.test.ts` to enforce critical German empty-state wording constraints.",
    ],
  },
  {
    id: "reeprise-feed-empty-state-german-copy-now-uses-sprachauswahl",
    timestamp: "2026-05-01T21:05:56+02:00",
    title: "Reeprise feed empty-state German copy now uses natural wording",
    summary:
      "The German empty-state copy in Reeprise feed now uses `Sprachauswahl` instead of `Sprachraum` and a more natural title phrasing.",
    userHighlights: [
      "German empty-state title now reads `Hier beginnt dein Feed`.",
      "German body text now references `aktuelle Sprachauswahl` instead of `Sprachraum`.",
      "The guidance remains the same: create a text and it will appear in the feed.",
    ],
    technicalNotes: [
      "Updated German values for `reeprise_feed_empty_title` and `reeprise_feed_empty_body` in `src/assets/translations.json`.",
      "No behavioral or layout logic was changed.",
      "English copy remains unchanged.",
    ],
  },
  {
    id: "reeprise-feed-empty-language-space-now-shows-illustrated-empty-state",
    timestamp: "2026-05-01T21:04:10+02:00",
    title: "Reeprise feed now shows an illustrated empty state for empty language spaces",
    summary:
      "When a language space has no visible feed stories, Reeprise now shows a friendly empty state instead of a confusing `Load more` button.",
    userHighlights: [
      "Empty language-space feeds now show a clear and friendly placeholder card.",
      "The feed no longer offers `Load more` when there are no visible stories yet.",
      "Guidance now points to creating a new story so the next step is obvious.",
    ],
    technicalNotes: [
      "Updated feed rendering in `src/routes/app/reeprise/index.tsx` to gate pagination CTA by visible feed items.",
      "Added a new illustrated `FeedEmptyState` component and related i18n keys in `src/assets/translations.json`.",
      "Added Playwright regression coverage in `e2e/reeprise-generation.spec.ts` for empty language-space feed behavior.",
    ],
  },
  {
    id: "german-empty-state-wording-now-uses-sprachauswahl",
    timestamp: "2026-05-01T21:02:51+02:00",
    title: "German empty-state wording now uses more natural terminology",
    summary:
      "German vocabulary empty-state messaging now uses `Sprachauswahl` instead of `Sprachraum` to better match common UI wording.",
    userHighlights: [
      "German empty state now reads more naturally in both vocabulary review surfaces.",
      "The message still keeps the same action guidance: create new content in this language.",
      "Copy stays consistent across spaced preview and vocabulary preview.",
    ],
    technicalNotes: [
      "Updated German text for `spaced_preview_language_empty` in `src/assets/translations.json`.",
      "Updated German text for `vocabulary_preview_empty_state` in `src/assets/translations.json`.",
      "Kept existing English wording unchanged.",
    ],
  },
  {
    id: "language-space-empty-state-copy-now-matches-separated-language-spaces",
    timestamp: "2026-05-01T21:00:44+02:00",
    title: "Language-space empty-state copy now matches separated spaces",
    summary:
      "Empty-state messaging in vocabulary review now reflects separated language spaces and no longer tells you to switch languages for more items.",
    userHighlights: [
      "Empty-state text now clearly explains that this language space currently has no vocabulary.",
      "Guidance now points to creating new content in the same language instead of switching languages.",
      "Copy is now consistent between spaced preview and vocabulary preview empty states.",
    ],
    technicalNotes: [
      "Updated `spaced_preview_language_empty` German/English text in `src/assets/translations.json`.",
      "Updated `vocabulary_preview_empty_state` German/English text in `src/assets/translations.json`.",
      "Aligned both keys to the same separated-language-space wording for consistent UX behavior.",
    ],
  },
  {
    id: "esperanto-added-as-a-supported-learning-language",
    timestamp: "2026-05-01T17:50:04+02:00",
    title: "Esperanto is now available as a learning language",
    summary:
      "Esperanto can now be selected across text generation and language-space flows so it behaves like the other supported learning languages.",
    userHighlights: [
      "Esperanto now appears in language pickers used for learning-language selection.",
      "Language-space controls can now scope reading and vocabulary flows to Esperanto.",
      "Quick-translate language labels now include Esperanto where language names are shown.",
    ],
    technicalNotes: [
      "Extended backend language enums and language-code normalization in `barkguide/reeprise/models.py` and `barkguide/reeprise/views.py` with `ESPERANTO` support.",
      "Added Esperanto TTS/pronunciation language mappings in `barkguide/reeprise/tasks.py`.",
      "Added `TEXT_LANGUAGES` Esperanto metadata and translation key `language_esperanto` in `src/constants.ts` and `src/assets/translations.json`.",
    ],
  },
  {
    id: "quick-translate-ui-now-matches-shared-card-controls",
    timestamp: "2026-05-01T17:43:03+02:00",
    title: "Übersetzen unterwegs now matches shared app control styling",
    summary:
      "Quick translate card controls have been aligned to the same sizing, color, and spacing patterns used in the rest of the app utility cards.",
    userHighlights: [
      "Buttons and chips now use consistent sizing and typography with neighboring cards.",
      "Input area now feels sharper and less prototype-like while still supporting longer text.",
      "Ambiguous source state now uses the same neutral surface and border language as other controls.",
    ],
    technicalNotes: [
      "Adjusted `src/components/QuickTranslateCard.tsx` control classes to align with shared border-first utility-card controls.",
      "Reduced custom visual treatments and removed amber-heavy styling from ambiguity controls.",
      "Kept auto-grow behavior while tightening input line-height and spacing to match existing card rhythm.",
    ],
  },
  {
    id: "quick-translate-composer-ui-now-uses-a-polished-auto-grow-layout",
    timestamp: "2026-05-01T17:39:38+02:00",
    title: "Übersetzen unterwegs now uses a polished composer layout",
    summary:
      "The quick-translate card now uses an auto-growing input composer and a cleaner action hierarchy, replacing the prototype-like fixed input/button layout.",
    userHighlights: [
      "The input now grows with longer text instead of clipping in a fixed-height field.",
      "Primary actions are vertically centered and consistently aligned across states.",
      "Source-confirmation guidance and typo suggestion states now sit in a clearer secondary panel.",
    ],
    technicalNotes: [
      "Refactored `src/components/QuickTranslateCard.tsx` layout structure to a composer + action-bar pattern with consistent spacing and typography.",
      "Implemented textarea auto-resize with a bounded min/max height and overflow handoff for long content.",
      "Standardized button heights (`h-10`) and inline-flex centering for translate/save actions to remove baseline drift.",
    ],
  },
  {
    id: "quick-translate-now-suggests-typo-corrections-on-unclear-input",
    timestamp: "2026-05-01T17:38:32+02:00",
    title: "Übersetzen unterwegs now suggests likely typo corrections",
    summary:
      "When quick translate cannot confidently detect the source language, it can now suggest a likely corrected input such as `chien` for `chin` instead of silently failing direction routing.",
    userHighlights: [
      "Unclear input now offers a `Did you mean …?` correction chip directly in the quick translate card.",
      "You can apply the suggestion in one tap and retry translation immediately.",
      "If no reliable correction exists, the card now clearly asks for manual source-language selection.",
    ],
    technicalNotes: [
      "Extended `/api/reeprise/translate/` ambiguity response in `barkguide/reeprise/views.py` with typo-suggestion fields (`did_you_mean`, `did_you_mean_language`).",
      "Updated `src/components/QuickTranslateCard.tsx` to render a one-tap suggestion action and a clearer no-match fallback hint.",
      "Added backend regression coverage in `barkguide/reeprise/tests.py` (`test_quick_translate_returns_spelling_suggestion_when_detection_is_uncertain`).",
    ],
  },
  {
    id: "vocabulary-progress-language-controls-no-longer-duplicate-between-cards",
    timestamp: "2026-05-01T17:34:13+02:00",
    title: "Vocabulary Progress now uses one language control area",
    summary:
      "Language scope controls are now shown only in the workspace section, removing duplicated language selectors that previously competed with each other.",
    userHighlights: [
      "Language selection and language focus now live in one clear place at the top of the page.",
      "The lower filter card now focuses only on stage, search, and sorting.",
      "This removes repeated language UI that could suggest conflicting filter states.",
    ],
    technicalNotes: [
      "Removed the duplicated language dropdown block from the lower filter card in `src/routes/app/vocabulary_progress.tsx`.",
      "Deleted the now-unused `LanguageFilterDropdown` component from the same file.",
      "Kept workspace chip logic as the single source of truth for language scoping and focus.",
    ],
  },
  {
    id: "vocabulary-progress-workspace-counts-now-use-consistent-scope",
    timestamp: "2026-05-01T17:30:12+02:00",
    title: "Vocabulary Progress workspace counts now use one consistent scope",
    summary:
      "Workspace chip counts now use the same visible-data basis, and the account-wide total is shown separately to avoid contradictory numbers in the same control group.",
    userHighlights: [
      "The `All languages` chip now matches the same loaded/visible scope as the per-language chips.",
      "A separate line now shows the account-wide total (`Total in account`) for full-context reference.",
      "Workspace number reading is now consistent and easier to trust at a glance.",
    ],
    technicalNotes: [
      "Updated workspace chip count source in `src/routes/app/vocabulary_progress.tsx` to use summed visible language counts instead of mixed global totals.",
      "Added separate global-total hint text below workspace chips with existing paginated total count.",
      "Added new translation key `vocabulary_progress_workspace_total_hint` in `src/assets/translations.json`.",
    ],
  },
  {
    id: "quick-translate-direction-ui-now-reflects-real-detected-routing",
    timestamp: "2026-05-01T17:28:29+02:00",
    title: "Übersetzen unterwegs now shows the real translation direction",
    summary:
      "The quick-translate UI no longer shows a fixed target that can be wrong before detection. Direction labels now reflect the actual resolved source and target after routing.",
    userHighlights: [
      "The card no longer implies a fixed target language before source detection is resolved.",
      "After translation, direction is shown as `Source -> Target`, matching the real translation route.",
      "When source confirmation is required, the primary action now clearly asks for source confirmation before translating.",
    ],
    technicalNotes: [
      "Updated `src/components/QuickTranslateCard.tsx` to render direction metadata from backend `resolved_source_lang` and `resolved_target_lang` instead of prefilled target assumptions.",
      "Adjusted primary CTA states to use neutral and source-confirmation copy before routing is finalized.",
      "Added new translation keys in `src/assets/translations.json` for neutral and source-confirmation translate actions.",
    ],
  },
  {
    id: "vocabulary-progress-now-has-a-parallel-language-workspace-layout",
    timestamp: "2026-05-01T17:27:11+02:00",
    title: "Vocabulary Progress now has a parallel language workspace",
    summary:
      "Vocabulary Progress has been restructured so you can configure active languages quickly and switch focus instantly between language-specific views.",
    userHighlights: [
      "A new language workspace section makes active language selection visible and fast.",
      "You can now switch between `All selected languages` and one-language focus views in one tap.",
      "Per-language counts are shown directly on workspace chips to make scope decisions clearer.",
    ],
    technicalNotes: [
      "Added a new workspace UI section in `src/routes/app/vocabulary_progress.tsx` with active-language chips and view-mode chips.",
      "Introduced page-local focus state (`ALL_SELECTED` or concrete language id) and routed list/stage counts through that focus logic.",
      "Added new translation keys in `src/assets/translations.json` for workspace labels and guidance copy.",
    ],
  },
  {
    id: "vocabulary-progress-now-supports-multi-language-scope-without-space-lock",
    timestamp: "2026-05-01T17:11:45+02:00",
    title: "Vocabulary Progress now supports parallel language scopes",
    summary:
      "Vocabulary Progress no longer inherits a locked single language space. You can now select multiple languages (for example French and Spanish) and the page uses that exact scope consistently.",
    userHighlights: [
      "Language scope on Vocabulary Progress is now a true multi-select.",
      "You can learn in parallel tracks like `French + Spanish` in one view.",
      "The page no longer auto-overrides your selection with a global language-space lock.",
    ],
    technicalNotes: [
      "Removed `useLanguageSpace` coupling from `src/routes/app/vocabulary_progress.tsx` so language filtering is page-local and explicit.",
      "Persisted selected language filters with local storage key `vocabulary-progress-language-filters-v1`.",
      "Added `resolveVocabularyProgressLanguageFilters` utility and regression tests to enforce empty=all, multi-select retention, and invalid-value cleanup.",
    ],
  },
  {
    id: "quick-translate-now-targets-current-language-space-only",
    timestamp: "2026-05-01T17:06:30+02:00",
    title: "Übersetzen unterwegs now follows your current language space",
    summary:
      "Übersetzen unterwegs now provides one clear translation path: it translates into the language of your currently active language space instead of showing multiple target options.",
    userHighlights: [
      "The target language is now fixed to your active language space for a cleaner, faster translation flow.",
      "When language space is set to `ALL`, the feature safely falls back to your mother tongue as target language.",
      "The card now prioritizes one primary action state (`Translate`) with clearer target/source context chips.",
    ],
    technicalNotes: [
      "Reworked `src/components/QuickTranslateCard.tsx` to remove multi-target buttons and route all translation/save requests through a single resolved target language.",
      "Added `src/utils/quickTranslateTargetLanguage.ts` to centralize language-space target resolution and keep behavior consistent.",
      "Added regression coverage in `src/utils/quickTranslateTargetLanguage.test.ts` for both fixed-space and `ALL` fallback behavior.",
    ],
  },
  {
    id: "generated-texts-loading-no-longer-breaks-on-pagination-count",
    timestamp: "2026-05-01T17:05:52+02:00",
    title: "Loading no longer gets stuck from generated-text list pagination",
    summary:
      "A pagination regression that could return a server error while loading generated texts has been fixed, so app loading no longer hangs on that endpoint.",
    userHighlights: [
      "Main app loading now proceeds normally when the generated-text list is requested.",
      "The generated-text API response now consistently includes a stable `count` value.",
      "This removes the backend crash that could leave the app stuck on loading.",
    ],
    technicalNotes: [
      "Updated `MyCursorPagination` in `barkguide/reeprise/utils/pagination.py` to capture total count during `paginate_queryset()` instead of reading `self.page.paginator` from list-backed cursor pages.",
      "Added safe fallback logic in `get_paginated_response()` so count remains available even when queryset count is unavailable.",
      "Added backend regression coverage in `barkguide/reeprise/tests.py` (`test_generated_texts_list_includes_count_without_crashing`).",
    ],
  },
  {
    id: "vocabulary-progress-now-defaults-to-all-languages-for-list-visibility",
    timestamp: "2026-05-01T17:02:30+02:00",
    title: "Vocabulary Progress now defaults to all languages",
    summary:
      "Vocabulary Progress now starts with all languages visible by default, preventing empty lists caused by an implicit English-only language-space default.",
    userHighlights: [
      "Opening Vocabulary Progress now shows your full vocabulary list by default.",
      "You can still narrow to specific languages using the language filter.",
      "The empty-state issue from an implicit language lock is removed.",
    ],
    technicalNotes: [
      "Changed Vocabulary Progress language-space fallback from `ENGLISH` to `ALL` in `src/routes/app/vocabulary_progress.tsx`.",
      "Kept existing language-space behavior intact when a user explicitly sets a fixed language space.",
      "No backend API contract changes were required; this is a frontend default-filter fix.",
    ],
  },
  {
    id: "vocabulary-progress-loads-faster-with-stable-total-count",
    timestamp: "2026-05-01T16:56:53+02:00",
    title: "Vocabulary Progress now loads faster with a stable total count",
    summary:
      "Vocabulary Progress no longer auto-loads the entire vocabulary list on page open, and the `Angezeigt` value now shows a stable total immediately.",
    userHighlights: [
      "Opening `Vocabulary Progress` now feels faster on larger vocabulary sets.",
      "Changing `Sortierung` remains responsive because the page no longer keeps appending all pages in the background.",
      "`Angezeigt` now reflects the full total from the start instead of increasing while pages stream in.",
    ],
    technicalNotes: [
      "Extended cursor pagination response in `barkguide/reeprise/utils/pagination.py` to include a `count` field alongside `next`, `previous`, and `results`.",
      "Updated vocabulary list query typing in `src/hooks/usePaginatedVocabularyList.ts` to accept paginated `count` metadata.",
      "Removed eager `fetchNextPage()` loop in `src/routes/app/vocabulary_progress.tsx`, switched to explicit `Load more`, and bound `Angezeigt` to backend `count` for stable rendering.",
    ],
  },
  {
    id: "vocabulary-progress-statistics-card-labels-now-render-consistently",
    timestamp: "2026-05-01T07:40:20+02:00",
    title: "Vocabulary Progress statistics labels now render consistently",
    summary:
      "Statistics card labels on Vocabulary Progress no longer disappear intermittently while app language settings are still loading.",
    userHighlights: [
      "Vocabulary statistics cards now always show their label text.",
      "No more blank label cards during initial page load or refresh.",
      "Number values and existing translations remain unchanged.",
    ],
    technicalNotes: [
      "Updated statistics card list rendering in `src/routes/app/vocabulary_progress.tsx` to use stable internal React keys instead of translated label text.",
      "This prevents duplicate empty-string keys when `translate()` temporarily returns empty while user settings are loading.",
      "Added Playwright regression coverage in `e2e/vocabulary-progress-statistics.spec.ts` to assert non-empty labels in the vocabulary statistics section.",
    ],
  },
  {
    id: "vocabulary-word-column-now-consistently-shows-seen-surface-form",
    timestamp: "2026-05-01T07:35:33+02:00",
    title: "Vocabulary word column now consistently shows the seen form",
    summary:
      "Vocabulary entries now use one consistent display rule: the `WORT` column shows the seen/clicked surface form, while base form details remain available separately.",
    userHighlights: [
      "Entries like `te pose-t-il tant` and `de l'aide` now follow the same primary-word display logic.",
      "The main word label no longer switches between dictionary form and surface form depending on grammar metadata.",
      "Base form information is still available through `Grundform` when present.",
    ],
    technicalNotes: [
      "Simplified `getVocabularyPrimaryWord` in `src/utils/vocabularyTranslationDetails.ts` to always resolve from `source_surface_form` (fallback: stored `word`).",
      "Removed noun-specific canonical/plural promotion logic that previously replaced surface forms in some cards.",
      "Updated regression expectations in `src/utils/vocabularyTranslationDetails.test.ts` for noun and apostrophe-form scenarios.",
    ],
  },
  {
    id: "vocabulary-progress-search-now-finds-phrases-in-sentence-context",
    timestamp: "2026-05-01T07:31:06+02:00",
    title: "Vocabulary Progress search now finds phrase entries more reliably",
    summary:
      "Vocabulary Progress search now checks sentence context and normalizes punctuation variants, so phrase entries are easier to find even when apostrophes or dashes differ.",
    userHighlights: [
      "Phrase searches like `te pose-t-il tant` are now matched more reliably.",
      "Search now includes sentence text in addition to word and translation.",
      "Search placeholder text now reflects the broader search scope.",
    ],
    technicalNotes: [
      "Added normalized search matching in `src/routes/app/vocabulary_progress.tsx` that aligns apostrophe and dash variants before comparison.",
      "Extended client-side search matching to include `vocabulary.sentence` and `translation_details.source_surface_form`.",
      "Updated `vocabulary_progress_filters_search_placeholder` copy in `src/assets/translations.json` (German and English).",
    ],
  },
  {
    id: "context-save-now-creates-one-phrase-vocabulary-entry",
    timestamp: "2026-05-01T07:24:53+02:00",
    title: "Context save now creates one phrase vocabulary entry",
    summary:
      "Saving a context selection now creates one combined vocabulary entry for the full selected phrase instead of separate entries per clicked token.",
    userHighlights: [
      "A selection like `te pose-t-il tant` is now stored as one vocabulary item.",
      "Searching the phrase in Vocabulary Progress can now find that entry directly.",
      "Context translation and selection flow remains the same; only save behavior changed.",
    ],
    technicalNotes: [
      "Reworked context-save logic in `src/components/GeneratedTextDetailsTextPane.tsx` to use a single `agent_add` call with combined phrase + sentence context.",
      "Kept title-word save on the same backend path by introducing a shared phrase-save helper in the same component.",
      "Updated Playwright coverage in `e2e/highlighted-word-preview.spec.ts` to assert one `agent_add` request with the expected combined phrase payload.",
    ],
  },
  {
    id: "vocabulary-progress-list-now-has-user-friendly-sort-options",
    timestamp: "2026-05-01T07:18:50+02:00",
    title: "Vocabulary list now includes user-friendly sorting options",
    summary:
      "Vocabulary Progress now includes a dedicated sort control so you can quickly switch between learning priority, alphabetic order, added date, and practice-based ordering.",
    userHighlights: [
      "Added a new `Sort` dropdown directly in Vocabulary Progress filters.",
      "You can now sort by `Learn next`, `Word A to Z`, `Word Z to A`, `Recently added`, `Added first`, and `Most practiced`.",
      "The default still prioritizes vocabulary that is due for review first.",
    ],
    technicalNotes: [
      "Added `sortMode` UI state and a select control in `src/routes/app/vocabulary_progress.tsx`.",
      "Extracted sorting logic to `src/utils/sortVocabularyProgress.ts` for consistent behavior and easier testing.",
      "Added regression tests in `src/utils/sortVocabularyProgress.test.ts` and translation keys in `src/assets/translations.json` for both German and English labels.",
    ],
  },
  {
    id: "vocabulary-progress-statistics-now-use-language-aware-thousand-separators",
    timestamp: "2026-05-01T07:14:45+02:00",
    title: "Vocabulary Progress statistics now follow your language number format",
    summary:
      "Statistic values on Vocabulary Progress now use locale-aware thousand separators based on app language, so German and English users see expected formatting.",
    userHighlights: [
      "German app language now shows separators like `29.300`.",
      "English app language continues to show separators like `29,300`.",
      "Formatting updates automatically with your app language setting.",
    ],
    technicalNotes: [
      "Updated statistics card number formatting in `src/routes/app/vocabulary_progress.tsx` to use `useTranslation().language` and locale mapping (`GERMAN` -> `de-DE`, `ENGLISH` -> `en-US`).",
      "Replaced default `Intl.NumberFormat()` calls with a language-aware formatter helper for all statistics values rendered on Vocabulary Progress.",
      "Kept existing translation keys and stats data sources unchanged to isolate this to display formatting behavior.",
    ],
  },
  {
    id: "text-click-now-keeps-highlight-on-the-clicked-word-only",
    timestamp: "2026-05-01T07:13:00+02:00",
    title: "Text click now keeps highlight on the clicked word only",
    summary:
      "Clicking a word in generated text no longer triggers additional word highlights from broad active-word matching. Highlighting now stays on saved token positions, including the clicked word you just added.",
    userHighlights: [
      "Clicking a word to translate no longer lights up unrelated words in the same text.",
      "The clicked word still gets saved and highlighted at its exact position.",
      "Saved-word preview behavior on highlighted tokens remains unchanged.",
    ],
    technicalNotes: [
      "Removed selected-word overlay highlight matching from `src/components/GeneratedTextDetailsTextPane.tsx` so body highlights no longer come from full-text word-string matching.",
      "Kept token-position highlighting based on persisted/local vocabulary usage starts in the same component.",
      "Validated frontend utilities with `vitest` runs for `resolveGeneratedTextHighlightWords.test.ts` and `resolveActiveTextHighlightWords.test.ts`.",
    ],
  },
  {
    id: "similar-words-now-keeps-lemma-matches-across-word-forms",
    timestamp: "2026-05-01T07:12:08+02:00",
    title: "Similar words now keeps lemma matches across nearby word forms",
    summary:
      "The similar-words section now keeps close variants when they share the same lemma, even when grammar tagging differs between entries.",
    userHighlights: [
      "Word-form variants like `détendu` and `détendre` can now stay visible in `Ähnliche Wörter` when they are true lemma-related neighbors.",
      "Confusing strict filtering between adjective/verb variants is reduced for close spelling relatives.",
      "Noisy unrelated lookalikes are still filtered by the existing spelling and structure thresholds.",
    ],
    technicalNotes: [
      "Updated POS conflict gating in `src/utils/vocabularySimilarWords.ts` so near-spelling candidates are no longer rejected when either lemma or meaning signal aligns.",
      "Kept existing distance, score, and boundary-structure checks unchanged to preserve noise suppression.",
      "Added regression test in `src/utils/vocabularySimilarWords.test.ts` covering `détendu` vs `détendre` with shared lemma and differing part-of-speech metadata.",
    ],
  },
  {
    id: "feature-mockups-page-now-combines-generate-text-and-read-aloud-mockups",
    timestamp: "2026-05-01T07:09:15+02:00",
    title: "Feature Mockups now combines Generate Text and Read Aloud mockups",
    summary:
      "The two separate mockup pages are now grouped into one `Feature Mockups` page in the side navigation, so concept review happens in one place.",
    userHighlights: [
      "Side navigation now shows a single `Feature Mockups` entry instead of separate mockup links.",
      "The unified page includes both the Generate Text reading-first mockup and the Read Aloud mockup in one screen.",
      "Existing mockup interactions remain clickable and unchanged inside the consolidated page.",
    ],
    technicalNotes: [
      "Added a new `/app/feature_mockups` route in `src/routes/app/feature_mockups.tsx` that renders both existing mockup sections together.",
      "Updated side navigation wiring in `src/routes/app/route.tsx` to use a single `sidenav_feature_mockups` translation key and link target.",
      "Adjusted Playwright regression coverage in `e2e/reeprise-generation.spec.ts` to validate the new unified route and flow entry point.",
    ],
  },
  {
    id: "statistics-page-merged-into-vocabulary-progress",
    timestamp: "2026-05-01T07:07:14+02:00",
    title: "Statistics are now part of Vocabulary Progress",
    summary:
      "The separate Statistics page has been removed from navigation, and all statistics are now shown directly on the Vocabulary Progress page.",
    userHighlights: [
      "Vocabulary and text statistics now live in one place: `Vocabulary Progress`.",
      "The `Statistics` side navigation entry is removed.",
      "Existing `/app/statistics` links now forward to `Vocabulary Progress` automatically.",
    ],
    technicalNotes: [
      "Moved statistics card rendering into `src/routes/app/vocabulary_progress.tsx` using existing text and vocabulary statistics hooks.",
      "Removed the `Statistics` nav item from `src/routes/app/route.tsx` to avoid duplicate destinations.",
      "Replaced standalone statistics page content with a redirect route in `src/routes/app/statistics.tsx` for backward-compatible URL handling.",
    ],
  },
  {
    id: "context-selection-translate-now-can-save-selected-words",
    timestamp: "2026-05-01T07:01:41+02:00",
    title: "Context selection translation can now save selected words as vocabulary",
    summary:
      "The multi-word context translation rail now includes a direct action to save all selected words into vocabulary, using the same creation flow as single-word clicks in the text.",
    userHighlights: [
      "After selecting multiple words in context mode, you can now save the whole selection as vocabulary entries in one step.",
      "Saved entries keep the same downstream enrichment behavior as normal word clicks, including full translation metadata generation.",
      "The context rail keeps translation and save actions separate, so you can translate only, save only, or do both.",
    ],
    technicalNotes: [
      "Added a new context-rail save action in `src/components/GeneratedTextDetailsTextPane.tsx` that iterates selected tokens through existing `onCreateVocabulary` handling instead of introducing a new API path.",
      "Reused the existing `add_vocabulary` backend pipeline for each selected token, preserving sentence-scoped extraction and full translation-details generation behavior.",
      "Extended Playwright regression coverage in `e2e/highlighted-word-preview.spec.ts` to verify context-mode selection creates vocabulary entries through the real user flow.",
    ],
  },
  {
    id: "highlighted-word-preview-now-closes-when-clicking-the-same-word-again",
    timestamp: "2026-05-01T07:01:00+02:00",
    title: "Highlighted-word preview now closes when clicking the same word again",
    summary:
      "The translation preview flyout for highlighted words now toggles closed when you click the same highlighted word again, so closing no longer depends on using the close icon.",
    userHighlights: [
      "Clicking a highlighted word still opens its translation preview immediately.",
      "Clicking that same highlighted word again now closes the preview in place.",
      "Outside-click and explicit close-button behavior remain available as before.",
    ],
    technicalNotes: [
      "Adjusted preview-open interaction handling in `src/components/GeneratedTextDetailsTextPane.tsx` to support same-word toggle-close while preserving immediate pointerdown-open behavior.",
      "Added press/click interaction guard state so first click after pointerdown does not immediately close the newly opened flyout.",
      "Extended Playwright regression in `e2e/highlighted-word-preview.spec.ts` to assert same-word click closes the preview.",
    ],
  },
  {
    id: "daily-weather-text-no-longer-hides-struggle-words-card",
    timestamp: "2026-05-01T06:59:51+02:00",
    title: "Daily weather text creation no longer hides the struggle-words card",
    summary:
      "Creating the morning daily weather text now keeps the struggle-words card visible instead of incorrectly treating the daily text as a struggle-practice text.",
    userHighlights: [
      "After generating the daily weather text, the struggle-words card remains available on the same morning session.",
      "Daily reinforcement words no longer trigger the hide condition for the struggle-words card.",
      "The daily card flow and open-to-details behavior stay unchanged.",
    ],
    technicalNotes: [
      "Adjusted `isStrugglePracticeText()` in `src/utils/hasTodayStrugglePracticeText.ts` to ignore `struggle_words` payload when `origin === DAILY_NUDGE`.",
      "Kept existing fallback detection for genuine struggle-practice texts (origin/topic and non-daily payload-based detection).",
      "Added regression test `does not count a DAILY_NUDGE text with reinforcement words as struggle-practice text` in `src/utils/hasTodayStrugglePracticeText.test.ts`.",
    ],
  },
  {
    id: "title-highlighted-words-now-also-highlight-matching-body-words",
    timestamp: "2026-05-01T06:58:34+02:00",
    title: "Title-highlighted words now also highlight matching words in the text body",
    summary:
      "Words saved from a generated text title now also highlight matching occurrences inside the story body, so title and body highlighting behave consistently.",
    userHighlights: [
      "Clicking a title word to save it now highlights that same word in the story text as well.",
      "Body highlights stay consistent even when a word was originally added from the title.",
      "Existing chapter-generated highlights continue to work while title-word highlighting is included.",
    ],
    technicalNotes: [
      "Adjusted text-details highlight word resolution in `src/routes/app/reeprise/$generatedTextId.tsx` to merge title-origin vocabulary words into active text highlight inputs.",
      "Kept previous positional-protection behavior intact by extending only word-based highlight inputs, not usage-index mapping.",
      "Added regression unit coverage in `src/utils/resolveActiveTextHighlightWords.test.ts` and introduced shared merge utility `src/utils/resolveActiveTextHighlightWords.ts`.",
    ],
  },
  {
    id: "trainer-refresh-no-longer-repeats-just-answered-word",
    timestamp: "2026-04-27T21:55:44+02:00",
    title: "Trainer reload no longer repeats a just-answered word",
    summary:
      "After marking a trainer word as known, refreshing the page now keeps the queue aligned with server SRS state instead of resurfacing that same word.",
    userHighlights: [
      "A word marked with `Knew it` no longer reappears immediately after refresh.",
      "Trainer queue now reflects current due words from server state after reload.",
      "Review actions are now more resilient during tab reload/unload timing.",
    ],
    technicalNotes: [
      "Removed early snapshot-only session restore in `src/components/VocabularyPreviewCard.tsx` so restored batches are reconciled against fresh server vocabulary IDs.",
      "Hardened review mutations in `src/hooks/useMarkVocabularyAsLearned.ts` with `keepalive: true` and trainer-query invalidation on settle.",
      "Updated trainer query freshness in `src/hooks/useVocabularyTrainerVocabularies.ts` (`staleTime: 0`, mount/focus refetch enabled) and added regressions in `barkguide/reeprise/tests.py` plus `e2e/reeprise-generation.spec.ts`.",
    ],
  },
  {
    id: "trainer-answer-actions-on-mobile-no-longer-force-scroll-to-top",
    timestamp: "2026-04-27T21:46:23+02:00",
    title: "Trainer answer actions no longer force-scroll to top on mobile",
    summary:
      "When answering a trainer card with `Knew it`, `Almost`, or `Didn't know`, the page now keeps your current scroll position instead of jumping back to the trainer top.",
    userHighlights: [
      "Answering from lower down inside an expanded trainer card now feels stable and does not jump upward.",
      "Manual expand/collapse and answer actions now both keep viewport position calm on mobile.",
      "The next card still loads normally after each answer.",
    ],
    technicalNotes: [
      "Removed the explicit `scrollToTop()` call from trainer answer handling in `src/components/VocabularyPreviewCard.tsx`.",
      "Removed the same forced scroll call from trainer delete success flow to keep behavior consistent.",
      "Updated Playwright route-transition coverage in `e2e/reeprise-generation.spec.ts` to assert answer actions preserve scroll position from lower content.",
    ],
  },
  {
    id: "similar-words-now-filters-weak-lookalikes-more-strictly",
    timestamp: "2026-04-27T21:42:05+02:00",
    title: "Similar words now suppress weak letter-only lookalikes",
    summary:
      "The similar-words section now applies stricter matching so words with superficial letter overlap are less likely to appear as confusing suggestions.",
    userHighlights: [
      "The `Ähnliche Wörter` list now focuses more on genuinely useful spelling neighbors instead of loose letter overlap.",
      "Lookalikes with conflicting grammar metadata are now filtered out.",
      "Very short or weakly structured matches are now rejected more aggressively to reduce noise.",
    ],
    technicalNotes: [
      "Strengthened `computeSimilarVocabularySuggestions` in `src/utils/vocabularySimilarWords.ts` with a length-aware near-spelling gate and additional structural similarity checks (prefix/suffix and bigram dice overlap).",
      "Added semantic gating for near-spelling candidates using normalized part-of-speech, lemma, and primary-meaning signals.",
      "Added regression unit tests in `src/utils/vocabularySimilarWords.test.ts` for accepted close variants and rejected noisy lookalikes (different starts without semantic signal, POS conflicts).",
    ],
  },
  {
    id: "trainer-same-translation-debug-trace-for-candidate-filtering",
    timestamp: "2026-04-27T21:39:22+02:00",
    title: "Trainer same-translation filtering now has optional debug tracing",
    summary:
      "The trainer can now emit structured debug output showing exactly why same-translation candidates are accepted or rejected, making it easier to investigate missing `Ähnliches Wort` notes.",
    userHighlights: [
      "When debug mode is enabled locally, the trainer logs the full candidate decision trace for the same-translation section.",
      "The trace includes the current word, expected normalized translation, and per-candidate rejection reasons.",
      "Debug logging stays off by default, so normal trainer behavior and UI remain unchanged.",
    ],
    technicalNotes: [
      "Extended `computeSameTranslationVocabularySuggestions` in `src/utils/vocabularySimilarWords.ts` with structured debug events for exclusion, same-word filtering, pending/missing translations, translation mismatch, and duplicate-word dedupe.",
      "Added local debug gating through `window.localStorage` key `trainer_same_translation_debug` (value `1` enables logs).",
      "Logs are emitted as `[trainer_same_translation_debug]` payloads so local diagnosis can map missing same-translation notes back to exact filter conditions.",
    ],
  },
  {
    id: "manual-trainer-collapse-on-mobile-no-longer-jumps-to-card-top",
    timestamp: "2026-04-27T21:36:52+02:00",
    title: "Manual trainer collapse on mobile no longer jumps back to card top",
    summary:
      "When you manually close an opened trainer card on mobile, the page now keeps your current reading position instead of force-scrolling to the top of the trainer.",
    userHighlights: [
      "Tapping an expanded trainer card to close it no longer triggers a sudden jump to the trainer top.",
      "The trainer stays calmer during manual open/close checks on mobile.",
      "Review actions such as `Knew it` still advance the flow as before.",
    ],
    technicalNotes: [
      "Removed the `tapped` transition side effect in `src/components/VocabularyPreviewCard.tsx` that previously called `scrollToTop()` on manual collapse.",
      "Kept explicit scroll-to-top behavior for progress actions (`known`/`almost`/`forgotten`) and delete flow, so next-card navigation behavior stays intact.",
      "Added Playwright regression coverage in `e2e/reeprise-generation.spec.ts` to assert manual collapse does not force-scroll back to the trainer root on mobile.",
    ],
  },
  {
    id: "similar-words-v2-structured-distinction-explainer",
    timestamp: "2026-04-27T21:33:01+02:00",
    title: "Similar words now include a structured distinction explainer",
    summary:
      "The similar-words card now offers an on-demand learning panel that explains usage differences between related words in a structured format.",
    userHighlights: [
      "Inside `Ähnliche Wörter`, a new `Unterschiede lernen` action opens a structured explanation for the current word and related alternatives.",
      "The panel now shows main-word meaning, practical usage, per-word differences, and a short key-distinction summary to reduce confusion.",
      "The explainer loads only on demand and then reuses cached results for the same word combination.",
    ],
    technicalNotes: [
      "Added a new backend endpoint `POST /api/reeprise/vocabulary/:id/similar_words_explainer/` in `barkguide/reeprise/views.py` with strict candidate-id validation and structured JSON normalization.",
      "Introduced persistent per-vocabulary explainer caching via `similar_words_explainer_cache` (new migration `0084_vocabulary_similar_words_explainer_cache.py`).",
      "Added request serializer `VocabularySimilarWordsExplainerRequestSerializer` in `barkguide/reeprise/serializer.py`.",
      "Implemented frontend mutation hook `src/hooks/useVocabularySimilarWordsExplainer.ts` and integrated a new expandable explainer panel in `src/components/VocabularyPreviewCard.tsx`.",
      "Added translation keys for explainer controls and section labels in `src/assets/translations.json`.",
      "Added backend regression tests in `barkguide/reeprise/tests.py` for explainer generation, cache reuse, and invalid-candidate handling, plus Playwright coverage in `e2e/similar-words-explainer.spec.ts`.",
    ],
  },
  {
    id: "source-text-focus-highlight-now-has-strong-distinct-visual-treatment",
    timestamp: "2026-04-27T21:31:26+02:00",
    title: "Source-text jump focus is now visually distinct from saved word highlights",
    summary:
      "When opening source context from trainer cards, the temporary focus highlight now uses a stronger dedicated visual style so it is clearly distinguishable from regular saved-vocabulary highlights.",
    userHighlights: [
      "Jumped source context now appears with a clearly different focus style from normal blue saved-word highlights.",
      "The focused sentence remains visible longer after navigation, making it easier to spot on arrival.",
      "Persistent saved-vocabulary highlight styling remains unchanged.",
    ],
    technicalNotes: [
      "Updated sentence-focus highlight classes in `src/components/utils/generatedTextDetailsTextPaneUtils.tsx` to use a dedicated amber gradient + underline + pulse ring treatment.",
      "Extended sentence-focus flash lifetime in `src/components/GeneratedTextDetailsTextPane.tsx` so the navigation focus remains visible longer.",
      "Adjusted route-transition selector coverage in `e2e/reeprise-generation.spec.ts` and updated unit expectations in `src/components/utils/generatedTextDetailsTextPaneUtils.test.tsx`.",
    ],
  },
  {
    id: "generated-text-now-supports-contextual-multi-word-translation-selection",
    timestamp: "2026-04-26T19:36:37+02:00",
    title: "Generated text now supports contextual multi-word translation selection",
    summary:
      "Text details now include a dedicated context-selection mode where multiple words can be translated together, including non-adjacent selections in the same sentence.",
    userHighlights: [
      "A new `Select context words` mode lets you click multiple words and request one combined translation.",
      "You can combine adjacent or non-adjacent words, as long as they belong to the same sentence.",
      "If you click into another sentence by mistake, the UI now shows a clear warning and a one-tap `Start new selection here` action.",
    ],
    technicalNotes: [
      "Extended `src/components/GeneratedTextDetailsTextPane.tsx` with context-selection mode state, multi-token selection rail, sentence-bound guard handling, and phrase translation requests via `/api/reeprise/translate/`.",
      "Extended shared span rendering in `src/components/utils/generatedTextDetailsTextPaneUtils.tsx` to support context-selection click routing and selected-token styling without breaking existing add/preview behavior.",
      "Added translation keys for context-selection copy in `src/assets/translations.json`.",
      "Added Vitest coverage in `src/components/utils/generatedTextDetailsTextPaneUtils.test.tsx` and Playwright regression coverage in `e2e/reeprise-generation.spec.ts` for adjacent/non-adjacent selection flow and cross-sentence guard behavior.",
    ],
  },
  {
    id: "vocabulary-training-mode-now-includes-the-clarify-helper-panel",
    timestamp: "2026-04-26T19:20:40+02:00",
    title: "Vocabulary training mode now includes the Clarify helper",
    summary:
      "The same Clarify panel from text details is now available in vocabulary training mode, so questions can be asked without leaving trainer flow.",
    userHighlights: [
      "You can now ask clarification questions directly on the vocabulary training mode page.",
      "The helper keeps the same chat-style interaction used on text details.",
      "The helper is now bound to the active trainer card, so even generic questions stay anchored to that card’s word and sentence.",
    ],
    technicalNotes: [
      "Mounted shared `LanguageHelperPanel` in `src/routes/app/vocabulary_training_mode.tsx`.",
      "Wired helper inputs with training context (`generatedTextId`, active `word_language`, recent trainer vocabulary, sentence-based `contextText`) and bound the helper payload to the currently active trainer card (`word` + `sentence`).",
      "Added Playwright regression coverage in `e2e/reeprise-generation.spec.ts` to verify helper rendering, send flow, and active-card-bound request payload.",
    ],
  },
  {
    id: "daily-callout-copy-is-now-fully-neutral-without-type-labeling",
    timestamp: "2026-04-26T19:10:25+02:00",
    title: "Daily callout copy is now fully neutral again",
    summary:
      "The daily card no longer shows text-type labeling and now keeps neutral wording in both pre-create and done states.",
    userHighlights: [
      "Daily card wording stays generic even when the generated text is a podcast or another specific type.",
      "No `Type:` meta line is shown on the done card anymore.",
      "The card now avoids extra inference logic and remains simpler to understand.",
    ],
    technicalNotes: [
      "Removed daily text-type label rendering from `src/routes/app/reeprise/index.tsx`.",
      "Removed now-unneeded type helper and tests (`src/utils/dailyTextType.ts`, `src/utils/dailyTextType.test.ts`).",
      "Removed unused translation key `dailyTextTypeMeta` from `src/assets/translations.json`.",
      "Updated Playwright regression in `e2e/reeprise-generation.spec.ts` to assert the daily done card remains neutral with no type label.",
    ],
  },
  {
    id: "vocabulary-training-mode-now-includes-shared-pronunciation-control",
    timestamp: "2026-04-26T19:04:34+02:00",
    title: "Vocabulary training mode now includes direct word pronunciation",
    summary:
      "Trainer cards in vocabulary training mode now include the same compact pronunciation button used across other vocabulary surfaces.",
    userHighlights: [
      "You can now trigger word audio directly inside each vocabulary training-mode card.",
      "The pronunciation action uses a compact placement in the card header to avoid adding visual noise.",
      "Audio behavior stays consistent with other vocabulary views because the same shared pronunciation control is reused.",
    ],
    technicalNotes: [
      "Reused `VocabularyPronunciationButton` in `src/routes/app/vocabulary_training_mode.tsx` instead of adding a second trainer-specific audio implementation.",
      "Kept layout compact by placing the shared control inline with the card question row and using smaller button sizing.",
      "Added Playwright regression coverage in `e2e/reeprise-generation.spec.ts` to verify the training-mode card renders the shared pronunciation control and sends pronunciation generation requests.",
    ],
  },
  {
    id: "daily-text-callout-now-shows-text-type-for-story-dialogue-podcast-and-more",
    timestamp: "2026-04-26T19:02:45+02:00",
    title: "Daily text callout now reflects the actual text type",
    summary:
      "The daily done-callout now surfaces the real generated text type (for example podcast) and no longer uses story-specific fallback wording in English.",
    userHighlights: [
      "When today’s daily text already exists, the done card now shows a type meta line such as `Type: Podcast`.",
      "Pre-creation daily card copy stays neutral and does not claim a type before the text is actually generated.",
      "English daily fallback text is now generic (`text`) instead of story-only wording.",
    ],
    technicalNotes: [
      "Added `src/utils/dailyTextType.ts` with text-type normalization for safe daily-card type labeling.",
      "Integrated type-aware callout metadata in `src/routes/app/reeprise/index.tsx` for the done daily-card state only (where a real generated text exists).",
      "Updated daily translation copy in `src/assets/translations.json` (`dailyTextHeading`, `dailyTextDoneDescription`) and added `dailyTextTypeMeta`.",
      "Added regression unit tests in `src/utils/dailyTextType.test.ts` for known-type normalization and unknown-type fallback behavior.",
    ],
  },
  {
    id: "similar-words-now-separates-spelling-neighbors-from-same-translation-words",
    timestamp: "2026-04-26T18:58:41+02:00",
    title:
      "Similar words now separate spelling neighbors from same-translation words",
    summary:
      "The similar-words card now keeps same-translation matches in a separate subsection so users can distinguish spelling similarity from translation overlap.",
    userHighlights: [
      "The `Ähnliche Wörter` card now has a dedicated `Gleiche Übersetzung` subsection under the existing spelling-based suggestions.",
      "Words with the same translation (for example `mieux` and `meilleur` -> `besser`) are shown separately instead of being mixed into one list.",
      "The new subsection includes a short note clarifying that same translation does not always mean same meaning.",
    ],
    technicalNotes: [
      "Added `computeSameTranslationVocabularySuggestions` in `src/utils/vocabularySimilarWords.ts` to resolve same-translation candidates in the same source language while excluding same-spelling duplicates and already-listed similar-word ids.",
      "Updated `src/components/VocabularyPreviewCard.tsx` to render two distinct blocks inside `Ähnliche Wörter`: existing spelling-based suggestions and a new same-translation block with explanatory copy.",
      "Added translation keys for the new subsection and note in `src/assets/translations.json`.",
      "Extended unit coverage in `src/utils/vocabularySimilarWords.test.ts` for same-translation matching, exclusion of same-spelling duplicates, and cross-section de-duplication.",
    ],
  },
  {
    id: "mobile-trainer-now-scrolls-back-to-next-word-after-close",
    timestamp: "2026-04-26T18:49:38+02:00",
    title: "Mobile trainer now returns to the next word after closing an open card",
    summary:
      "On mobile, closing an expanded trainer card from lower down the page now scrolls back to the trainer top so the next word is immediately visible.",
    userHighlights: [
      "After closing or continuing from an expanded trainer card on mobile, the view returns to the trainer top.",
      "The next trainer word stays in view instead of leaving users at lower mnemonic/details sections.",
      "Desktop trainer behavior remains unchanged.",
    ],
    technicalNotes: [
      "Implemented `scrollPreviewToTop` in `src/components/VocabularyPreviewCard.tsx` to perform a mobile-only window scroll to the trainer card root.",
      "Kept desktop behavior unchanged by guarding the scroll logic with a mobile-width check.",
      "Added Playwright regression coverage in `e2e/reeprise-generation.spec.ts` for the flow: expand trainer card on mobile, scroll down, continue, and verify the viewport returns to trainer top.",
    ],
  },
  {
    id: "text-details-links-now-use-complete-route-search-shape",
    timestamp: "2026-04-26T10:13:30+02:00",
    title: "Text-details links now consistently open with complete route state",
    summary:
      "Navigation to text details now uses a consistent search payload everywhere, preventing broken builds and keeping transitions stable when opening texts.",
    userHighlights: [
      "Opening generated texts from feed cards, notifications, and quick actions behaves the same as before.",
      "Daily-text and generation flows still navigate directly to the created text details page.",
      "Chapter navigation in text details continues to work without carrying stale word-focus state.",
    ],
    technicalNotes: [
      "Updated all `/app/reeprise/$generatedTextId` navigations that previously passed only `chapter` to now provide the full search object (`chapter`, `focusWord`, `focusSentence`, `focusToken`).",
      "Patched route callers in `src/routes/app/route.tsx`, `src/routes/app/reeprise/index.tsx`, `src/components/ReepriseTextCard.tsx`, `src/components/GenerateTextCard.tsx`, `src/routes/app/vocabulary_training_mode.tsx`, and `src/routes/app/reeprise/$generatedTextId.tsx`.",
      "Verified with `npm run build` (includes `tsc -b`) to confirm TypeScript route-search type safety passes end to end.",
    ],
  },
  {
    id: "trainer-source-link-skips-word-focus-when-source-sentence-missing",
    timestamp: "2026-04-26T10:06:24+02:00",
    title: "Trainer source-text jump no longer fakes focus when source sentence is missing",
    summary:
      "When a trainer card has no saved source sentence, opening the source text now avoids fallback word-only focus highlights.",
    userHighlights: [
      "Cards without `Ausgangssatz` still open `Zum Ursprungstext` normally.",
      "No misleading word-only flash highlight appears when sentence context is unavailable.",
      "Sentence-level focus remains unchanged for cards that do have a saved source sentence.",
    ],
    technicalNotes: [
      "Updated source-text links in `src/components/VocabularyPreviewCard.tsx`, `src/routes/app/vocabulary_trainer.tsx`, and `src/routes/app/vocabulary_training_mode.tsx` to only pass focus params when a source sentence exists.",
      "Extended Playwright route-transition coverage in `e2e/reeprise-generation.spec.ts` to assert no focus highlight is rendered for missing-source-sentence navigation.",
      "No backend changes were required.",
    ],
  },
  {
    id: "trainer-source-link-now-uses-clear-copy-when-source-sentence-missing",
    timestamp: "2026-04-26T10:01:01+02:00",
    title: "Trainer now uses clearer copy when source sentence is missing",
    summary:
      "When a card can still open the source text but the sentence field itself is missing, trainer cards now show explicit copy that the sentence was not saved on this card.",
    userHighlights: [
      "You no longer see the contradictory `Kein Ausgangssatz verfügbar.` state next to an available `Zum Ausgangstext` button.",
      "Cards with source-text navigation but no saved sentence now explain that the sentence is not stored on the card.",
      "Source-text navigation remains available and unchanged.",
    ],
    technicalNotes: [
      "Added `resolveSourceSentenceFallbackKey` in `src/utils/vocabularySourceSentenceCopy.ts` and wired it into trainer card sentence rendering.",
      "Applied the same fallback-key behavior in both `src/components/VocabularyPreviewCard.tsx` and `src/routes/app/vocabulary_trainer.tsx`.",
      "Added translation key `vocabulary_source_sentence_not_saved_on_card` in `src/assets/translations.json`.",
      "Added unit coverage in `src/utils/vocabularySourceSentenceCopy.test.ts` and Playwright regression coverage in `e2e/reeprise-generation.spec.ts` for source-link cards without saved source sentence.",
    ],
  },
  {
    id: "source-sentence-focus-now-has-distinct-visual-highlight",
    timestamp: "2026-04-26T09:56:16+02:00",
    title: "Source sentence focus now stands out from normal word highlights",
    summary:
      "When jumping from trainer to text details, focused source sentences now use a distinct visual highlight style so the sentence is easy to spot even among many highlighted words.",
    userHighlights: [
      "Focused sentence words now use a clearly different color treatment from regular vocabulary highlights.",
      "Sentence focus is easier to scan in dense texts with multiple highlighted words.",
      "Regular word highlights and preview focus behavior remain intact.",
    ],
    technicalNotes: [
      "Extended `createTextSpans` in `src/components/utils/generatedTextDetailsTextPaneUtils.tsx` with dedicated `sentenceFlashStarts` handling and sentence-focus highlight/ring classes.",
      "Updated `src/components/GeneratedTextDetailsTextPane.tsx` to track sentence-focus flash starts separately from regular word flash starts.",
      "Added regression tests in `src/components/utils/generatedTextDetailsTextPaneUtils.test.tsx` to assert sentence-focus styling differs from standard word highlight styling.",
    ],
  },
  {
    id: "vocabulary-canonical-noun-form-guardrails-for-bad-article-shapes",
    timestamp: "2026-04-26T09:48:33+02:00",
    title: "Vocabulary cards now guard against broken noun article forms",
    summary:
      "When noun metadata looks inconsistent, cards now avoid showing a likely broken canonical article form and fall back to the seen word form.",
    userHighlights: [
      "Vocabulary and trainer cards now avoid displaying malformed noun dictionary labels when article metadata is inconsistent.",
      "Valid canonical nouns with apostrophe articles (for example `l'ami`) still render correctly.",
      "This only affects suspicious noun-canonical labels and keeps normal noun article behavior unchanged.",
    ],
    technicalNotes: [
      "Added canonical noun-form guard helpers in `src/utils/vocabularyTranslationDetails.ts` to validate dictionary-form/article alignment before using canonical noun labels.",
      "When the noun dictionary form fails the guard, `getVocabularyPrimaryWord` now falls back to source surface form instead of rendering a potentially bad canonical value.",
      "Added regression tests in `src/utils/vocabularyTranslationDetails.test.ts` for article mismatch fallback and apostrophe-article acceptance.",
    ],
  },
  {
    id: "trainer-source-text-jump-now-opens-context-and-highlights-source-sentence",
    timestamp: "2026-04-26T09:40:00+02:00",
    title: "Trainer words can now jump back to source text with sentence focus",
    summary:
      "From trainer cards, you can now open the original text directly and the matching source sentence is highlighted so context is immediately visible.",
    userHighlights: [
      "Expanded trainer cards now include an `Open source text` action when the word has a saved source usage.",
      "The jump opens the correct generated text (including chapter context when available).",
      "On arrival, the matching source sentence is highlighted so you can quickly re-read the exact context.",
    ],
    technicalNotes: [
      "Extended vocabulary usage payloads with `chapter_counter` in `barkguide/reeprise/serializer.py` and ensured chapter-aware prefetching in `barkguide/reeprise/views.py`.",
      "Added source-text navigation links in trainer surfaces (`src/components/VocabularyPreviewCard.tsx`, `src/routes/app/vocabulary_training_mode.tsx`, `src/routes/app/vocabulary_trainer.tsx`).",
      "Extended text-details route search handling (`focusWord`, `focusSentence`, `focusToken`) in `src/routes/app/reeprise/$generatedTextId.tsx`.",
      "Updated sentence focus behavior in `src/components/GeneratedTextDetailsTextPane.tsx` to flash-highlight all words of the matched source sentence and scroll it into view.",
      "Added backend regression coverage in `barkguide/reeprise/tests.py` and Playwright route-transition coverage in `e2e/reeprise-generation.spec.ts`.",
    ],
  },
  {
    id: "trainer-cards-now-reuse-canonical-vocabulary-word-pairs",
    timestamp: "2026-04-26T09:37:13+02:00",
    title: "Trainer cards now reuse canonical vocabulary word pairs",
    summary:
      "Trainer cards now display the same canonical source/translation pair as vocabulary cards, including noun article forms from structured translation details.",
    userHighlights: [
      "Trainer prompts and answers now match the exact word pair shown on the vocabulary card for the same entry.",
      "Noun cards with canonical article forms (for example `le ...`) now keep those forms in trainer mode too.",
      "Direction toggles still work, but they now flip the same normalized pair instead of raw stored strings.",
    ],
    technicalNotes: [
      "Updated `src/routes/app/vocabulary_training_mode.tsx` to resolve trainer labels from shared translation-details helpers instead of raw `word`/`translation` fields.",
      "Added `src/utils/vocabularyTrainerCardLabels.ts` to centralize trainer-card word-pair resolution, including pending-translation placeholder normalization.",
      "Added regression coverage in `src/utils/vocabularyTrainerCardLabels.test.ts` for canonical article forms, reversed direction mapping, and pending translation fallback.",
    ],
  },
  {
    id: "trainer-delete-now-handles-already-removed-words-without-false-error",
    timestamp: "2026-04-26T09:35:52+02:00",
    title: "Trainer delete now handles already-removed words without a false error",
    summary:
      "Deleting a word from the trainer no longer shows a failure message when that word was already removed in the meantime.",
    userHighlights: [
      "Delete in trainer now stays smooth even if the word was already removed elsewhere.",
      "You no longer get a misleading delete-failed snackbar for that stale-card case.",
      "Trainer continues forward instead of appearing blocked by a stale delete state.",
    ],
    technicalNotes: [
      "Updated `src/hooks/useVocabularyDelete.ts` to treat `404` delete responses as an idempotent success for stale UI state.",
      "Kept existing query invalidation and `vocabulary-deleted` event dispatch so trainer/list state stays in sync.",
      "Added Playwright regression coverage in `e2e/reeprise-generation.spec.ts` for deleting a trainer card after the same vocabulary was already deleted via API.",
    ],
  },
  {
    id: "podcast-continue-flow-and-details-player-alignment",
    timestamp: "2026-04-26T09:29:56+02:00",
    title: "Podcast continuation now keeps episode behavior on text details",
    summary:
      "Continuing a podcast text with selected words now keeps podcast episode behavior, and the text-details page now includes podcast-specific controls that match the podcast card experience.",
    userHighlights: [
      "Using `Mit Wörtern fortsetzen` on a podcast now creates a real podcast continuation chapter flow instead of dropping podcast behavior.",
      "Podcast text details now show podcast meta info and a transcript toggle directly in the audio section.",
      "Transcript view in details works for both the base podcast text and generated continuation chapters.",
    ],
    technicalNotes: [
      "Backend: `generate_chapter` now schedules `generate_speech` after commit for podcast chapters in `barkguide/reeprise/tasks.py`, so new podcast chapters enqueue chapter-level audio generation automatically.",
      "Frontend: added podcast-specific details UI in `src/routes/app/reeprise/$generatedTextId.tsx` (meta line, podcast idle copy, transcript toggle/panel) and new translation keys in `src/assets/translations.json`.",
      "Regression coverage: added Django test for podcast chapter speech enqueueing in `barkguide/reeprise/tests.py` and Playwright flow coverage in `e2e/reeprise-generation.spec.ts` for podcast continue-with-words navigation and transcript behavior.",
    ],
  },
  {
    id: "quick-translate-now-shows-example-sentence-in-both-target-and-source-languages",
    timestamp: "2026-04-26T09:28:30+02:00",
    title: "Quick Translate now shows the same example sentence in both languages",
    summary:
      "Übersetzung unterwegs now returns and displays a paired example: one sentence in the selected target language and the same sentence in the detected source language.",
    userHighlights: [
      "After translating a short word or phrase, you now see the target-language example sentence plus the same sentence in the detected source language.",
      "The source-language example is shown directly below the target example for immediate side-by-side comparison.",
      "Saving to vocabulary keeps the existing target-language sentence behavior unchanged.",
    ],
    technicalNotes: [
      "Extended `/api/reeprise/translate/` request/response handling to support `source_example_sentence` and to generate it from the target example when source language is known.",
      "Updated `src/components/QuickTranslateCard.tsx` state and payload wiring to render and persist both example sentences in the quick-translate save flow.",
      "Added translation keys for the new target/source example labels in `src/assets/translations.json`.",
      "Added backend regression tests in `reeprise/tests.py` covering dual-sentence response generation and save-path reuse of provided source examples.",
    ],
  },
  {
    id: "mnemonic-anchor-input-now-uses-dynamic-word-specific-guidance",
    timestamp: "2026-04-26T09:15:51+02:00",
    title: "Anchor-word input now uses dynamic per-word guidance",
    summary:
      "The mnemonic anchor input no longer shows a fixed example and now guides users with dynamic wording based on the currently selected vocabulary word.",
    userHighlights: [
      "Static example placeholder was replaced with neutral anchor-word guidance.",
      "A new helper line now references the current word directly (sound-similar hint).",
      "Anchor input guidance now remains relevant across different vocabulary items.",
    ],
    technicalNotes: [
      "Updated anchor placeholder copy and added a dynamic hint key with `{{word}}` interpolation in `src/assets/translations.json`.",
      "Rendered the new dynamic anchor hint in `src/components/VocabularyPreviewCard.tsx` using the current target word.",
      "No API, model, or generation pipeline changes.",
    ],
  },
  {
    id: "reeprise-top-area-spacing-rhythm-polish-for-desktop",
    timestamp: "2026-04-26T09:14:27+02:00",
    title: "Top-area spacing on Reeprise desktop was polished",
    summary:
      "The top layout rhythm was tuned so daily cards, section spacing, and utility-column stack spacing feel more intentional and balanced across desktop widths.",
    userHighlights: [
      "Daily card row spacing is slightly more open and readable.",
      "Main column section rhythm is tighter and more consistent.",
      "Utility column stack now uses matching vertical cadence.",
    ],
    technicalNotes: [
      "Adjusted top layout spacing classes in `src/routes/app/reeprise/index.tsx` (`space-y`, `gap`) for the main column, daily row, and desktop utility column stack.",
      "No component behavior, API, or data-model changes.",
      "Re-ran desktop utility-column Playwright regression to confirm layout behavior remained intact.",
    ],
  },
  {
    id: "guided-mnemonic-anchor-suggestions-now-explicitly-manual",
    timestamp: "2026-04-26T06:48:32+02:00",
    title: "Guided mnemonic anchor suggestions now run only on explicit request",
    summary:
      "Anchor suggestions in guided mnemonic mode are no longer auto-fetched in the background and now run only when you click `Suggest anchors`.",
    userHighlights: [
      "No background anchor-suggestion generation is triggered when opening mnemonic editing.",
      "Suggestions load only after an explicit click on `Suggest anchors`.",
      "Guided mnemonic generation with selected anchor and direction remains unchanged.",
    ],
    technicalNotes: [
      "Removed the auto-suggestion `useEffect` fallback in `src/components/VocabularyPreviewCard.tsx`.",
      "Kept the existing manual suggestion button flow and mutation wiring unchanged.",
      "Extended `e2e/reeprise-generation.spec.ts` to assert zero anchor-suggestion calls before click and one call after click.",
    ],
  },
  {
    id: "reeprise-desktop-compose-card-centered-outside-split-grid",
    timestamp: "2026-04-25T18:45:47+02:00",
    title: "Desktop Generate Text card now centers independently of split columns",
    summary:
      "The fixed desktop Generate Text card is now centered in the usable content area while the existing 2:1 split structure remains in place.",
    userHighlights: [
      "The Generate Text card now appears visually centered on desktop.",
      "Side navigation width is still respected for positioning.",
      "Main-content split behavior for the page remains unchanged.",
    ],
    technicalNotes: [
      "Kept the desktop `2fr / 1fr` split container in `src/routes/app/reeprise/index.tsx`.",
      "Moved the `GenerateTextCard` render to a centered wrapper outside the split grid within `reeprise-desktop-compose`.",
      "Updated the Playwright desktop layout regression in `e2e/reeprise-generation.spec.ts` to measure the compose form bounds/center directly instead of the outer compose shell.",
    ],
  },
  {
    id: "reeprise-desktop-compose-now-uses-sidenav-aware-width",
    timestamp: "2026-04-25T18:40:48+02:00",
    title: "Desktop Generate Text composer now centers in usable content width",
    summary:
      "The fixed desktop Generate Text composer now uses the available content width after side navigation is subtracted, so it stays centered correctly in both compact and expanded sidenav states.",
    userHighlights: [
      "Desktop composer alignment now tracks the real reading area, not full viewport width.",
      "Composer no longer drifts toward the side navigation when the nav is expanded.",
      "Collapsed and expanded side navigation both keep stable composer centering.",
    ],
    technicalNotes: [
      "Updated `src/routes/app/reeprise/index.tsx` so desktop composer width is `calc(100vw - var(--app-desktop-nav-width))` while keeping center alignment via `left` + `-translate-x-1/2`.",
      "Reused dynamic `--app-desktop-nav-width` from the app shell (`72px` compact, `440px` expanded).",
      "Extended `e2e/reeprise-generation.spec.ts` desktop layout regression to assert composer bounds and center alignment for both collapsed and expanded side navigation states.",
    ],
  },
  {
    id: "reeprise-utility-column-top-offset-removed-for-top-line-alignment",
    timestamp: "2026-04-25T18:36:53+02:00",
    title: "Trainer column now starts on the same top line as daily cards",
    summary:
      "The desktop utility column sticky offset was reduced so the trainer card aligns with the daily card row instead of starting visibly lower.",
    userHighlights: [
      "Trainer column now starts at the same top line as daily weather cards.",
      "Utility column remains independently scrollable.",
      "No card-height forcing was reintroduced.",
    ],
    technicalNotes: [
      "Changed Reeprise desktop utility sticky offset from `xl:top-20` to `xl:top-0` in `src/routes/app/reeprise/index.tsx`.",
      "Preserved existing utility overflow behavior (`overflow-y-auto`) and hidden-scrollbar styling.",
      "Re-ran Playwright desktop utility scroll regression.",
    ],
  },
  {
    id: "reverted-forced-trainer-daily-height-parity-on-desktop",
    timestamp: "2026-04-25T18:35:05+02:00",
    title: "Reverted forced equal height between trainer and daily cards",
    summary:
      "The temporary desktop rule forcing trainer and daily weather cards to identical fixed height was reverted.",
    userHighlights: [
      "Trainer card no longer uses forced fixed-height parity with daily weather cards.",
      "Desktop utility column keeps independent scrolling behavior.",
      "Top alignment between columns remains unchanged.",
    ],
    technicalNotes: [
      "Removed shared fixed-height token and trainer-slot wrapper from `src/routes/app/reeprise/index.tsx`.",
      "Dropped temporary `className` extension on `VocabularyPreviewCard` used only for forced fixed-height behavior.",
      "Removed the fixed-height parity Playwright regression and kept utility-column scroll regression coverage.",
    ],
  },
  {
    id: "reeprise-desktop-columns-now-share-daily-section-start-line",
    timestamp: "2026-04-25T12:53:55+02:00",
    title: "Trainer column now starts at the same top line as daily cards",
    summary:
      "On Reeprise desktop, the trainer utility column now starts at the same vertical position as the daily weather card section for consistent alignment.",
    userHighlights: [
      "Trainer card and daily weather cards now begin on the same top line.",
      "Notification messages no longer push only the left column down.",
      "Desktop utility scroll and fixed-height trainer behavior remain unchanged.",
    ],
    technicalNotes: [
      "Moved `NotificationsCenter` render placement in `src/routes/app/reeprise/index.tsx` above the two-column desktop grid so both columns share the same start anchor.",
      "Removed duplicate notification rendering from inside the main (left) column.",
      "Re-ran existing desktop layout Playwright regressions for utility scrolling and trainer/daily card height parity.",
    ],
  },
  {
    id: "trainer-card-height-aligned-to-daily-weather-cards-on-desktop",
    timestamp: "2026-04-25T12:52:42+02:00",
    title: "Desktop trainer card height now matches daily weather cards",
    summary:
      "The Reeprise desktop trainer card now uses the same fixed card height as the daily weather cards, with internal trainer scrolling inside that frame.",
    userHighlights: [
      "Trainer card and daily weather cards now share the same desktop height.",
      "Long trainer content scrolls inside the trainer card instead of stretching the card height.",
      "The right utility column still keeps independent scrolling behavior.",
    ],
    technicalNotes: [
      "Introduced a shared desktop card-height class token in `src/routes/app/reeprise/index.tsx` and applied it to desktop-strip daily cards plus the desktop trainer slot.",
      "Extended `VocabularyPreviewCard` with an optional `className` prop so desktop layout can apply internal overflow handling without changing other surfaces.",
      "Added Playwright regression in `e2e/reeprise-generation.spec.ts` asserting desktop trainer-slot height matches the primary daily weather card height.",
    ],
  },
  {
    id: "reeprise-mobile-generate-card-centers-with-sidenav-aware-width",
    timestamp: "2026-04-25T12:51:30+02:00",
    title: "Generate Text card now stays centered in compact layouts",
    summary:
      "In compact Reeprise layouts, the Generate Text card now centers against the available content area after side navigation width is accounted for.",
    userHighlights: [
      "The Generate Text card stays visually centered when the compact side navigation rail is visible.",
      "Card width now follows the real usable content width instead of the raw viewport.",
      "Desktop fixed composer behavior remains unchanged.",
    ],
    technicalNotes: [
      "Added dynamic `--app-desktop-nav-width` on `src/routes/app/route.tsx` based on side-nav state (`72px` collapsed, `440px` expanded).",
      "Updated the non-`xl` Generate Text wrapper in `src/routes/app/reeprise/index.tsx` to center and use a sidenav-aware max-width.",
      "Extended `e2e/reeprise-generation.spec.ts` with a compact-layout regression that asserts the mobile compose shell is centered within viewport width minus sidenav width.",
    ],
  },
  {
    id: "guided-mnemonic-ux-now-uses-anchor-words-and-clear-submit-flow",
    timestamp: "2026-04-25T12:50:21+02:00",
    title: "Guided mnemonic flow now has explicit submit and clearer output",
    summary:
      "Guided mnemonic input now has a clear submit action, anchor suggestions are constrained to short sound-similar anchor words, and the generated draft section is explicitly labeled.",
    userHighlights: [
      "You can now explicitly submit the anchor word and scene direction with `Generate from these inputs`.",
      "Anchor suggestions are now short sound-based anchor words instead of random sentence fragments.",
      "The generated result area is now labeled `Generated draft` so it is clear where the final mnemonic appears.",
    ],
    technicalNotes: [
      "Updated mnemonic prompt guidance rules in `barkguide/reeprise/tasks.py` to enforce user guidance usage and exact-anchor behavior when requested.",
      "Refined anchor-suggestion prompt and cleaner in `barkguide/reeprise/tasks.py` to prefer short 1–2 word, sound-similar anchors and filter long sentence-like outputs.",
      "Updated `src/components/VocabularyPreviewCard.tsx` with explicit guidance-submit button, clearer result labeling, and automatic scroll-to-result after generation.",
      "Adjusted mnemonic UI translation keys in `src/assets/translations.json` and updated related backend/E2E regression tests.",
    ],
  },
  {
    id: "reeprise-scrollbar-hide-keeps-utility-wheel-scroll-active",
    timestamp: "2026-04-25T12:41:52+02:00",
    title: "Hidden utility scrollbar now preserves scroll interaction",
    summary:
      "The utility column scrollbar hide style was adjusted so the scrollbar stays invisible without breaking normal wheel/touchpad scrolling.",
    userHighlights: [
      "The right utility column remains scrollable.",
      "Scrollbar visuals stay hidden.",
      "No layout or spacing changes were introduced.",
    ],
    technicalNotes: [
      "Updated `.hide-scrollbar` in `src/index.css` to use WebKit `display: none` for scrollbar pseudo-element instead of zero-size scrollbar dimensions.",
      "Kept cross-browser scrollbar suppression via `scrollbar-width: none` and `-ms-overflow-style: none`.",
      "Retained desktop utility column overflow behavior and re-ran the existing Playwright utility-column scroll regression.",
    ],
  },
  {
    id: "reeprise-desktop-utility-scrollbar-hidden-while-keeping-scroll",
    timestamp: "2026-04-25T12:35:03+02:00",
    title: "Desktop utility column now scrolls without visible scrollbar",
    summary:
      "The Reeprise desktop utility column keeps independent scrolling but now hides the scrollbar UI so the panel looks cleaner.",
    userHighlights: [
      "You can still scroll the trainer column with wheel or touchpad.",
      "The visible scrollbar in the right utility column is now hidden.",
      "Trainer expansion no longer forces full-page growth and no longer shows a side scrollbar.",
    ],
    technicalNotes: [
      "Added a reusable `.hide-scrollbar` helper in `src/index.css` with `scrollbar-width: none`, `-ms-overflow-style: none`, and WebKit scrollbar suppression.",
      "Applied `hide-scrollbar` to the desktop utility aside in `src/routes/app/reeprise/index.tsx` while keeping `overflow-y-auto` behavior.",
      "Verified existing desktop utility scroll regression in `e2e/reeprise-generation.spec.ts` still passes.",
    ],
  },
  {
    id: "reeprise-desktop-trainer-column-now-scrolls-independently",
    timestamp: "2026-04-25T12:31:55+02:00",
    title: "Desktop trainer column now scrolls independently",
    summary:
      "On Reeprise desktop layout, the utility column now has its own scroll area so an expanded trainer card no longer forces the full page height to grow.",
    userHighlights: [
      "The right-side trainer/utility column can now scroll independently on desktop.",
      "Opening a larger trainer card no longer pushes the whole page height unexpectedly.",
      "Compose card spacing at the bottom remains preserved while scrolling the utility column.",
    ],
    technicalNotes: [
      "Updated the desktop utility aside in `src/routes/app/reeprise/index.tsx` with `max-height`, `overflow-y-auto`, and bottom padding for fixed compose-area clearance.",
      "Added Playwright regression coverage in `e2e/reeprise-generation.spec.ts` to assert the desktop utility column keeps `overflow-y-auto` behavior.",
      "No API or data-model changes.",
    ],
  },
  {
    id: "trainer-similar-words-semantic-fallback-and-deduping-refinement",
    timestamp: "2026-04-25T09:44:21+02:00",
    title: "Trainer similar words now distinguishes senses more reliably",
    summary:
      "Similar-word suggestions now keep same-gloss homographs separated by grammar/lemma/context and can still suggest exact-spelling alternatives when translation is pending.",
    userHighlights: [
      "Exact same spelling can still appear when one entry has pending translation but context indicates a different sense.",
      "Same-gloss entries are no longer merged when they represent different grammatical senses.",
      "True duplicates with same spelling and same sense still collapse into one suggestion.",
    ],
    technicalNotes: [
      "Refined semantic signature construction in `src/utils/vocabularySimilarWords.ts` to use pending-aware meaning detection plus context/lemma/grammar fallbacks.",
      "Changed dedupe keying to combine meaning with grammar/lemma/context (instead of meaning-only), while preserving duplicate collapse for same-sense entries.",
      "Extended regression coverage in `src/utils/vocabularySimilarWords.test.ts` with pending-meaning fallback and same-gloss/different-grammar cases.",
    ],
  },
  {
    id: "guided-mnemonic-now-supports-ai-anchor-suggestions",
    timestamp: "2026-04-25T09:43:46+02:00",
    title: "Guided mnemonic now supports AI anchor suggestions",
    summary:
      "Mnemonic drafting now supports guided anchors and scene direction, including one-click AI anchor suggestions when you do not have an idea yet.",
    userHighlights: [
      "You can add your own mnemonic anchor before generating a draft.",
      "A new `Suggest anchors` action proposes short anchor ideas for the selected word.",
      "You can add a scene direction and optionally ask the generator to keep your anchor wording as close as possible.",
    ],
    technicalNotes: [
      "Extended `generate_mnemonic` request handling in `barkguide/reeprise/views.py` to accept optional `anchor`, `direction`, and `use_anchor_exactly` guidance.",
      "Added `/api/reeprise/vocabulary/:id/mnemonic_anchor_suggestions/` action that calls AI and returns cleaned, deduplicated suggestion chips.",
      "Updated mnemonic prompt builders and cleaning helpers in `barkguide/reeprise/tasks.py`, including guided-prompt injection and structured anchor-suggestion parsing.",
      "Updated `src/components/VocabularyPreviewCard.tsx` with guided mnemonic fields, suggestion chips, auto-suggest fallback on empty state, and payload wiring through `useVocabularyGenerateMnemonic`.",
      "Added new translation keys in `src/assets/translations.json` and regression coverage in both `reeprise/tests.py` and `e2e/reeprise-generation.spec.ts`.",
    ],
  },
  {
    id: "trainer-similar-words-now-shows-homographs-and-hides-true-duplicates",
    timestamp: "2026-04-25T09:37:37+02:00",
    title: "Trainer similar words now handles homographs correctly",
    summary:
      "The “Ähnliche Wörter” list now includes exact same-spelling words when they carry a different meaning, while collapsing real duplicates that only differ by source sentence.",
    userHighlights: [
      "Exact same spelling with different meaning now appears in the trainer suggestions.",
      "Repeated duplicates with the same spelling and same meaning are reduced to a single suggestion.",
      "A new label clarifies when a match is the same spelling but another meaning.",
    ],
    technicalNotes: [
      "Extracted similar-word ranking into `src/utils/vocabularySimilarWords.ts` for deterministic filtering and deduplication.",
      "Updated `src/components/VocabularyPreviewCard.tsx` to use the new selector and show a dedicated reason label for exact-spelling/different-meaning matches.",
      "Added regression tests in `src/utils/vocabularySimilarWords.test.ts` covering homograph inclusion and duplicate-meaning collapse.",
    ],
  },
  {
    id: "struggle-word-text-now-uses-at-most-five-words",
    timestamp: "2026-04-25T09:36:27+02:00",
    title: "Text with your words now uses at most five words",
    summary:
      "The struggle-word text flow now limits selected words to five, so generated texts stay focused and easier to follow.",
    userHighlights: [
      "“Text mit deinen Wörtern” now uses a maximum of 5 words.",
      "If fewer than 5 eligible words exist, it still uses the available set.",
      "The card count and generated text selection stay aligned to the same cap.",
    ],
    technicalNotes: [
      "Added `STRUGGLE_PRACTICE_VOCABULARY_LIMIT = 5` in `barkguide/reeprise/tasks.py` and reused it as the default selector limit.",
      "Updated struggle-practice generation and `/api/reeprise/vocabulary/struggle_words/` endpoint to use the shared limit constant.",
      "Added backend regression coverage in `barkguide/reeprise/tests.py` to assert the struggle-words endpoint returns at most five entries.",
    ],
  },
  {
    id: "dialogue-text-typesetting-now-normalizes-punctuation-and-line-breaking",
    timestamp: "2026-04-22T21:18:14+02:00",
    title: "Dialogue text now follows cleaner typesetting rules",
    summary:
      "Text details now normalize detached punctuation and apply better line-breaking behavior, so dialogue lines read more like properly typeset text on mobile and desktop.",
    userHighlights: [
      "Detached punctuation like `word . Next` now renders as `word. Next`.",
      "French dialogue spacing now keeps punctuation attached using proper narrow non-breaking spacing (for example before `?`).",
      "Long words and line wraps now break more gracefully in dialogue and standard text views.",
    ],
    technicalNotes: [
      "Added locale-aware punctuation-spacing normalization in `src/components/utils/generatedTextDetailsTextPaneUtils.tsx` during symbol-run rendering.",
      "Passed text language into span rendering so French punctuation spacing can follow language-specific behavior.",
      "Applied `lang`, `hyphens-auto`, and improved wrap rules in `src/components/GeneratedTextDetailsTextPane.tsx` for cleaner typographic line breaks.",
      "Extended regression tests in `src/components/utils/generatedTextDetailsTextPaneUtils.test.tsx` for detached-period and French-question-mark spacing.",
    ],
  },
  {
    id: "struggle-word-label-now-uses-less-technical-german-wording",
    timestamp: "2026-04-22T21:13:15+02:00",
    title: "Struggle-word labels now use less technical wording",
    summary:
      "The newly introduced focus-word phrasing was softened to more natural wording so the UI stays calm and human without sounding technical.",
    userHighlights: [
      "German label now reads `Text mit deinen Wörtern`.",
      "English label now reads `Text with your words`.",
      "Matching loading and error lines were aligned to the same tone.",
    ],
    technicalNotes: [
      "Updated `struggle_words_title`, `struggle_words_started`, and `struggle_words_failed` in `src/assets/translations.json`.",
      "Replaced `Fokuswörtern` / `Focus-word` wording with less technical alternatives.",
      "No behavior or API changes.",
    ],
  },
  {
    id: "focus-word-text-label-replaces-practice-text-wording",
    timestamp: "2026-04-22T21:11:07+02:00",
    title: "Struggle-word flow now avoids classroom wording",
    summary:
      "The struggle-word text labels were changed from practice-style wording to neutral product language that matches the editorial voice guidelines.",
    userHighlights: [
      "The label was moved away from classroom wording to neutral product language.",
      "Status and error messages in the same flow now use matching neutral wording.",
      "Behavior is unchanged; only copy was adjusted.",
    ],
    technicalNotes: [
      "Updated `struggle_words_title`, `struggle_words_started`, `struggle_words_failed`, and `struggle_words_topic_generic` in `src/assets/translations.json`.",
      "Removed `Übungstext` and `Practice text` phrasing from the struggle-word generation flow.",
      "No route, state, API, or data-model changes.",
    ],
  },
  {
    id: "struggle-words-description-copy-now-uses-neutral-editorial-wording",
    timestamp: "2026-04-22T21:09:40+02:00",
    title: "Struggle-word text description now uses neutral wording",
    summary:
      "The practice-text description was tightened to neutral editorial phrasing and no longer uses progress-coaching language.",
    userHighlights: [
      "The struggle-word helper text now reads more direct and factual.",
      "Meaning stays the same: it still describes a short text built from selected words.",
      "No behavior or flow changes were made.",
    ],
    technicalNotes: [
      "Updated `struggle_words_description` in `src/assets/translations.json` for both German and English.",
      "Replaced the phrase `for your next step` with neutral wording aligned to the product voice guidelines.",
      "No route, state, or API changes were required.",
    ],
  },
  {
    id: "translations-copy-tone-aligned-to-editorial-style-guidelines",
    timestamp: "2026-04-22T21:05:42+02:00",
    title: "Application copy now follows the editorial voice guidelines",
    summary:
      "User-facing translation copy was revised to remove chatty, motivational, and promotional phrasing so interface text stays calm, neutral, and concise.",
    userHighlights: [
      "Messages now avoid exclamation-heavy and hype-style wording.",
      "Emoji-based UI copy was removed from key flows.",
      "Pricing and onboarding labels now use more neutral phrasing.",
    ],
    technicalNotes: [
      "Updated multiple entries in `src/assets/translations.json` including naming, signup, login subtitle, subscription success, photo-import success, vocabulary empty state, and pricing copy keys.",
      "Replaced motivational/promotional wording (for example `Get started`, `Perfect for`, and `Happy learning`) with neutral alternatives.",
      "Removed decorative emojis from interface copy to keep tone consistent across languages.",
    ],
  },
  {
    id: "dialogue-auto-detection-now-ignores-structured-step-and-chapter-lines",
    timestamp: "2026-04-22T20:57:35+02:00",
    title: "Dialogue auto-detection now ignores structured labels",
    summary:
      "Text details no longer switch into dialogue layout for structured prose lines like step/chapter labels that use a colon.",
    userHighlights: [
      "Structured lines such as “Step 1: ...” no longer trigger speaker-style dialogue blocks.",
      "Dialogue layout still works for normal multi-speaker text.",
      "Reading layout is now more stable for instructional or outline-like content.",
    ],
    technicalNotes: [
      "Added a stricter speaker-label heuristic in `src/components/utils/generatedTextDetailsTextPaneUtils.tsx` for dialogue parsing.",
      "Dialogue detection now rejects labels with digits and common structured prefixes such as `step`, `chapter`, and `part`.",
      "Extended unit tests in `src/components/utils/generatedTextDetailsTextPaneUtils.test.tsx` with step/chapter false-positive regression cases.",
    ],
  },
  {
    id: "grammar-lesson-now-uses-configured-model-with-cache-and-token-accounting",
    timestamp: "2026-04-22T20:55:59+02:00",
    title: "Grammar lesson now follows model settings and avoids duplicate calls",
    summary:
      "Grammar lesson generation now uses the configured AI model, tracks token usage, and reuses a short-lived cached result for repeated requests on the same text.",
    userHighlights: [
      "Grammar lesson generation is now more stable under repeated clicks.",
      "Repeated requests for the same text can return faster during a short cache window.",
      "Response content format remains unchanged.",
    ],
    technicalNotes: [
      "Updated `grammar_lesson` in `barkguide/reeprise/views.py` to resolve model selection from `UserSetting.ai_model` with a safe fallback.",
      "Added usage-token accounting for grammar lesson responses via existing `track_tokens_in_account` flow.",
      "Added a short TTL cache key scoped by user, text, response language, learning language, model, and text-content hash.",
    ],
  },
  {
    id: "highlight-preview-word-matching-now-respects-line-boundaries",
    timestamp: "2026-04-22T20:53:26+02:00",
    title: "Highlight preview matching now works across line breaks",
    summary:
      "Word preview mapping now correctly stops at line boundaries, so highlighted words at the end of a line still resolve to the expected saved vocabulary.",
    userHighlights: [
      "Highlighted words followed by a line break now open the correct preview.",
      "No behavior changed for normal inline punctuation and spaces.",
      "Preview reliability is improved in multiline text details.",
    ],
    technicalNotes: [
      "Added `WORD_BOUNDARY_SYMBOLS` in `src/components/utils/generatedTextDetailsTextPaneUtils.tsx` to include `\\n`, `\\r`, and `\\t` for preview-word extraction.",
      "Updated `readWordAtStart` in the same file to use the boundary-aware symbol set.",
      "Added regression coverage in `src/components/utils/generatedTextDetailsTextPaneUtils.test.tsx` for `harbor\\nmarket` highlight mapping.",
    ],
  },
  {
    id: "desktop-recent-history-copy-now-matches-mixed-history-content",
    timestamp: "2026-04-22T20:51:39+02:00",
    title: "Desktop history copy now reflects mixed recent texts",
    summary:
      "The desktop strip language was updated from daily-only wording to recent-text wording, so labels match the current mixed-history behavior.",
    userHighlights: [
      "History cards now use wording that fits mixed recent content.",
      "The show-more control now describes recent texts instead of older daily cards.",
      "Behavior stays the same; only wording and test naming were clarified.",
    ],
    technicalNotes: [
      "Updated translation copy in `src/assets/translations.json` for `dailyTextPreviousBadge`, `reeprise_daily_show_more`, and `reeprise_daily_show_less` to use recent-history wording.",
      "Renamed the matching Playwright test title in `e2e/reeprise-generation.spec.ts` to reflect recent-history semantics.",
      "No API or data model changes were required.",
    ],
  },
  {
    id: "dialogue-layout-now-emphasizes-the-first-speaker-as-primary",
    timestamp: "2026-04-22T20:27:32+02:00",
    title: "Dialogue layout now highlights the first speaker as primary",
    summary:
      "Dialogue text details now emphasize the first speaker with a stronger accent treatment, while other speakers are intentionally quieter for clearer visual hierarchy.",
    userHighlights: [
      "The first speaker now stands out as the primary highlighted voice.",
      "Subsequent speakers are still distinct but visually calmer.",
      "Reading flow and vocabulary interactions stay unchanged.",
    ],
    technicalNotes: [
      "Updated speaker-tone mapping in `src/components/GeneratedTextDetailsTextPane.tsx` so index `0` uses a stronger primary sky palette.",
      "Moved non-primary speakers to neutral secondary palettes (`slate`, `zinc`, `stone`) to reduce competing emphasis.",
      "No backend/API behavior changes.",
    ],
  },
  {
    id: "dialogue-layout-no-longer-flashes-from-regular-into-speaker-mode",
    timestamp: "2026-04-22T20:26:31+02:00",
    title: "Dialogue details no longer flash from regular to speaker layout",
    summary:
      "Text details now avoid the initial regular-text flash before dialogue styling appears, so dialogue pages open in a visually stable reading layout.",
    userHighlights: [
      "Dialogue texts now open directly in speaker-grouped mode in typical cases.",
      "The brief regular-layout flash before speaker styling is removed.",
      "Non-dialogue texts keep the same existing rendering behavior.",
    ],
    technicalNotes: [
      "Updated dialogue layout selection in `src/components/GeneratedTextDetailsTextPane.tsx` to prefer dialogue rendering when parsed dialogue segments are already detectable, even if `text_type` has not resolved yet.",
      "Kept explicit `text_type === \"DIALOGUE\"` behavior as the primary condition once API data is available.",
      "No backend/API changes were required.",
    ],
  },
  {
    id: "dialogue-text-details-readability-tuned-with-stronger-speaker-contrast",
    timestamp: "2026-04-22T20:24:43+02:00",
    title: "Dialogue text details readability is now tighter and clearer",
    summary:
      "Dialogue blocks on text details now use a more compact text size and stronger speaker contrast, so conversations are easier to read without changing the non-dialogue layout.",
    userHighlights: [
      "Dialogue lines are now less oversized and easier to scan in sequence.",
      "Speaker separation is clearer through stronger color contrast and border emphasis.",
      "Regular non-dialogue text presentation remains unchanged.",
    ],
    technicalNotes: [
      "Adjusted dialogue speaker tone classes in `src/components/GeneratedTextDetailsTextPane.tsx` to increase border and label contrast while keeping the existing editorial style.",
      "Reduced dialogue body typography from large display sizing to a tighter reading scale (`text-base` / `md:text-lg`) with calmer line-height.",
      "Tightened dialogue block spacing and increased left accent stroke weight for faster speaker switching during reading.",
    ],
  },
  {
    id: "reeprise-feed-card-hover-borders-softened",
    timestamp: "2026-04-22T20:14:52+02:00",
    title: "Feed card hover borders are now softer",
    summary:
      "The hover stroke on Reeprise feed cards was toned down to reduce aggressive contrast and keep the interface calmer.",
    userHighlights: [
      "Hover state now feels subtler and less harsh.",
      "Card hover affordance remains visible.",
      "No interaction behavior changed.",
    ],
    technicalNotes: [
      "Adjusted hover/border token classes in `src/components/ReepriseTextCard.tsx` for both regular and podcast card variants.",
      "Reduced unread-state border intensity from blue-200/300 to blue-100/200.",
      "Reduced neutral-state hover contrast from slate-100/200 + lg 300/400 to softer slate-200/300 levels.",
    ],
  },
  {
    id: "dialogue-text-details-now-use-speaker-grouped-reading-layout",
    timestamp: "2026-04-22T20:11:59+02:00",
    title: "Dialogue text details now group lines by speaker",
    summary:
      "Generated text details now render dialogue texts in speaker-grouped blocks, making multi-person conversations easier to scan while keeping the existing reading and vocabulary interactions.",
    userHighlights: [
      "Dialogue texts now show each speaker in distinct visual blocks instead of one long paragraph.",
      "A compact speaker row appears above the text so multi-person conversations are easier to follow.",
      "Non-dialogue text types keep the existing text-details layout unchanged.",
    ],
    technicalNotes: [
      "Added dialogue-segment parsing in `src/components/utils/generatedTextDetailsTextPaneUtils.tsx` and enabled offset-aware span generation so vocabulary interactions keep correct global indexes in per-speaker blocks.",
      "Updated `GeneratedTextDetailsTextPane` to switch to a speaker-grouped renderer only when `text_type === \"DIALOGUE\"`, while retaining the existing body rendering for all other types.",
      "Extended Vitest coverage in `src/components/utils/generatedTextDetailsTextPaneUtils.test.tsx` for dialogue parsing and offset-index behavior.",
    ],
  },
  {
    id: "daily-strip-desktop-cards-now-use-equal-height",
    timestamp: "2026-04-22T19:57:28+02:00",
    title: "Daily strip cards now keep equal height on desktop",
    summary:
      "Daily cards in the desktop strip now align to equal heights, so card rows no longer look uneven when one card has less content.",
    userHighlights: [
      "Daily strip cards on desktop now align with a consistent height in each row.",
      "The primary daily card and older/history cards now follow the same vertical rhythm.",
      "Card click/open behavior remains unchanged.",
    ],
    technicalNotes: [
      "Updated daily section grid in `src/routes/app/reeprise/index.tsx` from `items-start` to `items-stretch` for consistent row height behavior.",
      "Removed desktop strip `self-start` overrides and applied `h-full` to primary and history daily cards so they fill their grid tracks.",
      "Extended desktop layout Playwright coverage in `e2e/reeprise-generation.spec.ts` to assert equal heights across the first daily strip cards.",
    ],
  },
  {
    id: "mobile-topbar-toggle-now-has-left-padding",
    timestamp: "2026-04-22T19:55:47+02:00",
    title: "Mobile topbar toggle now has left spacing",
    summary:
      "The top-left side-nav toggle now has a small left inset on mobile so it no longer sits flush against the screen edge.",
    userHighlights: [
      "The side-nav toggle has clearer left breathing room on mobile.",
      "Desktop alignment remains unchanged.",
      "Toggle behavior remains unchanged.",
    ],
    technicalNotes: [
      "Updated the topbar toggle slot in `src/routes/app/route.tsx` with mobile-only left padding (`pl-2`) and reset on desktop (`md:pl-0`).",
      "Kept the fixed desktop toggle axis and existing sidenav expand/collapse logic unchanged.",
      "No API, state, or navigation flow changes.",
    ],
  },
  {
    id: "desktop-sidenav-top-border-removed-from-icon-rail",
    timestamp: "2026-04-22T19:54:24+02:00",
    title: "Desktop side-nav rail no longer shows a top border line",
    summary:
      "Removed the extra top horizontal border from the desktop side-nav rail so the icon column starts cleaner under the top bar.",
    userHighlights: [
      "The horizontal line above the first side-nav icon is gone.",
      "The left vertical divider and bottom rail border remain in place.",
      "Navigation behavior is unchanged.",
    ],
    technicalNotes: [
      "Changed side-nav panel desktop border classes in `src/routes/app/route.tsx` from `md:border-y md:border-l` to `md:border-b md:border-l`.",
      "Kept the existing desktop rail width logic and expand/collapse behavior untouched.",
      "Extended Playwright desktop sidenav regression in `e2e/reeprise-generation.spec.ts` to assert `borderTopWidth` is `0px`.",
    ],
  },
  {
    id: "daily-strip-show-more-now-reveals-history-when-done-card-is-active",
    timestamp: "2026-04-22T19:53:48+02:00",
    title: "Daily strip now reveals older cards even after today is done",
    summary:
      "The “Alle älteren Karten anzeigen” toggle in the daily strip now correctly reveals older cards when today’s daily card is already marked as done.",
    userHighlights: [
      "Clicking “Alle älteren Karten anzeigen” now shows the expected older cards in the daily strip.",
      "The fix applies even when the primary daily card is in the completed (“done”) state.",
      "Daily card navigation behavior remains unchanged.",
    ],
    technicalNotes: [
      "Fixed `DailyPracticeCallout` in `src/routes/app/reeprise/index.tsx` to render history cards in both branches (done and create states) instead of returning early without history in the done branch.",
      "Added `data-testid=\"reeprise-daily-history-card\"` to daily history cards for stable regression assertions.",
      "Added Playwright regression in `e2e/reeprise-generation.spec.ts` to verify show-more expansion with an active done card state.",
    ],
  },
  {
    id: "desktop-sidenav-divider-now-renders-as-single-full-height-line",
    timestamp: "2026-04-22T19:51:41+02:00",
    title: "Desktop side-nav divider now renders as one full-height line",
    summary:
      "The desktop side-nav divider now renders as one continuous line from top to bottom, without a short extra segment beside the toggle icon.",
    userHighlights: [
      "The side-nav split line now appears continuous from the very top to the bottom.",
      "The extra short divider segment next to the top-left toggle no longer appears as a separate line.",
      "Collapsed and expanded desktop side-nav states keep the same divider behavior.",
    ],
    technicalNotes: [
      "Moved `topbar-sidenav-divider` in `src/routes/app/route.tsx` from a header-only absolute element to a full-height fixed desktop overlay.",
      "Updated desktop side-nav panel borders to avoid a duplicate right-edge divider while preserving panel framing.",
      "Extended `e2e/reeprise-generation.spec.ts` to assert the divider spans the full viewport height in compact desktop mode.",
    ],
  },
  {
    id: "topbar-sidenav-toggle-now-keeps-fixed-x-position-on-open",
    timestamp: "2026-04-22T19:46:11+02:00",
    title: "Side-nav toggle now keeps a fixed position when opening",
    summary:
      "The topbar side-nav toggle no longer shifts horizontally when the side navigation opens on desktop.",
    userHighlights: [
      "The toggle stays in the same place before and after opening the side navigation.",
      "The icon no longer jumps during expand/collapse transitions.",
      "Side navigation behavior and content remain unchanged.",
    ],
    technicalNotes: [
      "Updated topbar layout in `src/routes/app/route.tsx` to keep the toggle in a fixed desktop slot (`md:w-[72px]`) instead of binding it to dynamic nav width.",
      "Removed the temporary root CSS variable wiring used for the moving toggle slot.",
      "Extended desktop Playwright coverage in `e2e/reeprise-generation.spec.ts` to assert stable toggle X-position across open/close.",
    ],
  },
  {
    id: "topbar-toggle-now-aligns-to-sidenav-icon-axis",
    timestamp: "2026-04-22T19:41:59+02:00",
    title: "Topbar side-nav toggle now aligns with sidenav icon axis",
    summary:
      "The topbar side-nav toggle is now centered on the same vertical axis as the compact sidenav icon column, so it no longer appears offset.",
    userHighlights: [
      "The top-left toggle now lines up with the icon rail below.",
      "Collapsed and expanded sidenav states keep consistent alignment.",
      "Sidenav behavior remains unchanged.",
    ],
    technicalNotes: [
      "Added shared `--app-desktop-nav-width` on the app shell in `src/routes/app/route.tsx` and reused it for both topbar and sidenav layout.",
      "Wrapped the topbar toggle in a dedicated desktop nav-width slot (`md:w-[var(--app-desktop-nav-width)]`) centered with `justify-center`.",
      "Kept existing toggle handler, accessibility label, and side navigation interaction logic unchanged.",
    ],
  },
  {
    id: "app-sidenav-toggle-now-uses-directional-chevrons-and-aligned-spacing",
    timestamp: "2026-04-22T19:36:38+02:00",
    title: "Side-nav toggle now uses directional chevrons and aligned sizing",
    summary:
      "The side-nav toggle icon now uses standard directional chevrons for expand/collapse and is aligned with neighboring topbar icons using consistent button sizing and spacing.",
    userHighlights: [
      "The toggle icon now better communicates open/close sidebar direction.",
      "Topbar icon alignment is cleaner with matching button dimensions.",
      "Sidebar behavior remains unchanged.",
    ],
    technicalNotes: [
      "Replaced `ViewColumnsIcon` usage in `src/routes/app/route.tsx` with `ChevronDoubleRightIcon` (collapsed) and `ChevronDoubleLeftIcon` (expanded).",
      "Normalized header icon-button sizing to `h-9 w-9` and removed offset margins that made the toggle look misaligned.",
      "Kept existing sidenav state handling, accessibility label, and desktop/mobile behavior unchanged.",
    ],
  },
  {
    id: "app-sidenav-toggle-now-uses-viewcolumns-panel-icon",
    timestamp: "2026-04-22T19:31:22+02:00",
    title: "Side-nav toggle now uses a panel-style icon",
    summary:
      "The top-left side-nav toggle now uses a standard panel-outline icon that visually matches sidebar controls better than the previous chevron variant.",
    userHighlights: [
      "The toggle now looks closer to common sidebar panel icons.",
      "The icon style is now from a standard free icon set instead of a custom look.",
      "Toggle behavior stays exactly the same.",
    ],
    technicalNotes: [
      "Replaced toggle icon usage in `src/routes/app/route.tsx` with Heroicons `ViewColumnsIcon`.",
      "Removed prior double-chevron toggle icon imports for this control.",
      "Kept existing click handler, accessibility label, and sidenav state logic unchanged.",
    ],
  },
  {
    id: "reeprise-web-declutter-pass-rebalanced-spacing-and-control-emphasis",
    timestamp: "2026-04-22T19:30:54+02:00",
    title: "Reeprise web declutter pass rebalanced spacing and emphasis",
    summary:
      "The Reeprise web view now uses calmer spacing rhythm and lower-contrast secondary controls to reduce visual clutter while preserving existing flows.",
    userHighlights: [
      "Main content blocks now have clearer breathing room and separation.",
      "Daily secondary toggle and feed load-more control are visually quieter.",
      "Right utility column now stays visible while scrolling on desktop for cleaner scanning.",
    ],
    technicalNotes: [
      "In `src/routes/app/reeprise/index.tsx`, increased major layout spacing (`gap`/`space-y`) and slightly reduced feed heading scale.",
      "Changed daily show-more toggle and feed load-more button styles to neutral, lower-contrast treatments.",
      "Made desktop utility sidebar sticky (`xl:sticky xl:top-20`) and tightened internal sidebar spacing.",
    ],
  },
  {
    id: "app-sidenav-toggle-now-uses-standard-free-heroicon",
    timestamp: "2026-04-22T19:28:34+02:00",
    title: "Side-nav toggle now uses a standard free icon",
    summary:
      "The app side-nav toggle now uses a standard Heroicons chevron icon set instead of a custom-drawn glyph, so the control looks cleaner and more native.",
    userHighlights: [
      "The toggle icon now matches a common app-style icon look.",
      "Collapsed state shows a double-right chevron; expanded state shows a double-left chevron.",
      "Sidebar expand/collapse behavior is unchanged.",
    ],
    technicalNotes: [
      "Replaced the custom toggle glyph markup in `src/routes/app/route.tsx` with Heroicons `ChevronDoubleRightIcon` and `ChevronDoubleLeftIcon`.",
      "Removed the now-unused custom line/dot icon structure from the toggle button.",
      "Kept existing accessibility label and toggle handler behavior unchanged.",
    ],
  },
  {
    id: "reeprise-page-top-padding-increased-under-top-bar",
    timestamp: "2026-04-22T19:27:48+02:00",
    title: "Reeprise cards now sit lower under the top bar",
    summary:
      "Increased top padding on the Reeprise page so the first card row is no longer too close to the top bar.",
    userHighlights: [
      "The top cards now have more breathing room below the header bar.",
      "Visual separation between the top bar and first content block is improved.",
      "No changes to card logic or interactions.",
    ],
    technicalNotes: [
      "Updated Reeprise page wrapper classes in `src/routes/app/reeprise/index.tsx` to increase top padding (`pt-4` and `sm:pt-5`).",
      "Kept existing layout grid, sidebar split, and fixed composer behavior unchanged.",
      "No API/data/state logic changes.",
    ],
  },
  {
    id: "app-sidenav-toggle-now-uses-sidebar-chevron-icon",
    timestamp: "2026-04-22T19:26:33+02:00",
    title: "Side-nav toggle now uses a sidebar chevron icon",
    summary:
      "The top-left app toggle now uses a sidebar-style expand/collapse icon (panel divider with chevron) instead of a burger icon, matching common desktop sidenav behavior.",
    userHighlights: [
      "The toggle now visually communicates sidebar expand/collapse instead of menu open.",
      "Collapsed state shows a right chevron; expanded state shows a left chevron.",
      "Navigation behavior remains the same.",
    ],
    technicalNotes: [
      "Updated the toggle glyph in `src/routes/app/route.tsx` from a custom burger/X animation to a panel-divider + directional chevron icon.",
      "Added `ChevronLeftIcon` usage for expanded-state direction while reusing `ChevronRightIcon` for collapsed-state direction.",
      "Kept existing toggle handler, accessibility label, and desktop/mobile navigation logic unchanged.",
    ],
  },
  {
    id: "desktop-sidenav-divider-now-overlays-topbar-crossing",
    timestamp: "2026-04-22T19:23:52+02:00",
    title: "Desktop side-nav divider now overlays the topbar crossing",
    summary:
      "The vertical desktop divider for the side navigation now draws above the topbar line at their crossing point, so the split looks continuous and cleaner.",
    userHighlights: [
      "The side-nav divider no longer looks interrupted where it meets the top bar.",
      "The vertical divider now visually sits above the topbar baseline at the crossing.",
      "Divider positioning still follows compact and expanded side-nav states.",
    ],
    technicalNotes: [
      "Added a desktop-only `topbar-sidenav-divider` overlay element in `src/routes/app/route.tsx` with absolute positioning and animated left offset for collapsed (`72px`) and expanded (`440px`) nav widths.",
      "Kept existing side navigation structure and panel border behavior unchanged.",
      "Extended desktop Playwright coverage in `e2e/reeprise-generation.spec.ts` to assert divider position in compact state and after expansion toggle.",
    ],
  },
  {
    id: "reeprise-daily-header-removed-and-history-toggle-moved-below-cards",
    timestamp: "2026-04-22T19:22:10+02:00",
    title: "Daily header removed and older-cards toggle moved below strip",
    summary:
      "The “Deine täglichen Texte” heading above the daily strip was removed, and the “Alle älteren Karten anzeigen” control now appears below the daily cards.",
    userHighlights: [
      "Daily cards now start immediately without a section title above them.",
      "The older-cards toggle sits below the card row.",
      "Daily card order and behavior remain unchanged.",
    ],
    technicalNotes: [
      "Removed daily section header block from `src/routes/app/reeprise/index.tsx`.",
      "Moved daily history toggle render block to below the daily card grid in the same file.",
      "Updated Playwright layout test in `e2e/reeprise-generation.spec.ts` by removing assertions for the removed daily heading.",
    ],
  },
  {
    id: "reeprise-utility-cards-now-mount-once-per-viewport",
    timestamp: "2026-04-21T22:23:49+02:00",
    title: "Reeprise now mounts one trainer utility stack per viewport",
    summary:
      "The Reeprise screen now mounts a single Vocabulary Trainer/Quick Translate utility stack at a time, preventing duplicate hidden instances that could trigger heavy rerenders.",
    userHighlights: [
      "The trainer utility area on Reeprise no longer duplicates in the DOM across mobile/desktop layouts.",
      "Page responsiveness is improved on Reeprise, especially during state-heavy trainer updates.",
      "This reduces the chance of browser slowdown warnings caused by repeated render/update churn.",
    ],
    technicalNotes: [
      "Added viewport-gated utility rendering in `src/routes/app/reeprise/index.tsx` so mobile and desktop utility stacks are mounted mutually exclusively instead of via CSS-only hiding.",
      "Added Playwright regression in `e2e/reeprise-generation.spec.ts` to verify exactly one `vocabulary-preview-card` is mounted on Reeprise and no React render-loop console errors are emitted after load.",
      "Validated the new regression locally with `npx playwright test e2e/reeprise-generation.spec.ts --grep \"reeprise mounts a single vocabulary preview card and avoids render-loop errors\"`.",
    ],
  },
  {
    id: "trainer-now-localizes-pending-translation-placeholder",
    timestamp: "2026-04-21T22:15:11+02:00",
    title: "Trainer now shows localized pending-translation text",
    summary:
      "Words added from text details no longer show the raw backend placeholder `currently beeing generated` in trainer cards.",
    userHighlights: [
      "Pending translations in trainer cards now show a proper localized UI message.",
      "The same fix applies to similar-word suggestions inside the trainer card.",
      "You no longer see backend placeholder wording in normal training flow.",
    ],
    technicalNotes: [
      "Added pending-translation normalization in `src/components/VocabularyPreviewCard.tsx` for both main answer/question text and similar-word translation rows.",
      "Mapped backend placeholders (`currently beeing generated` and `currently being generated`) to existing translation key `generated_text_word_preview_translation_pending`.",
      "Validated frontend compilation with `npm run build`.",
    ],
  },
  {
    id: "daily-weather-texts-now-include-reinforcement-vocabulary-links",
    timestamp: "2026-04-21T22:10:22+02:00",
    title: "Daily weather texts now include reinforcement vocabulary links",
    summary:
      "Daily generated texts now carry reinforcement vocabulary into saved text usages, so those words appear in the vocabulary list and can be highlighted in the text view.",
    userHighlights: [
      "Daily weather-card texts now surface reinforcement words in the right-side vocabulary list.",
      "Those reinforcement words are now highlightable from persisted text positions.",
      "Daily generation still keeps a natural reading flow while weaving the words in.",
    ],
    technicalNotes: [
      "Extended daily generation prompts in `barkguide/reeprise/tasks.py` to include reinforcement vocabulary entries for `DAILY_NUDGE` texts.",
      "Enabled missing-word retry handling for daily generation and persisted linked `VocabularyUsage` spans after completion.",
      "Added backend regression coverage in `barkguide/reeprise/tests.py` to assert daily texts include reinforcement words in both `struggle_words` and `/api/reeprise/vocabulary/?generated_text_id=...` results.",
    ],
  },
  {
    id: "highlighted-generation-words-now-open-quick-preview-with-hover-or-click",
    timestamp: "2026-04-21T22:09:24+02:00",
    title: "Generation-highlighted words now open quick preview on hover/click",
    summary:
      "Highlighted words that came from generation targets now open the same quick translation preview as words that were added directly from this text.",
    userHighlights: [
      "Hover/click quick preview now works for both kinds of highlighted words in text details.",
      "Generation-target highlights no longer look interactive without opening a translation preview.",
      "If the word already has a saved translation, the preview shows it immediately.",
    ],
    technicalNotes: [
      "Added selected-highlight fallback mapping in `src/components/utils/generatedTextDetailsTextPaneUtils.tsx` so preview vocabulary can be resolved by highlighted word match when no usage-position mapping exists.",
      "Updated `src/components/GeneratedTextDetailsTextPane.tsx` to merge those fallback mappings into `previewableStarts` while keeping usage-based mappings authoritative.",
      "Added Vitest regression coverage in `src/components/utils/generatedTextDetailsTextPaneUtils.test.tsx` for selected-highlight mapping and non-override behavior.",
    ],
  },
  {
    id: "trainer-delete-no-longer-sticks-on-loading-copy",
    timestamp: "2026-04-21T22:07:38+02:00",
    title: "Trainer delete no longer gets stuck on loading copy",
    summary:
      "Deleting a vocabulary word from the trainer card no longer leaves the card stuck on the “Vokabeln werden geladen...” state.",
    userHighlights: [
      "After deleting a word in the trainer card, the trainer now continues instead of freezing on the loading message.",
      "Queue updates now keep existing trainer state visible during deletion refresh.",
      "The delete action still shows the same success and error feedback messages.",
    ],
    technicalNotes: [
      "Removed hard reset of trainer query data in `src/hooks/useVocabularyDelete.ts` and kept invalidate-based refresh behavior.",
      "Removed the extra awaited trainer refetch inside delete handling in `src/components/VocabularyPreviewCard.tsx` to avoid blocking the state transition.",
      "Narrowed initial-loading placeholder conditions in `src/components/VocabularyPreviewCard.tsx` so delete-driven transitions no longer render as first-load spinner state.",
    ],
  },
  {
    id: "text-details-trainer-now-reuses-main-trainer-card-with-text-scoped-queue",
    timestamp: "2026-04-21T22:02:23+02:00",
    title: "Text Details trainer now uses the same main trainer card",
    summary:
      "Starting trainer from Text Details now opens the same trainer card experience used on the main Reeprise page, while limiting the queue to words from the current text.",
    userHighlights: [
      "Text Details no longer sends you to a separate trainer-style page.",
      "The exact same trainer card UI and actions from the main page are now used in reading mode.",
      "Word selection stays scoped to the current generated text, so unrelated words do not appear.",
    ],
    technicalNotes: [
      "Reused `VocabularyPreviewCard` directly in `src/routes/app/reeprise/$generatedTextId.tsx` and replaced route navigation with an in-page trainer toggle.",
      "Extended `src/components/VocabularyPreviewCard.tsx` to accept optional `generatedTextId` and pass it to the existing trainer query hook.",
      "Added scoped queue regression in `e2e/reeprise-generation.spec.ts` to verify `top_fifteen_priority` requests include `generated_text_id` when launched from Text Details.",
    ],
  },
  {
    id: "on-demand-grammar-lesson-for-generated-text-details",
    timestamp: "2026-04-21T22:00:06+02:00",
    title: "Generated text details can now create an on-demand grammar lesson",
    summary:
      "Each generated text now offers a dedicated grammar-lesson action that analyzes the current text only when you request it.",
    userHighlights: [
      "A new “Generate grammar lesson” action is available in generated text details.",
      "Grammar analysis is generated only after you click the action, not automatically in the background.",
      "The lesson appears directly on the text details screen with a short summary and concrete grammar points from the text.",
    ],
    technicalNotes: [
      "Added `grammar_lesson` action to `GeneratedTextViewSet` in `barkguide/reeprise/views.py` (`POST /api/reeprise/generated_texts/:id/grammar_lesson/`) with model-based analysis prompt and structured JSON response parsing.",
      "Extended generated-text details UI in `src/routes/app/reeprise/$generatedTextId.tsx` with on-demand request state, error handling, and lesson rendering.",
      "Added translation keys in `src/assets/translations.json`, backend API coverage in `barkguide/reeprise/tests.py`, and Playwright regression coverage in `e2e/reeprise-generation.spec.ts` to verify true on-demand behavior.",
    ],
  },
  {
    id: "saved-highlight-word-preview-now-shows-existing-translation-on-fast-repeat-click",
    timestamp: "2026-04-21T21:49:53+02:00",
    title: "Saved highlighted words now show existing translations on fast repeat click",
    summary:
      "When you quickly tap or click a highlighted word that was already added before, the preview now shows the saved translation immediately instead of a temporary “translation is being prepared” state.",
    userHighlights: [
      "Fast repeat taps/clicks on already-saved highlighted words now open with the stored translation right away.",
      "Saved-word preview behavior is now consistent across normal and quick interactions.",
      "No extra action is required to reveal translations that are already available on the vocabulary card.",
    ],
    technicalNotes: [
      "Changed preview-vocabulary merge precedence in `src/components/GeneratedTextDetailsTextPane.tsx` so persisted vocabulary data from the API overrides stale local preview cache when both exist for the same highlight position.",
      "Added `buildPreviewVocabularyByStartMap` in `src/components/utils/generatedTextDetailsTextPaneUtils.tsx` to centralize and enforce the merge behavior.",
      "Added a Vitest regression in `src/components/utils/generatedTextDetailsTextPaneUtils.test.tsx` verifying that a persisted translated entry wins over a local pending placeholder.",
    ],
  },
  {
    id: "text-details-now-starts-a-trainer-scoped-to-the-current-text",
    timestamp: "2026-04-21T21:48:46+02:00",
    title: "Text Details now starts a trainer scoped to the current text",
    summary:
      "You can now start vocabulary training directly from a text’s details page, and the trainer session is limited to vocabulary from that specific generated text.",
    userHighlights: [
      "Text Details now includes a direct “Start trainer for this text” action.",
      "The trainer session focuses only on words connected to the current text, avoiding unrelated vocabulary.",
      "You can jump back to the same text details page from inside training mode.",
    ],
    technicalNotes: [
      "Extended trainer query parameters with `generated_text_id` in `barkguide/reeprise/serializer.py` and applied scoped filtering in `barkguide/reeprise/views.py` for `/vocabulary/top_fifteen_priority/`.",
      "Added text-scoped trainer navigation from `src/routes/app/reeprise/$generatedTextId.tsx` and support for scoped mode/back link in `src/routes/app/vocabulary_training_mode.tsx`.",
      "Updated query plumbing in `src/hooks/useVocabularyTrainerVocabularies.ts` and added regression coverage in `barkguide/reeprise/tests.py` plus `e2e/reeprise-generation.spec.ts` for the new route-transition flow.",
    ],
  },
  {
    id: "struggle-word-texts-now-link-target-words-to-text-vocabulary-list",
    timestamp: "2026-04-21T21:47:59+02:00",
    title: "Struggle-word texts now link target words into the text vocabulary list",
    summary:
      "Generated struggle-word texts now persist usage links for matched target words, so those words appear in the text’s vocabulary list and can be highlighted from saved usage positions.",
    userHighlights: [
      "Words selected for tricky-word generation now show up in the vocabulary list for that generated text.",
      "Those target words can now be highlighted from persisted text positions, not only from ad-hoc matching.",
      "The fix applies automatically for newly generated struggle-word texts.",
    ],
    technicalNotes: [
      "Added `_find_word_surface_matches` and `_link_struggle_vocabulary_usages` in `barkguide/reeprise/tasks.py`.",
      "Hooked struggle-usage linking into `complete_generated_text` after final content is saved.",
      "Extended regression coverage in `barkguide/reeprise/tests.py` to assert created `VocabularyUsage` rows and inclusion in `/api/reeprise/vocabulary/?generated_text_id=...` results.",
    ],
  },
  {
    id: "vocabulary-trainer-card-now-supports-direct-word-deletion",
    timestamp: "2026-04-21T21:46:46+02:00",
    title: "Vocabulary trainer card now supports direct word deletion",
    summary:
      "You can now delete a vocabulary word directly from the trainer card while practicing, without leaving the current training flow.",
    userHighlights: [
      "A delete action is now available directly inside the expanded trainer card.",
      "Deleting a word removes it from the current trainer flow immediately.",
      "You no longer need to switch to the vocabulary progress page just to remove a word.",
    ],
    technicalNotes: [
      "Added an inline delete action in `src/components/VocabularyPreviewCard.tsx` using the existing `useVocabularyDelete` mutation and existing delete success/error translations.",
      "Updated trainer card state flow so deletion advances the session and refreshes queue data in-place.",
      "Added Playwright regression coverage in `e2e/reeprise-generation.spec.ts` to seed a vocabulary item and verify direct deletion from the trainer card.",
    ],
  },
  {
    id: "daily-weather-text-topic-history-now-avoids-single-niche-overfit",
    timestamp: "2026-04-21T21:36:54+02:00",
    title: "Daily weather text now avoids overfitting to one repeated niche topic",
    summary:
      "Daily text generation now diversifies topic-history signals so one repeatedly entered niche topic no longer dominates weather-card texts.",
    userHighlights: [
      "Daily weather texts now draw from a broader mix of your past interests.",
      "Repeated niche entries are treated as one preference signal instead of being echoed too often.",
      "You should see more practical and varied daily reading topics over time.",
    ],
    technicalNotes: [
      "Added diversified interest-topic selection in `barkguide/reeprise/tasks.py` before building daily-guidance prompts.",
      "Deduplicated repeated raw-topic entries and capped dominance from heavily repeated topic/category history.",
      "Added regression coverage in `barkguide/reeprise/tests.py` to ensure repeated niche topics appear at most once in daily-guidance context.",
    ],
  },
  {
    id: "reeprise-non-feed-card-surfaces-now-match-feed-background-tone",
    timestamp: "2026-04-17T21:59:47+02:00",
    title: "Reeprise non-feed card surfaces now match feed background tone",
    summary:
      "Utility and generate cards now use the same neutral background tone as the feed area to create a more consistent surface system.",
    userHighlights: [
      "Trainer, Quick Translate, and Generate card backgrounds now match the feed backdrop tone.",
      "Cards look less contrast-heavy and more visually unified.",
      "Card content and interactions remain unchanged.",
    ],
    technicalNotes: [
      "Updated card root backgrounds to `bg-neutral-50` in `src/components/VocabularyPreviewCard.tsx`, `src/components/QuickTranslateCard.tsx`, and `src/components/GenerateTextCard.tsx`.",
      "Kept existing borders, sizing, and layout behavior unchanged.",
      "No API/state logic changes were introduced.",
    ],
  },
  {
    id: "app-top-bar-now-uses-sidebar-matching-stroke-border",
    timestamp: "2026-04-17T21:58:18+02:00",
    title: "Top bar now uses a sidebar-matching border stroke",
    summary:
      "Added a thin slate border to the top bar so it uses the same stroke treatment as the sidebar panel.",
    userHighlights: [
      "Top bar now has a clear outline stroke.",
      "Header and sidebar now share a consistent border language.",
      "No navigation or interaction behavior changed.",
    ],
    technicalNotes: [
      "Updated top-bar container classes in `src/routes/app/route.tsx` to include `border border-slate-200`.",
      "Kept the previously removed top-bar shadow unchanged (still no shadow).",
      "No layout, routing, or state logic changed.",
    ],
  },
  {
    id: "reeprise-daily-section-title-now-has-more-top-spacing",
    timestamp: "2026-04-17T21:57:24+02:00",
    title: "Reeprise daily section title now has more top spacing",
    summary:
      "Added additional vertical space above the “Deine täglichen Texte” title to improve separation from the content above.",
    userHighlights: [
      "The daily section title now has more breathing room.",
      "Section hierarchy is easier to scan.",
      "No interaction or content behavior changed.",
    ],
    technicalNotes: [
      "Added top padding to the daily section container in `src/routes/app/reeprise/index.tsx`.",
      "No changes were made to daily card logic, feed rendering, or navigation.",
      "Desktop/mobile structure remains unchanged.",
    ],
  },
  {
    id: "app-top-bar-shadow-removed-for-flatter-header",
    timestamp: "2026-04-17T21:56:39+02:00",
    title: "Top bar shadow was removed",
    summary:
      "The app header/top bar no longer uses a drop shadow, resulting in a flatter and cleaner surface transition.",
    userHighlights: [
      "Top bar now appears without elevation shadow.",
      "Header spacing and controls remain unchanged.",
      "Navigation behavior is unchanged.",
    ],
    technicalNotes: [
      "Removed `shadow-lg` from the top bar container class in `src/routes/app/route.tsx`.",
      "No structural, interaction, or routing changes were made.",
      "Other shadow styles in unrelated components were not touched.",
    ],
  },
  {
    id: "reeprise-web-side-gutters-reduced-for-wider-content-fit",
    timestamp: "2026-04-17T21:54:47+02:00",
    title: "Reeprise web side spacing was reduced",
    summary:
      "Desktop Reeprise now uses smaller horizontal side gutters so the content area sits closer to the edges and feels less constrained.",
    userHighlights: [
      "Main Reeprise content now has less left/right outer spacing on web.",
      "The fixed generate composer keeps alignment with the reduced page gutters.",
      "No changes to content order, interactions, or card behavior.",
    ],
    technicalNotes: [
      "Reduced Reeprise route horizontal paddings in `src/routes/app/reeprise/index.tsx` (`lg:px-8` → `lg:px-4`, `xl:px-12` → `xl:px-6`).",
      "Adjusted desktop fixed composer wrapper paddings in the same file to match tighter side gutters.",
      "Kept existing 2-column desktop structure and fixed composer column alignment unchanged.",
    ],
  },
  {
    id: "reeprise-desktop-composer-now-aligns-to-first-content-column",
    timestamp: "2026-04-17T21:53:46+02:00",
    title: "Reeprise desktop composer now aligns to the first content column",
    summary:
      "The fixed desktop generate-text composer is now aligned to column 1 of the Reeprise two-column layout instead of appearing centered across the full page width.",
    userHighlights: [
      "The composer now sits exactly in the main content column.",
      "Sidebar spacing is preserved while composing.",
      "Composer behavior and interactions remain unchanged.",
    ],
    technicalNotes: [
      "Updated fixed composer wrapper in `src/routes/app/reeprise/index.tsx` to use `max-w-[1100px]` matching page width.",
      "Changed composer inner layout to the same desktop grid split (`2fr / 1fr`) and rendered `GenerateTextCard` in the first column.",
      "Kept existing fixed-bottom placement and pointer-events behavior unchanged.",
    ],
  },
  {
    id: "reeprise-desktop-sidebar-width-adjusted-to-one-third",
    timestamp: "2026-04-17T21:52:31+02:00",
    title: "Reeprise desktop sidebar now uses one-third width",
    summary:
      "The desktop Reeprise layout now uses a 2:1 split so the right utility column takes about one-third of the page width instead of a narrower rail.",
    userHighlights: [
      "The trainer and Quick Translate column is now wider and easier to use.",
      "Trainer remains first, with Quick Translate directly below in the same right column.",
      "Main feed/daily content remains in the left column.",
    ],
    technicalNotes: [
      "Changed desktop Reeprise grid split in `src/routes/app/reeprise/index.tsx` from `4fr/1fr` to `2fr/1fr`.",
      "Kept utility-card stacking order in the right column unchanged (VocabularyPreviewCard first, QuickTranslateCard second).",
      "Updated Playwright layout assertion in `e2e/reeprise-generation.spec.ts` to validate right-column share around one-third and vertical card stacking.",
    ],
  },
  {
    id: "reeprise-daily-and-feed-headlines-now-use-smaller-scale",
    timestamp: "2026-04-17T21:48:48+02:00",
    title: "Reeprise daily and feed headlines now use a smaller size",
    summary:
      "The “Deine täglichen Texte” and “Feed” section headlines were reduced significantly in size to keep the page hierarchy calmer.",
    userHighlights: [
      "Section headlines now appear much smaller and less dominant.",
      "Typography remains consistent with the existing serif heading style.",
      "Layout, spacing, and interaction behavior remain unchanged.",
    ],
    technicalNotes: [
      "Updated both Reeprise section `h2` classes in `src/routes/app/reeprise/index.tsx` from larger custom rem sizes to smaller values (`1.3rem` / `1.4rem` on `sm+`).",
      "Kept `font-serif`, `font-semibold`, and `leading-tight` unchanged to preserve style continuity.",
      "No data, card logic, or navigation code changed.",
    ],
  },
  {
    id: "generate-text-card-topic-input-now-auto-grows-with-content",
    timestamp: "2026-04-17T21:46:49+02:00",
    title: "Generate text card input now grows with longer topic text",
    summary:
      "The topic input in the generate-text card now expands vertically as text gets longer, so content is readable without being constrained to a fixed one-line field.",
    userHighlights: [
      "Long topic text now wraps and increases input height automatically.",
      "The generate card itself grows with the input instead of clipping.",
      "Typing, idea insertion, and submit behavior remain unchanged.",
    ],
    technicalNotes: [
      "Replaced the single-line topic input with an auto-resizing textarea in `src/components/GenerateTextCard.tsx`.",
      "Added a resize effect that recalculates textarea height from `scrollHeight` on topic-value updates.",
      "Extended desktop Reeprise Playwright regression in `e2e/reeprise-generation.spec.ts` to assert composer form height increases after entering a long topic.",
    ],
  },
  {
    id: "reeprise-utility-cards-now-use-matched-desktop-heights",
    timestamp: "2026-04-17T21:44:51+02:00",
    title: "Reeprise utility cards now use matched desktop heights",
    summary:
      "The top utility row cards now keep the same height on desktop so the vocabulary and quick-translate surfaces align cleanly.",
    userHighlights: [
      "The two top cards now end at the same baseline on desktop.",
      "Spacing between utility row and following sections is more consistent.",
      "No behavior changes in vocabulary review or quick translate.",
    ],
    technicalNotes: [
      "Set utility-row grid to explicit stretch in `src/routes/app/reeprise/index.tsx`.",
      "Updated root containers in `src/components/VocabularyPreviewCard.tsx` and `src/components/QuickTranslateCard.tsx` to `h-full` for consistent equal-height behavior.",
      "Added Playwright assertion in `e2e/reeprise-generation.spec.ts` to enforce height parity of the first two utility cards on desktop.",
    ],
  },
  {
    id: "reeprise-card-mini-preview-now-crops-at-bottom-edge",
    timestamp: "2026-04-17T21:42:50+02:00",
    title: "Reeprise mini preview now crops at the card bottom edge",
    summary:
      "The small tilted streamed-text preview now sits lower so it is intentionally clipped by the feed card bottom edge, matching the intended visual reference.",
    userHighlights: [
      "The mini preview now appears partially cut at the bottom of the card.",
      "The visual treatment now feels more intentional and less like a full floating card.",
      "Card interaction and content remain unchanged.",
    ],
    technicalNotes: [
      "Updated both mini-preview link overlays in `src/components/ReepriseTextCard.tsx` from top-based to negative bottom offsets.",
      "Changed transform origin for mini preview overlays from `top right` to `bottom right` to keep hover scaling anchored to the cropped edge.",
      "Kept existing dimensions, borders, and hover behavior while adjusting placement only.",
    ],
  },
  {
    id: "reeprise-section-headlines-now-use-serif-type-to-match-reading-surfaces",
    timestamp: "2026-04-17T21:41:46+02:00",
    title: "Reeprise section headlines now use serif typography",
    summary:
      "The “Deine täglichen Texte” and “Feed” section headlines now use a serif style and tuned sizing so they fit the overall Reeprise reading typography better.",
    userHighlights: [
      "Both major section headlines now feel more consistent with the card text style.",
      "Headline scale remains prominent but less disconnected from nearby typography.",
      "Layout and interactions remain unchanged.",
    ],
    technicalNotes: [
      "Updated both section headline classes in `src/routes/app/reeprise/index.tsx` to `font-serif` with explicit desktop/mobile size tuning.",
      "Adjusted line-height with `leading-tight` to maintain compact headline rhythm above card grids.",
      "No data-fetching, navigation, or card-behavior logic was changed.",
    ],
  },
  {
    id: "reeprise-desktop-compose-now-centers-to-main-content-column",
    timestamp: "2026-04-17T21:40:40+02:00",
    title: "Reeprise desktop composer now centers to the main content column",
    summary:
      "The fixed desktop “Thema für deinen Text eingeben” composer now aligns to the Reeprise main content column center instead of generic viewport center.",
    userHighlights: [
      "The desktop composer now sits visually centered with the main Reeprise column.",
      "Alignment remains stable when the side navigation switches between compact and expanded widths.",
      "Composer size and interaction behavior remain unchanged.",
    ],
    technicalNotes: [
      "Updated desktop composer positioning in `src/routes/app/reeprise/index.tsx` to use `left: calc((100vw + var(--app-desktop-nav-width, 72px)) / 2)` with `-translate-x-1/2`.",
      "Set `--app-desktop-nav-width` dynamically in `src/routes/app/route.tsx` based on side-nav state (`72px` compact, `440px` expanded).",
      "Kept the existing `max-w-[860px]` composer width and fixed-bottom behavior unchanged.",
    ],
  },
  {
    id: "reeprise-daily-primary-card-now-shows-pointer-cursor-on-hover",
    timestamp: "2026-04-17T21:38:12+02:00",
    title: "Reeprise daily primary card now shows pointer cursor on hover",
    summary:
      "The primary “Text des Tages” card now exposes a clear pointer cursor on desktop hover to match the interaction signal of other clickable cards.",
    userHighlights: [
      "The first daily card now clearly indicates clickability on hover.",
      "Pointer behavior now matches neighboring daily/history cards.",
      "Card click and keyboard interactions remain unchanged.",
    ],
    technicalNotes: [
      "Updated primary daily-card container classes in `src/routes/app/reeprise/index.tsx` to explicitly apply `hover:cursor-pointer` in interactive states.",
      "Added `data-testid=\"reeprise-daily-primary-card\"` in both create and done render branches for stable test targeting.",
      "Extended `e2e/reeprise-generation.spec.ts` desktop-layout test with a CSS cursor assertion on the primary daily card.",
    ],
  },
  {
    id: "reeprise-main-column-width-widened-with-matched-composer-adaptation",
    timestamp: "2026-04-17T21:34:56+02:00",
    title: "Reeprise main column was widened and composer width was adapted",
    summary:
      "The Reeprise content column was widened again for a roomier desktop reading surface, and the fixed composer container was resized to match.",
    userHighlights: [
      "Reeprise now feels less narrow on desktop.",
      "Cards gain more horizontal room without changing their interaction pattern.",
      "The fixed create/composer area remains aligned with the main content width.",
    ],
    technicalNotes: [
      "Updated Reeprise wrapper max width in `src/routes/app/reeprise/index.tsx` from `max-w-[700px]` to `max-w-[860px]`.",
      "Updated the fixed desktop composer wrapper from `max-w-[700px]` to `max-w-[860px]` for consistent alignment.",
      "No changes to feed/daily data logic or card click/navigation behavior.",
    ],
  },
  {
    id: "reeprise-primary-daily-card-now-uses-compact-strip-layout",
    timestamp: "2026-04-17T21:33:48+02:00",
    title: "Primary daily card now uses a compact strip layout",
    summary:
      "The first daily card in the desktop strip now uses a compact layout so it no longer grows much taller than neighboring cards.",
    userHighlights: [
      "The first daily card now aligns better with the height rhythm of adjacent cards.",
      "Long descriptive text no longer creates excessive vertical stretch in strip mode.",
      "Primary action remains visible and accessible.",
    ],
    technicalNotes: [
      "Changed strip-mode layout in `DailyPracticeCallout` (`src/routes/app/reeprise/index.tsx`) from two-column content/action to stacked compact flow.",
      "Hidden long body paragraphs for strip mode on both the primary daily card and done-state daily card to avoid oversized card height.",
      "Kept non-strip/mobile daily card layouts and behavior unchanged.",
    ],
  },
  {
    id: "reeprise-daily-cards-now-use-content-height-to-remove-empty-space",
    timestamp: "2026-04-17T21:30:40+02:00",
    title: "Reeprise daily cards now use content height to remove empty space",
    summary:
      "The daily card row no longer forces equal tall card heights, so cards now size to their content and avoid large empty white areas.",
    userHighlights: [
      "Daily cards now visually fit their text content more tightly.",
      "Large blank areas under short card text are removed.",
      "The daily row keeps the same order and interaction behavior.",
    ],
    technicalNotes: [
      "Updated the daily-strip grid in `src/routes/app/reeprise/index.tsx` to `items-start` so cards don’t stretch to the tallest sibling.",
      "Removed forced desktop `h-full/min-h` sizing from `DailyPracticeCallout` card container classes and switched to intrinsic `self-start` sizing.",
      "Applied `self-start` to desktop history cards to keep each item at natural content height.",
    ],
  },
  {
    id: "reeprise-daily-strip-now-uses-three-column-grid-to-fill-row",
    timestamp: "2026-04-17T21:28:44+02:00",
    title: "Reeprise daily strip now uses a 3-column grid to fill the row",
    summary:
      "The daily section now uses three desktop columns so the three visible daily cards fill the available row width without an empty fourth slot.",
    userHighlights: [
      "Daily cards now span the full row width when three are shown.",
      "The previous unused empty column is removed.",
      "Show-more behavior remains unchanged.",
    ],
    technicalNotes: [
      "Changed daily-strip layout in `src/routes/app/reeprise/index.tsx` from `lg:grid-cols-4` to `lg:grid-cols-3`.",
      "Adjusted layout regression expectation in `e2e/reeprise-generation.spec.ts` to match three-column desktop behavior.",
      "No data loading, card content, or interaction logic changed.",
    ],
  },
  {
    id: "reeprise-daily-strip-now-shows-three-cards-by-default",
    timestamp: "2026-04-17T21:27:40+02:00",
    title: "Reeprise daily strip now shows three cards by default",
    summary:
      "The daily-text strip now shows fewer cards at first load so the section stays tighter and easier to scan.",
    userHighlights: [
      "Only three daily cards are shown by default in the strip view.",
      "You can still expand to see more older cards.",
      "Main daily-card behavior remains unchanged.",
    ],
    technicalNotes: [
      "Reduced `desktopHistoryPreviewLimit` in `src/routes/app/reeprise/index.tsx` from `3` to `2` for older cards, resulting in three visible cards total with the primary daily card.",
      "Kept existing show-more/show-less toggle behavior and history metadata logic intact.",
      "No API/data contract changes were introduced.",
    ],
  },
  {
    id: "reeprise-main-column-width-now-significantly-narrower",
    timestamp: "2026-04-17T21:26:26+02:00",
    title: "Reeprise main column width is now significantly narrower",
    summary:
      "The Reeprise main content column was reduced to a much narrower maximum width so the desktop reading column feels more focused.",
    userHighlights: [
      "Desktop Reeprise now uses a tighter central reading column.",
      "Content lines are shorter and easier to scan.",
      "The fixed composer width was aligned to the same narrower column.",
    ],
    technicalNotes: [
      "Reduced the Reeprise page wrapper max width in `src/routes/app/reeprise/index.tsx` from `max-w-[1260px]` to `max-w-[700px]`.",
      "Aligned the desktop fixed composer container max width from `max-w-[920px]` to `max-w-[700px]`.",
      "No changes to feed/daily logic, card behavior, or data loading.",
    ],
  },
  {
    id: "reeprise-feed-now-uses-single-column-editorial-layout-with-desktop-mini-preview",
    timestamp: "2026-04-17T21:24:55+02:00",
    title:
      "Reeprise feed now uses a single-column editorial layout with desktop mini preview",
    summary:
      "The Reeprise feed now stacks cards vertically for stronger reading rhythm and improved typography, while keeping the mini streamed preview visible on desktop.",
    userHighlights: [
      "Feed cards are now placed one under another for easier long-form scanning.",
      "Typography and spacing were rebalanced for clearer hierarchy and reading rhythm.",
      "The mini preview card remains visible on desktop, not only on mobile.",
    ],
    technicalNotes: [
      "Changed feed container in `src/routes/app/reeprise/index.tsx` to a single-column grid and adjusted the load-more row span accordingly.",
      "Refined `src/components/ReepriseTextCard.tsx` proportions (title scale, body rhythm, spacing) and retained mini preview visibility at desktop breakpoints.",
      "Updated Playwright layout expectation in `e2e/reeprise-generation.spec.ts` so desktop feed grid asserts single-column behavior.",
    ],
  },
  {
    id: "reeprise-feed-cards-now-use-utility-editorial-neutral-tone",
    timestamp: "2026-04-17T21:17:48+02:00",
    title: "Reeprise feed cards now use a utility/editorial-neutral tone",
    summary:
      "Feed cards were shifted away from expressive gradients and decorative styling to a cleaner neutral editorial tone on web view.",
    userHighlights: [
      "Feed cards now use plain neutral surfaces instead of stylized gradient treatments.",
      "Podcast cards now match the same neutral card language as regular feed cards.",
      "Card readability and scan consistency on desktop are improved.",
    ],
    technicalNotes: [
      "Updated desktop feed card surfaces in `src/components/ReepriseTextCard.tsx` to neutral `bg-white` styles and removed decorative overlay accents.",
      "Normalized podcast-card supporting UI elements (audio block, transcript, muted labels) to slate-based neutral colors.",
      "Kept daily-strip cards and other non-feed sections unchanged.",
    ],
  },
  {
    id: "reeprise-desktop-daily-header-toggle-no-longer-triggers-hook-order-crash",
    timestamp: "2026-04-17T21:13:02+02:00",
    title: "Reeprise daily-header toggle no longer triggers a hook-order crash",
    summary:
      "A hook-order regression in the Reeprise overview was fixed so the page no longer falls into a runtime error while loading desktop daily-header controls.",
    userHighlights: [
      "The Reeprise page now loads reliably while daily-header controls initialize.",
      "The daily history toggle behavior remains unchanged after the fix.",
      "No visual workflow changes were introduced beyond stability.",
    ],
    technicalNotes: [
      "Moved the `showAllDailyHistoryDesktop` reset `useEffect` in `src/routes/app/reeprise/index.tsx` before the early loading return to keep hook order stable across renders.",
      "Kept existing daily-strip header-toggle layout and metadata wiring unchanged.",
      "Validated the same Reeprise layout regression flow in Playwright after the fix.",
    ],
  },
  {
    id: "reeprise-daily-history-toggle-now-sits-in-header-with-text-hover-state",
    timestamp: "2026-04-17T21:11:38+02:00",
    title:
      "Reeprise daily-history toggle now sits in the section header with text hover state",
    summary:
      "The daily-history toggle now aligns to the right side of the “Deine täglichen Texte” heading row and uses a text-link style with clear hover feedback.",
    userHighlights: [
      "The toggle now appears on the same height line as the daily section title.",
      "The control is right-aligned in the header area.",
      "Hover now provides a cleaner text-state cue with pointer behavior.",
    ],
    technicalNotes: [
      "Lifted desktop history-toggle state from `DailyPracticeCallout` into the parent `Reeprise` route to place the control in the section header row.",
      "Added metadata callback wiring from `DailyPracticeCallout` to parent (`hasMore`, `hiddenCount`) so the header control can render accurately.",
      "Removed the previous in-grid toggle row from the callout and applied text-link interaction classes in `src/routes/app/reeprise/index.tsx`.",
    ],
  },
  {
    id: "reeprise-feed-now-uses-four-column-desktop-grid-with-redesigned-web-cards",
    timestamp: "2026-04-17T21:09:31+02:00",
    title:
      "Reeprise feed now uses a 4-column desktop grid with redesigned web cards",
    summary:
      "On desktop, the Reeprise feed now renders in a four-column grid and cards were redesigned for a cleaner modern web reading surface while mobile card behavior stays intact.",
    userHighlights: [
      "Desktop feed cards now display in a denser 4-column layout for faster scanning.",
      "Desktop card visuals now use a cleaner editorial style with tighter typography and fewer decorative blocks.",
      "The mobile presentation keeps the previous card experience.",
    ],
    technicalNotes: [
      "Switched the feed container in `src/routes/app/reeprise/index.tsx` from flex-wrap to responsive grid (`md:2`, `lg:3`, `xl:4` columns).",
      "Refined `src/components/ReepriseTextCard.tsx` with desktop-only compact card spacing, hidden floating preview layers on `lg+`, and updated gradient/border styling for webview readability.",
      "Extended Playwright regression coverage in `e2e/reeprise-generation.spec.ts` to assert desktop/mobile column behavior for both daily strip and feed grid.",
    ],
  },
  {
    id: "app-sidenav-toggle-now-uses-cute-hamburger-button",
    timestamp: "2026-04-17T21:06:45+02:00",
    title: "Side navigation toggle now uses a playful hamburger button",
    summary:
      "The app now uses a clearer, friendlier side-navigation toggle icon that matches common web patterns and animates between closed and open states.",
    userHighlights: [
      "The top-left side-nav toggle now looks like a familiar hamburger button.",
      "When the side navigation opens, the icon smoothly morphs into a close shape.",
      "A small accent dot adds a playful touch while the menu is closed.",
    ],
    technicalNotes: [
      "Replaced the old top-bar command-line icon trigger in `src/routes/app/route.tsx` with an accessible `button` using a custom animated three-line glyph.",
      "Added `aria-label` binding via existing translation key `website_nav_menu_toggle` to improve accessibility and keep i18n consistency.",
      "Kept existing toggle behavior, compact desktop rail behavior, and mobile drawer interaction unchanged.",
    ],
  },
  {
    id: "reeprise-daily-strip-toggle-now-uses-text-only-style",
    timestamp: "2026-04-17T21:06:18+02:00",
    title: "Reeprise daily-strip toggle now uses a text-only style",
    summary:
      "The older-cards toggle in the Reeprise daily strip now appears as plain text without button chrome.",
    userHighlights: [
      "The toggle no longer has background fill.",
      "The toggle no longer has an outline/border.",
      "Behavior and wording stay unchanged.",
    ],
    technicalNotes: [
      "Updated the desktop daily-strip toggle class in `src/routes/app/reeprise/index.tsx` to a text-only style (`text-xs`, no border, no background).",
      "Kept the same click handler and expansion logic for older cards.",
      "No changes were made to data loading or navigation.",
    ],
  },
  {
    id: "reeprise-daily-strip-toggle-button-now-uses-compact-chip-style",
    timestamp: "2026-04-17T21:04:49+02:00",
    title: "Reeprise daily-strip toggle now uses a compact button style",
    summary:
      "The desktop daily-strip toggle for older cards now uses a smaller chip-style button so it feels less visually heavy.",
    userHighlights: [
      "The older-cards button is no longer full width.",
      "The control now appears as a compact rounded chip.",
      "Toggle behavior remains unchanged.",
    ],
    technicalNotes: [
      "Updated the desktop strip toggle container in `src/routes/app/reeprise/index.tsx` to a non-block flex row.",
      "Replaced the previous full-width `text-sm` button style with a compact `inline-flex`, `rounded-full`, `text-xs` style.",
      "No data, navigation, or history-toggle logic changed.",
    ],
  },
  {
    id: "desktop-sidenav-now-has-thin-outline-stroke",
    timestamp: "2026-04-17T21:03:00+02:00",
    title: "Desktop side navigation now has a thin outline stroke",
    summary:
      "The desktop side navigation now uses a subtle thin border so it is visually separated from the page background.",
    userHighlights: [
      "The side nav is easier to distinguish from the main background.",
      "The new outline stays minimal and does not change navigation behavior.",
      "Mobile navigation behavior remains unchanged.",
    ],
    technicalNotes: [
      "Updated the side navigation panel container in `src/routes/app/route.tsx` from right-edge-only border styling to a thin desktop border (`md:border md:border-slate-200`).",
      "Kept existing compact/expanded desktop widths and mobile drawer interaction unchanged.",
      "Extended the desktop sidenav Playwright regression in `e2e/reeprise-generation.spec.ts` to assert the panel border width is `1px`.",
    ],
  },
  {
    id: "reeprise-daily-strip-now-reaches-four-columns-earlier-and-expands-from-full-history",
    timestamp: "2026-04-17T21:02:26+02:00",
    title:
      "Reeprise daily strip now reaches four columns earlier and expands from full history",
    summary:
      "The daily strip now switches to four columns at a broader desktop breakpoint, and the older-cards toggle now expands from all generated history cards instead of only daily-nudge history.",
    userHighlights: [
      "Four compact cards now appear at more desktop widths.",
      "The older-cards toggle can reveal a broader set of generated cards.",
      "Daily strip card density is tighter with smaller card footprint and text sizing.",
    ],
    technicalNotes: [
      "Changed the daily-strip breakpoint in `src/routes/app/reeprise/index.tsx` from `xl:grid-cols-4` to `lg:grid-cols-4`.",
      "Updated desktop strip history source in `DailyPracticeCallout` to use `sortGeneratedTexts(generatedTexts)` while still excluding the featured card.",
      "Reduced strip card min-height/padding and adjusted desktop toggle row span to `lg:col-span-4`.",
    ],
  },
  {
    id: "desktop-sidenav-now-defaults-to-compact-icon-rail",
    timestamp: "2026-04-17T21:01:04+02:00",
    title: "Desktop side navigation now starts as a compact icon rail",
    summary:
      "On desktop, the app side navigation now stays visible in a compact icon-only mode by default and expands to the full panel when you toggle it.",
    userHighlights: [
      "Desktop side navigation now stays visible in a minimal icon layout even when collapsed.",
      "The top-left menu toggle now expands that icon rail into the full command and label panel.",
      "Mobile side navigation behavior remains unchanged.",
    ],
    technicalNotes: [
      "Updated `/app` shell layout in `src/routes/app/route.tsx` so desktop collapsed width uses a persistent compact rail (`md:w-[72px]`) instead of `md:w-0`.",
      "Kept desktop collapsed rail interactive by preserving pointer events and showing icon-only navigation labels with `aria-label`/`title` metadata.",
      "Added Playwright regression coverage in `e2e/reeprise-generation.spec.ts` to verify compact desktop default width, icon-rail navigation, and expansion on toggle.",
    ],
  },
  {
    id: "reeprise-daily-strip-now-shows-four-cards-with-expandable-older-history",
    timestamp: "2026-04-17T20:59:11+02:00",
    title:
      "Reeprise daily strip now shows four cards with expandable older history",
    summary:
      "The daily-text strip on desktop is now more compact so four cards fit across, and older daily cards can be expanded on demand.",
    userHighlights: [
      "Desktop daily cards are smaller, so the first row fits up to four cards.",
      "Older daily cards are now collapsed by default to keep the page cleaner.",
      "A dedicated button lets you show all older daily cards and collapse them again.",
    ],
    technicalNotes: [
      "Changed the desktop daily-strip grid in `src/routes/app/reeprise/index.tsx` to `xl:grid-cols-4` and reduced strip card footprint (`min-h` and padding).",
      "Added desktop-only history expansion state in `DailyPracticeCallout` with a preview limit and `show more/show less` toggle for older daily cards.",
      "Added translation keys in `src/assets/translations.json` and tightened Playwright desktop layout coverage in `e2e/reeprise-generation.spec.ts` to assert a four-column strip.",
    ],
  },
  {
    id: "reeprise-desktop-side-gutters-now-use-larger-horizontal-padding",
    timestamp: "2026-04-17T20:56:02+02:00",
    title: "Reeprise desktop side gutters now use more horizontal space",
    summary:
      "The Reeprise page now keeps a larger left and right gutter on bigger screens so the content breathes more and feels less edge-to-edge.",
    userHighlights: [
      "Desktop Reeprise now has visibly more side spacing.",
      "Mobile spacing stays compact.",
      "Layout structure and interaction flow stay unchanged.",
    ],
    technicalNotes: [
      "Updated the root Reeprise container padding in `src/routes/app/reeprise/index.tsx` with responsive horizontal classes (`sm:px-3 lg:px-8 xl:px-12`).",
      "Kept existing `max-w-[1260px]` and section composition unchanged.",
      "No data, navigation, or generation behavior was modified.",
    ],
  },
  {
    id: "reeprise-desktop-section-headlines-now-use-smaller-title-size",
    timestamp: "2026-04-17T20:55:01+02:00",
    title: "Reeprise desktop section headlines now use a smaller title size",
    summary:
      "The desktop headlines for the daily-text section and feed section on Reeprise were reduced to a more balanced size for better visual hierarchy.",
    userHighlights: [
      "“Deine täglichen Texte” now appears smaller and less dominant.",
      "“Feed” now matches the same reduced section-title scale.",
      "The page structure and interactions stay unchanged.",
    ],
    technicalNotes: [
      "Updated section heading classes in `src/routes/app/reeprise/index.tsx` from `text-[30px]` to `text-2xl` for both desktop section titles.",
      "No behavior, navigation, or data-loading logic changed.",
      "Kept existing desktop layout composition and test hooks intact.",
    ],
  },
  {
    id: "reeprise-desktop-layout-now-uses-top-utility-row-and-daily-strip",
    timestamp: "2026-04-15T17:50:34+02:00",
    title:
      "Reeprise desktop now follows a top-utility, daily-strip, and feed layout",
    summary:
      "The Reeprise overview on wide screens now uses a clearer desktop composition with utility cards on top, a dedicated daily-texts strip, and a separated feed section while mobile keeps the current flow.",
    userHighlights: [
      "On desktop, vocabulary preview and quick translate now sit side by side at the top for faster access.",
      "Daily texts are grouped into their own section before the main feed so you can scan today and previous days more quickly.",
      "The text creation card now stays docked at the bottom on desktop for faster repeated creation.",
    ],
    technicalNotes: [
      "Restructured `src/routes/app/reeprise/index.tsx` into desktop sections with explicit utility, daily, and feed containers plus a desktop-only docked generate card.",
      "Extended `DailyPracticeCallout` with a `layout` mode to support compact strip rendering without changing its existing generation and navigation logic.",
      "Added new translation keys in `src/assets/translations.json` and Playwright regression coverage in `e2e/reeprise-generation.spec.ts` for desktop/mobile Reeprise composition behavior.",
    ],
  },
  {
    id: "feed-headlines-now-use-situation-first-style-rules-by-text-type",
    timestamp: "2026-04-13T22:17:45+02:00",
    title:
      "Feed headlines now follow situation-first style rules across text types",
    summary:
      "Generated text headlines are now shaped as short real-life moments with social tension and curiosity, with tailored guidance per text type.",
    userHighlights: [
      "Headlines are less likely to read like generic topics or scenic descriptions.",
      "Story, dialogue, crime, knowledge, and podcast texts now each get type-specific headline guidance.",
      "English headlines are now constrained to concise, click-worthy moment titles with clearer interaction signals.",
    ],
    technicalNotes: [
      "Expanded backend title prompt builders in `reeprise/tasks.py` with shared situation-first headline rules plus per-text-type guidance blocks.",
      "Added backend headline-style validation for English titles (word-count bounds, topic-echo guard, social-tension checks) before persisting `GeneratedText.topic`.",
      "Added rewrite/fallback handling and regression tests in `reeprise/tests.py` to enforce style-safe title outcomes across generation paths.",
    ],
  },
  {
    id: "generated-text-streaming-restored-for-localhost-and-loopback-dev-hosts",
    timestamp: "2026-04-13T22:09:05+02:00",
    title:
      "Generated-text streaming is restored for localhost and loopback in local development",
    summary:
      "Generated-text live streaming now works again in local development when the app and backend are reached via either `localhost` or `127.0.0.1`.",
    userHighlights: [
      "Generated text details can receive live text chunks again while a text is being created.",
      "Local setups that mix `localhost` and `127.0.0.1` no longer lose streaming because of websocket origin mismatch.",
      "Generation completion behavior remains unchanged; this restores live-progress delivery.",
    ],
    technicalNotes: [
      "Replaced `AllowedHostsOriginValidator` usage for `/ws/streams/` with an explicit `OriginValidator` host list builder in `reeprise/routing.py`.",
      "Added `_allowed_ws_hosts()` to include loopback hosts (`localhost`, `127.0.0.1`, `[::1]`) automatically in `DEBUG` while still honoring configured `ALLOWED_HOSTS`.",
      "Added backend regression tests in `reeprise/tests.py` covering debug loopback allowlisting and non-debug configured-host behavior.",
    ],
  },
  {
    id: "tricky-word-card-now-uses-local-created-date-matching",
    timestamp: "2026-04-11T23:00:38+02:00",
    title: "Tricky-word card now matches generated texts by local created date",
    summary:
      "The tricky-word card now compares generated-text dates in local time, so it hides correctly after creating today’s text and reappears correctly when the local day changes.",
    userHighlights: [
      "After generating a tricky-word text, the card now hides more reliably on the same local day.",
      "Day rollover behavior now follows your local calendar date instead of UTC date slicing.",
      "Daily-callout date checks now follow the same local-date logic for consistent behavior.",
    ],
    technicalNotes: [
      "Added `getLocalIsoDateFromTimestamp()` in `src/utils/getLocalIsoDate.ts` to convert backend timestamps into local `YYYY-MM-DD` values.",
      "Updated tricky-word card visibility logic in `hasTodayStrugglePracticeText.ts` to use local timestamp conversion instead of `created_at.slice(0, 10)`.",
      "Updated daily-practice date checks in `reeprise/index.tsx` and added regression tests in `getLocalIsoDate.test.ts`.",
    ],
  },
  {
    id: "tricky-word-card-hide-detection-now-follows-struggle-origin-and-payload",
    timestamp: "2026-04-11T22:59:16+02:00",
    title:
      "Tricky-word card hide detection now follows struggle origin and payload",
    summary:
      "The tricky-word card now hides reliably after generating a tricky-word text, including cases where titles are normalized and would otherwise mask origin by topic text.",
    userHighlights: [
      "Creating a tricky-word text now hides the card the same day more reliably.",
      "Texts with custom/generated titles still count as tricky-word texts when they carry struggle-word payload.",
      "German generic tricky-word topic input is now recognized as tricky-word origin on create.",
    ],
    technicalNotes: [
      "Extended backend `_infer_generated_text_origin()` in `views.py` to classify German generic tricky-word topic input as `STRUGGLE_PRACTICE`.",
      "Updated frontend `isStrugglePracticeText()` to also treat texts with non-empty `struggle_words` payload as tricky-word texts.",
      "Added backend and frontend regression tests for German topic-origin inference and payload-based same-day hide detection.",
    ],
  },
  {
    id: "tricky-word-card-day-rollover-now-uses-local-date",
    timestamp: "2026-04-11T22:56:09+02:00",
    title: "Tricky-word card day rollover now uses local date",
    summary:
      "The tricky-word card now switches day-state based on local calendar date instead of UTC date, so it can reappear immediately at local midnight on the next day.",
    userHighlights: [
      "The tricky-word card can now reappear as soon as your local date changes.",
      "No delayed rollover because of UTC day boundaries.",
      "Daily card behavior keeps using the same visible day logic across the Reeprise page.",
    ],
    technicalNotes: [
      "Added `getLocalIsoDate()` utility in `src/utils` for consistent local `YYYY-MM-DD` date resolution.",
      "Replaced UTC-based `toISOString().slice(0, 10)` day checks in `reeprise/index.tsx` with local-date helper usage.",
      "Added unit tests for local date formatting and zero-padding behavior.",
    ],
  },
  {
    id: "tricky-word-card-now-hides-after-todays-practice-text-is-created",
    timestamp: "2026-04-11T22:51:24+02:00",
    title: "Tricky-word card now hides after today’s practice text is created",
    summary:
      "After creating today’s tricky-word practice text, the generate card now disappears and the new text remains available in the generated-text list. The card appears again on the next day.",
    userHighlights: [
      "The tricky-word generate card no longer stays visible after you already created a practice text today.",
      "Your generated practice text remains accessible in the regular generated-text list.",
      "The tricky-word card still reappears automatically on the next day.",
    ],
    technicalNotes: [
      "Added `hasTodayStrugglePracticeText()` utility to detect same-day `STRUGGLE_PRACTICE` text presence with language-space filtering.",
      "Updated `reeprise/index.tsx` to pass `showStruggleBanner={!hasTodayStruggleText}` into `GenerateTextCard`.",
      "Added Vitest coverage for date and language-space visibility cases in `hasTodayStrugglePracticeText.test.ts`.",
    ],
  },
  {
    id: "tricky-word-generation-no-longer-fails-hard-when-ai-misses-words",
    timestamp: "2026-04-11T22:07:04+02:00",
    title: "Tricky-word generation no longer fails hard when AI misses words",
    summary:
      "When tricky-word generation still misses required words after retries, the text now stays available by appending the missing vocabulary context instead of moving to a hard failed state.",
    userHighlights: [
      "The `text could not be loaded` failure screen is avoided for this tricky-word mismatch case.",
      "Required tricky words are still guaranteed to appear in the final practice text.",
      "Generation remains retry-first, but now has a reliable final fallback instead of aborting.",
    ],
    technicalNotes: [
      "Replaced the strict final `FAILED` branch in `complete_generated_text` with a fallback that appends missing struggle-word context lines after max retry attempts.",
      "Added `_append_missing_struggle_context()` in `tasks.py` to guarantee required-word presence while preserving already-generated content.",
      "Updated backend regression coverage to assert completion with required-word inclusion after max retries.",
    ],
  },
  {
    id: "generated-text-failed-screen-no-longer-triggers-hook-order-crash",
    timestamp: "2026-04-11T21:45:18+02:00",
    title: "Generated-text failed screen no longer triggers a hook-order crash",
    summary:
      "Opening a generated text that transitions into failed state no longer crashes with a React hook-order error.",
    userHighlights: [
      "The generated-text details page now stays stable when a text is marked failed.",
      "The failed-state screen now opens without the `Rendered fewer hooks than expected` crash.",
      "Refreshing from the failed-state screen remains available as before.",
    ],
    technicalNotes: [
      "Moved the `FAILED` early-return branch in `reeprise/$generatedTextId.tsx` to after all hooks in `GeneratedTextDetailsPage`.",
      "Kept the failed-screen UI and behavior unchanged while ensuring consistent hook invocation order across renders.",
      "Verified the runtime source via `.logs/ui.log` stack traces pointing to `GeneratedTextDetailsPage`.",
    ],
  },
  {
    id: "tricky-word-practice-now-enforces-required-vocabulary-coverage",
    timestamp: "2026-04-11T21:40:07+02:00",
    title: "Tricky-word practice now enforces required vocabulary coverage",
    summary:
      "Tricky-word practice texts now validate that all selected tricky words are present in the generated text. If words are missing, generation retries with stricter instructions instead of silently finishing with incomplete coverage.",
    userHighlights: [
      "Practice texts now reliably include the tricky words they were created for.",
      "If the first AI draft misses required words, the app now retries automatically.",
      "If required words still cannot be included after retries, generation fails clearly instead of showing a misleading practice text.",
    ],
    technicalNotes: [
      "Extended text-generation prompt building in `tasks.py` with retry-specific missing-word instructions for struggle practice.",
      "Added backend validation that checks generated body content for each required tricky word and triggers up to 3 generation attempts.",
      "Added regression tests for retry success and fail-after-max-attempt behavior, and updated existing fallback-vocabulary struggle test coverage.",
    ],
  },
  {
    id: "tricky-word-generated-vocabulary-now-highlights-like-clicked-words",
    timestamp: "2026-04-11T21:29:04+02:00",
    title: "Tricky-word generated vocabulary now highlights like clicked words",
    summary:
      "Words used to generate a tricky-word practice text are now highlighted with the same inline style as words you click to translate and save.",
    userHighlights: [
      "Generated tricky-word vocabulary now appears with the same blue inline emphasis as manually added word highlights.",
      "Highlighting is now more reliable for full words and avoids partial substring matches inside other words.",
      "Language filtering still ensures only vocabulary from the generated text language is highlighted.",
    ],
    technicalNotes: [
      "Added a shared `resolveGeneratedTextHighlightWords` utility to unify selected-word and tricky-word highlight resolution.",
      "Switched `GeneratedTextDetailsTextPane` to use the shared boundary-aware resolver instead of separate matching logic paths.",
      "Added Vitest coverage for combined highlight sources, language filtering, deduplication, and deterministic index ordering.",
    ],
  },
  {
    id: "tricky-word-practice-card-now-stays-available-with-broader-word-picks",
    timestamp: "2026-04-11T17:04:03+02:00",
    title: "Tricky-word practice stays available more reliably",
    summary:
      "The tricky-word practice card now stays available more consistently because practice-word selection no longer depends only on a very narrow low-streak review state.",
    userHighlights: [
      "The tricky-word practice card no longer disappears so easily after you have reviewed words a bit differently.",
      "Practice texts can now draw from a broader set of weaker or still-relevant words instead of only the strictest recent failure cases.",
      "After creating one practice text, the card remains available instead of being hidden for the rest of the day.",
    ],
    technicalNotes: [
      "Added a shared backend selector that ranks practice-word candidates by recent weak progress first, then falls back to lower-streak, older-learning, and unreviewed vocabulary in the same language.",
      "Switched both `/api/reeprise/vocabulary/struggle_words/` and struggle-practice text generation to the same selector so the banner and generated text stay aligned.",
      "Removed the frontend's 24-hour local hide gate for the tricky-word practice banner and added backend regression coverage for the new fallback behavior.",
    ],
  },
  {
    id: "desktop-side-navigation-now-keeps-its-scroll-container-when-open",
    timestamp: "2026-04-11T16:48:45+02:00",
    title: "Desktop side navigation now keeps scrolling reliably when open",
    summary:
      "The app sidebar now preserves its own scroll area more consistently on desktop, so long navigation lists remain usable instead of sometimes getting stuck.",
    userHighlights: [
      "Opening the side navigation on desktop now keeps the full menu scrollable more reliably.",
      "Long navigation lists should no longer depend on a fragile height calculation to reach lower entries.",
      "The sidebar still behaves the same visually, but its desktop interaction is more stable.",
    ],
    technicalNotes: [
      "Tightened the desktop `/app` shell layout in `route.tsx` so the side-navigation wrapper and panel both participate in an explicit `min-h-0` flex height chain.",
      "Added desktop overflow containment to the side-navigation shell to keep the sidebar's own `overflow-y-auto` region authoritative when the panel is opened.",
      "Extended Playwright coverage with a desktop regression that checks the side-navigation panel can still scroll after being opened.",
    ],
  },
  {
    id: "mobile-side-navigation-now-opens-with-correct-layering-and-scroll",
    timestamp: "2026-04-11T16:42:56+02:00",
    title: "Mobile side navigation now opens with correct layering and scroll",
    summary:
      "The app side navigation now opens more reliably on phones, with the menu panel staying above its backdrop and the mobile navigation list scrolling normally again.",
    userHighlights: [
      "The side menu no longer feels blocked by its dark backdrop on mobile.",
      "You can scroll through the full navigation list again on smaller screens.",
      "The app shell keeps the same overall navigation structure while behaving more predictably on phones.",
    ],
    technicalNotes: [
      "Reworked the `/app` shell in `route.tsx` so the mobile navigation uses a fixed layer with a valid z-index instead of the previous invalid `z-60` utility.",
      "Simplified the shell scroll behavior by removing the old nested `overflow-scroll` layout and giving both the side navigation and main content their own explicit vertical scrollers.",
      "Added Playwright coverage to verify the side navigation opens on a mobile viewport, can scroll, and still allows link navigation.",
    ],
  },
  {
    id: "mnemonic-prompt-now-prefers-strong-sound-hooks-over-overly-tidy-sentences",
    timestamp: "2026-04-11T16:24:00+02:00",
    title:
      "Mnemonic drafts now prefer stronger sound hooks and punchier scenes",
    summary:
      "Mnemonic generation now leans more toward memorable sound anchors and vivid scenes instead of overly tidy but forgettable sentences. The goal is to make hard words stick faster on the first encounter.",
    userHighlights: [
      "Generated mnemonic drafts can now be a bit more dramatic or unusual when that creates a stronger memory hook.",
      "The prompt now explicitly prefers a strong sound anchor over perfectly polished everyday phrasing.",
      "Weak examples that sounded neat but did not really help recall are now discouraged.",
    ],
    technicalNotes: [
      "Adjusted the mnemonic prompt in `reeprise/tasks.py` so stronger phonetic anchors and immediately sticky imagery outrank stylistic naturalness when both are in tension.",
      "Added a positive example for French `apprend` with an `abbrennt`-style hook and a negative example that rejects the weaker `Affe am Apfelbaum` pattern.",
      "Extended backend prompt tests to lock in the new emphasis on punchier recall-first mnemonic generation.",
    ],
  },
  {
    id: "mnemonic-drafts-now-use-stronger-context-and-clearer-memory-hooks",
    timestamp: "2026-04-11T16:15:28+02:00",
    title: "Mnemonic drafts now use stronger context and clearer memory hooks",
    summary:
      "Mnemonic generation now uses the saved vocabulary context more precisely and aims for clearer first-pass memory hooks instead of loose sound tricks. The editor also frames the AI output as a draft that learners should personalize.",
    userHighlights: [
      "Generated mnemonic drafts now stay closer to the actual meaning of the clicked word in its sentence.",
      "Weak formatting noise such as labels, markdown, or extra explanation text is stripped before a mnemonic is saved.",
      "The mnemonic card now makes it clearer that the best result comes from rewriting the draft in your own words.",
    ],
    technicalNotes: [
      "Rebuilt the mnemonic prompt in `reeprise/tasks.py` to include structured translation context such as dictionary form, translated sentence, context hints, and alternative meanings when available.",
      "Added server-side mnemonic cleanup so prefixed labels, markdown wrappers, and trailing extra lines are normalized before persistence.",
      "Updated backend tests for prompt construction and normalization, and refreshed the mnemonic card copy in `translations.json` to present generation as a draft rather than a final answer.",
    ],
  },
  {
    id: "product-updates-now-hide-technical-notes-from-non-staff-users",
    timestamp: "2026-04-11T16:15:16+02:00",
    title: "Product updates now focus on user-facing changes",
    summary:
      "The updates page now keeps technical implementation detail out of the default user view, so it is easier to understand what changed without reading internal engineering notes.",
    userHighlights: [
      "The main updates page now focuses on visible product changes and user impact.",
      "Technical implementation notes are only shown to staff users.",
      "The page language now reads more like an update archive and less like an internal logbook.",
    ],
    technicalNotes: [
      "Split the old mixed `product_logbook` view into a public `Product Updates` page plus a separate staff-only technical route backed by the same entry data.",
      "Removed technical notes from the public card layout, added a dedicated `/app/technical_change_log` route, and limited its navigation plus direct access to staff users.",
      "Extended Playwright coverage so the public route verifies that technical notes stay hidden from the normal user view while entry numbering still works.",
    ],
  },
  {
    id: "saved-word-preview-typography-now-has-a-clearer-hierarchy",
    timestamp: "2026-04-02T13:05:11+02:00",
    title: "Saved-word preview typography now has a clearer hierarchy",
    summary:
      "The saved-word flyout now makes the clicked word clearly primary, the translation clearly secondary, and the supporting labels visibly quieter. The typography reads more intentionally instead of giving similar weight to unlike information.",
    userHighlights: [
      "The clicked word now stands out first in the flyout instead of competing with the translation block.",
      "The translation still reads easily, but it no longer feels like it has the same priority as the saved word itself.",
      "Labels like `Translation` and `Base form` are quieter metadata now, not pseudo-headings.",
    ],
    technicalNotes: [
      "Adjusted the saved-word preview typography in `GeneratedTextDetailsTextPane.tsx` so the main word uses the strongest size and tone, while the translation is slightly reduced and softened.",
      "Lowered the visual weight of the section label and base-form label through smaller sizing and lighter color values.",
      "Kept the existing layout and interaction behavior unchanged; this pass focuses purely on text hierarchy and semantic emphasis.",
    ],
  },
  {
    id: "saved-word-preview-spacing-rhythm-now-uses-consistent-proportions",
    timestamp: "2026-04-02T13:01:14+02:00",
    title: "Saved-word preview spacing rhythm now uses consistent proportions",
    summary:
      "The saved-word flyout now uses a steadier spacing system, so the gaps between word, metadata, controls, and translation read as one coherent layout instead of a stack of mismatched offsets.",
    userHighlights: [
      "The flyout header now feels less jumpy because word, base-form line, and controls share a more even vertical rhythm.",
      "The divider and translation block sit closer to the content in a calmer proportion instead of leaving awkward empty pockets.",
      "Controls remain easy to reach, but the spacing now follows the reading content instead of arbitrary margins.",
    ],
    technicalNotes: [
      "Normalized the saved-word preview container padding and reduced mixed one-off offsets in `GeneratedTextDetailsTextPane.tsx`.",
      "Changed the header content block to a simple internal spacing rhythm and aligned the control cluster to the same vertical baseline.",
      "Tightened the divider and translation section spacing so the flyout uses one consistent gap system instead of separate `pt`/`mt` corrections.",
    ],
  },
  {
    id: "saved-word-preview-now-labels-the-base-form-explicitly",
    timestamp: "2026-04-02T12:58:04+02:00",
    title: "Saved-word preview now labels the base form explicitly",
    summary:
      "The secondary line in the saved-word flyout now clearly says when it is showing the base form, so inflected words are easier to understand at a glance.",
    userHighlights: [
      "The flyout no longer shows the smaller secondary form without context.",
      "If the seen word differs from its base form, the preview now labels it explicitly as `Base form`.",
      "The label stays visually quiet, so it clarifies the metadata without competing with the main word or translation.",
    ],
    technicalNotes: [
      "Added a dedicated base-form label in the generated-text saved-word flyout header in `GeneratedTextDetailsTextPane.tsx`.",
      "Added translation key `generated_text_word_preview_base_form_label` in English and German.",
      "Kept the base-form row conditional so it only appears when the saved surface form differs from the canonical form.",
    ],
  },
  {
    id: "saved-word-preview-typography-now-stays-compact-again",
    timestamp: "2026-04-02T12:55:34+02:00",
    title: "Saved-word preview typography now stays compact again",
    summary:
      "The saved-word flyout no longer uses oversized word and translation text. The preview keeps the same hierarchy, but the typography is back to a calmer, more proportional reading size.",
    userHighlights: [
      "Saved word and translation in the flyout no longer feel oversized compared with the surrounding reading view.",
      "The base-form line is quieter again and no longer competes with the main saved word.",
      "The flyout still reads clearly, but with less visual weight and better proportion.",
    ],
    technicalNotes: [
      "Reduced the saved-word headline and translation font sizes in `GeneratedTextDetailsTextPane.tsx` after the previous spacing pass pushed them too far.",
      "Scaled the secondary base-form line back down to restore a clearer primary-secondary hierarchy inside the flyout.",
      "Kept the revised spacing and control layout from the previous flyout cleanup while only correcting typography proportion.",
    ],
  },
  {
    id: "saved-word-preview-spacing-now-follows-content-priority-better",
    timestamp: "2026-04-02T12:48:26+02:00",
    title: "Saved-word preview spacing now follows content priority better",
    summary:
      "The saved-word flyout now uses cleaner proportions and whitespace so the word and translation lead the layout more naturally, while the controls stay compact and secondary.",
    userHighlights: [
      "The flyout header now gives the saved word more breathing room instead of crowding it with controls.",
      "The base form and translation sit in a calmer, more readable rhythm with the divider and outer padding.",
      "Close and audio controls still stay easy to reach, but they no longer dominate the preview.",
    ],
    technicalNotes: [
      "Adjusted saved-word preview outer padding, header gap, and divider spacing in `GeneratedTextDetailsTextPane.tsx` to create a more proportional content-first layout.",
      "Reduced the visual weight of the close control and tightened control grouping so the word block remains the primary anchor.",
      "Scaled the main word and translation typography with separate spacing so the flyout hierarchy follows reading function rather than equal visual treatment for every element.",
    ],
  },
  {
    id: "saved-word-preview-now-opens-on-press-instead-of-after-click-release",
    timestamp: "2026-04-02T12:45:12+02:00",
    title:
      "Saved-word preview now opens on press instead of after click release",
    summary:
      "The saved-word flyout now appears as soon as you press a highlighted word, instead of waiting for the full click to finish. The preview feels much more immediate while reading.",
    userHighlights: [
      "The translation flyout now shows up right on press for already saved highlighted words.",
      "Desktop hover preview still works, but click and tap feedback now feels noticeably faster too.",
      "The reading interaction no longer feels like it hesitates before opening the saved-word preview.",
    ],
    technicalNotes: [
      "Moved saved highlighted-word preview opening from the completed `click` path to previewable span pointer-down handling in the shared generated-text span renderer.",
      "Kept add-vocabulary behavior unchanged for non-saved words so first-time word taps still require the normal explicit add action.",
      "Updated Playwright coverage to assert the saved-word flyout appears on press and aligned the spec with the current simplified flyout content.",
    ],
  },
  {
    id: "generate-text-mockup-now-has-a-separate-reading-first-concept-page",
    timestamp: "2026-04-02T12:39:37+02:00",
    title: "Generate Text now has a separate reading-first concept page",
    summary:
      "A new in-app mockup page now demonstrates a reading-first future for Generate Text with a dominant `Surprise Me` path, lightweight topic steering, short story blocks, and a built-in continue loop.",
    userHighlights: [
      "There is now a dedicated `Generate Text Mockup` page in the side navigation.",
      "The mockup shows how the feature could start from a teaser and quickly move into short, curiosity-driven reading blocks.",
      "You can click through surprise, concretize, regenerate, continue, and tapped-word vocabulary states without touching the live generator flow.",
    ],
    technicalNotes: [
      "Added a new `/app/generate_text_mockup` route with local state for teaser rotation, on-demand concretization, mock generation, regeneration, continuation, and vocabulary inspection.",
      "Wired the page into the app navigation, added new translation keys for the mockup copy, and kept the implementation separate from the real `GenerateTextCard` flow.",
      "Added Playwright coverage to verify the mockup page is reachable from the side navigation and that the local reading flow can start and continue.",
    ],
  },
  {
    id: "saved-word-preview-flyout-now-keeps-the-word-primary",
    timestamp: "2026-04-02T12:39:45+02:00",
    title: "Saved-word preview flyout now keeps the word primary",
    summary:
      "The inline word-preview flyout now gives more space to the actual saved word and translation. The oversized close button and redundant saved-word label are gone, so the preview reads more cleanly.",
    userHighlights: [
      "The flyout no longer spends a full line on a `Gespeichertes Wort` heading before the actual word.",
      "The close action is now a compact icon instead of a large pill button that competed with the content.",
      "Saved word and translation stay easier to scan because the controls take up less space.",
    ],
    technicalNotes: [
      "Removed the redundant saved-word eyebrow from the generated-text preview flyout so the saved word itself remains the first visual anchor.",
      "Replaced the full-width close text button with a compact icon button while preserving the same translated close label via `aria-label` and `title`.",
      "Kept the existing pronunciation control and flyout behavior unchanged; this pass only tightens hierarchy and spacing inside the preview header.",
    ],
  },
  {
    id: "saved-highlighted-word-preview-now-also-opens-on-desktop-hover",
    timestamp: "2026-04-02T12:31:31+02:00",
    title: "Saved highlighted word preview now also opens on desktop hover",
    summary:
      "On desktop, saved highlighted words now reveal the same translation-and-audio flyout on hover, so quick meaning checks feel even lighter while reading. Tap behavior stays unchanged for mobile.",
    userHighlights: [
      "Desktop readers can now hover a saved highlighted word to see the translation preview without clicking first.",
      "The flyout stays reachable while moving the pointer from the word into the preview, so it does not disappear mid-hover.",
      "Phone and tablet tap behavior stays the same: the first tap still saves silently, and later taps open the preview on demand.",
    ],
    technicalNotes: [
      "Extended generated-text highlight span rendering with preview hover-start and hover-end hooks for previewable saved words only.",
      "Added fine-pointer hover detection plus a short delayed close in `GeneratedTextDetailsTextPane.tsx` so the flyout can be entered without flicker while preserving the existing click-based preview path.",
      "Updated Playwright coverage to verify the saved-word flyout opens on desktop hover in addition to the existing second-click behavior.",
    ],
  },
  {
    id: "saved-highlighted-words-now-open-an-inline-translation-preview",
    timestamp: "2026-04-02T12:20:53+02:00",
    title: "Saved highlighted words now open an inline translation preview",
    summary:
      "Reading stays interruption-free on the first click, but already saved highlighted words now open a compact inline flyout with the translation and pronunciation audio right where you are reading.",
    userHighlights: [
      "Clicking a new word still just saves it, so the translation does not interrupt the first reading pass.",
      "Clicking an already highlighted saved word now opens a small translation preview directly next to that word.",
      "The inline preview includes a pronunciation action, so you can hear the word without jumping to the side vocabulary list.",
    ],
    technicalNotes: [
      "Reworked generated-text body span rendering so saved highlighted words can open a preview flyout while unsaved words still go through the existing add-vocabulary action.",
      "Added local preview/highlight state in `GeneratedTextDetailsTextPane.tsx` so the second click works immediately without waiting for a vocabulary refetch, and reused the shared `VocabularyPronunciationButton` plus translation helpers.",
      "Added frontend regression coverage for previewable highlighted-word routing and a Playwright flow that checks add-first, preview-second behavior on a real generated text.",
    ],
  },
  {
    id: "read-aloud-mockup-now-reuses-the-existing-generator-and-text-details-ui",
    timestamp: "2026-04-02T11:56:45+02:00",
    title:
      "Read-aloud mockup now reuses the existing generator and text-details UI",
    summary:
      "The speaking mockup no longer presents read-aloud practice as a separate product surface. It now demonstrates how the feature can grow directly out of the current generator card and generated-text details page.",
    userHighlights: [
      "The mockup now shows the real `Generate Text` card as the entry surface instead of inventing a new start screen.",
      "The read-aloud flow is now shown inside a text-details-style layout rather than as a separate full redesign.",
      "The page makes it easier to judge whether speaking can feel native to the existing reading experience.",
    ],
    technicalNotes: [
      "Reworked the `/app/read_aloud_mockup` route to embed the real `GenerateTextCard` and a generated-text-details-style shell instead of a standalone concept layout.",
      "Added translation keys describing the integration direction so the mockup copy explains how read-aloud extends current product surfaces.",
      "Kept the side-navigation route and regression coverage while changing the page structure to align with existing UI patterns.",
    ],
  },
  {
    id: "read-aloud-speaking-flow-now-has-a-clickable-in-app-mockup-page",
    timestamp: "2026-04-02T11:46:06+02:00",
    title: "Read-aloud speaking flow now has a clickable in-app mockup page",
    summary:
      "A new mockup screen now lets the team preview a dedicated speaking-mode flow directly inside the app. The page can be opened from side navigation and walks through intro, recording, feedback, and completion states without needing backend speaking support yet.",
    userHighlights: [
      "There is now a dedicated `Read Aloud Mockup` page in the app navigation.",
      "The mockup demonstrates a focused one-sentence-at-a-time speaking mode instead of layering microphone controls onto the normal reading screen.",
      "You can click through the intended states for starting, recording, processing, feedback, and completion.",
    ],
    technicalNotes: [
      "Added a new `/app/read_aloud_mockup` route with local UI state to simulate the planned read-aloud interaction flow end to end.",
      "Wired the page into the side navigation and added translation keys for the new mockup labels and explanatory copy.",
      "Added Playwright coverage to verify the mockup page is reachable from the app navigation.",
    ],
  },
  {
    id: "product-logbook-numbering-now-stays-secondary-to-the-release-content",
    timestamp: "2026-04-02T11:24:26+02:00",
    title:
      "Product logbook numbering now stays secondary to the release content",
    summary:
      "The archive number now remains visible without competing with the actual release title, summary, and user impact. The page keeps the chronological reference system, but the content regains visual priority.",
    userHighlights: [
      "Release titles and summaries now lead the page more clearly than the archive number.",
      "The current release number is still easy to find, but it no longer dominates the header.",
      "Each note keeps a compact reference number for orientation without distracting from what changed.",
    ],
    technicalNotes: [
      "Reduced the visual weight of the current-release marker in the header so it behaves like secondary archive metadata rather than a hero KPI.",
      "Collapsed the card number rail into a compact inline metadata label with the timestamp, keeping numeric reference available while restoring content-first hierarchy.",
      "Added a frontend design rule to `AGENTS.md` requiring UI hierarchy to follow user task importance instead of the mere existence of a field.",
    ],
  },
  {
    id: "product-logbook-numbering-now-uses-an-archival-record-layout",
    timestamp: "2026-04-02T11:13:27+02:00",
    title: "Product logbook numbering now uses an archival record layout",
    summary:
      "The release-note number now acts as the organizing spine of the page instead of a loose badge, so the newest number is easier to read and every entry feels like part of the same archive.",
    userHighlights: [
      "The newest release number is now presented as a proper archive marker in the page header.",
      "Each note now has a dedicated number column that makes scanning the release sequence much easier.",
      "The layout now reads more like a chronological record and less like a stack of generic cards.",
    ],
    technicalNotes: [
      "Rebuilt the logbook header around a single current-release display with supporting archive context instead of secondary metadata chips.",
      "Refactored each release card into a responsive two-column record layout with a persistent number rail and timestamp metadata.",
      "Kept the numeric sequencing logic and Playwright regression coverage while updating the UI structure around it.",
    ],
  },
  {
    id: "product-logbook-numbering-ui-now-reads-like-a-real-logbook-system",
    timestamp: "2026-04-02T11:08:18+02:00",
    title:
      "Product logbook numbers now read like part of a real release archive",
    summary:
      "The logbook sequence is now presented as a named release-note system instead of a loose `Entry #...` label, so the newest number is easier to spot and each card feels like part of one coherent archive.",
    userHighlights: [
      "The page header now calls out the current release-note number more clearly.",
      "Each card now shows its number as a dedicated release-note badge instead of a floating label.",
      "The newest entry number should now feel intentional and easy to scan.",
    ],
    technicalNotes: [
      "Refined the product-logbook header to introduce the numbering as a named `Current Release Note` marker rather than a generic latest-entry text snippet.",
      "Reworked release-card metadata so each number appears in a dedicated badge with consistent wording and hierarchy next to the timestamp.",
      "Updated the Playwright regression to assert the new release-note copy instead of the earlier `Entry #...` phrasing.",
    ],
  },
  {
    id: "product-logbook-entry-numbers",
    timestamp: "2026-04-02T11:05:22+02:00",
    title: "Product logbook entries now show clear running numbers",
    summary:
      "The release log now shows a visible entry number for every card, so you can immediately tell which number the newest product update has.",
    userHighlights: [
      "Each product logbook card now shows a visible `Entry #...` label.",
      "The top of the page now states the latest release-note number.",
      "The existing human-readable title and internal slug ID still stay aligned with the same release item.",
    ],
    technicalNotes: [
      "Derived stable display numbers from the ordered `releaseEntries` list so newly prepended items increment the latest entry number without renumbering historical cards incorrectly.",
      "Updated the release-card header to show the numbered label separately from the timestamp while keeping the slug ID available as the React key.",
      "Added a Playwright regression that verifies the newest rendered product-logbook entry number matches the total number of entries on the page.",
    ],
  },
  {
    id: "russian-now-has-a-separate-cyrillic-learning-language-option",
    timestamp: "2026-03-22T21:53:20+01:00",
    title:
      "Russian now appears as its own learning language next to a separate Latin-transcription option",
    summary:
      "The language picker now distinguishes real Russian from Russian in Latin transcription. Existing transliterated Russian data keeps its previous behavior, while new Russian selections can use the normal Cyrillic script.",
    userHighlights: [
      "You can now choose real Russian as a learning language instead of only the transliterated variant.",
      "The existing transliterated Russian mode remains available as its own separate option.",
      "Older saved transliterated Russian content keeps the same meaning after the rollout.",
    ],
    technicalNotes: [
      "Added a dedicated `RUSSIAN_LATIN_TRANSCRIPTION` language code in frontend and backend registries while keeping `RUSSIAN` for actual Russian script.",
      "Moved Russian-specific transliteration and pronunciation hints onto the transcript variant so Cyrillic Russian is no longer normalized into Latin text by default.",
      "Added a data migration plus backend and Playwright regression coverage so historical transliterated Russian rows are relabeled safely and both Russian options remain selectable.",
    ],
  },
  {
    id: "russian-now-stays-in-latin-transliteration-across-titles-and-vocabulary",
    timestamp: "2026-03-22T21:36:06+01:00",
    title:
      "Russian now stays in Latin transliteration across titles, topics, and saved vocabulary",
    summary:
      "Russian generation and vocabulary storage now enforce Latin transliteration as a final backend normalization step, so Cyrillic no longer leaks into generated titles, topics, text bodies, or saved Russian word entries.",
    userHighlights: [
      "Russian text titles and topics now stay in Latin transliteration instead of sometimes showing Cyrillic.",
      "Generated Russian text bodies are normalized before saving, so selected vocabulary words come from transliterated text.",
      "Manually added Russian vocabulary entries now save in Latin transliteration as well.",
    ],
    technicalNotes: [
      "Added a centralized Russian Cyrillic-to-Latin normalization helper in `reeprise/tasks.py` and applied it to generated text bodies, committed titles, and translation-detail payloads.",
      "Updated Russian title-generation prompts to explicitly forbid Cyrillic in addition to the main text-generation prompt rules.",
      "Added backend regression coverage for Russian title/body normalization, Russian translation-detail normalization, and manual Russian vocabulary insertion.",
    ],
  },
  {
    id: "ai-quota-incidents-now-alert-ops-and-show-a-friendly-detail-screen",
    timestamp: "2026-03-22T08:51:39+01:00",
    title:
      "AI quota incidents now alert ops immediately and show a friendly text-failure screen",
    summary:
      "When AI credits run out, generated texts no longer fall back to fake offline content. The app now fails explicitly, sends an operator alert, and shows readers a calm recovery screen with a direct contact option.",
    userHighlights: [
      "A broken text no longer opens with confusing `Offline-Fallback` content.",
      "Readers now see a clear, friendly incident screen that explains we are already working on the issue.",
      "The detail page includes a direct contact option and an easy way back to the text list.",
    ],
    technicalNotes: [
      "Added quota-aware failure handling in `reeprise/tasks.py` so `insufficient_quota` errors mark generated texts as `FAILED`, stop retry loops for affected generation tasks, and trigger a cooldown-protected alert email.",
      "Exposed the configured support email through the account API and used it in the generated-text details route for a `mailto:` incident CTA.",
      "Updated frontend generated-text typing and polling so `FAILED` states stop refetching, then added a dedicated detail-page failure surface instead of rendering the normal reading layout.",
    ],
  },
  {
    id: "funny-smileys-added-to-the-website-footer",
    timestamp: "2026-03-21T21:38:38+01:00",
    title: "The website footer now has a row of funny smileys",
    summary:
      "The public website footer now includes a playful smiley strip so the landing page has a bit more character without changing navigation or core app behavior.",
    userHighlights: [
      "The landing page footer now shows a decorative row of funny smileys.",
      "The change is visible without logging in or opening any app screens.",
      "It adds personality without affecting navigation or feature flows.",
    ],
    technicalNotes: [
      "Added a decorative smiley line near the top of `Footer.tsx` so it appears across website pages that use the shared public footer.",
      "Kept the smileys as non-interactive footer content so the existing layout and links remain unchanged.",
      "Added a Playwright regression that checks the landing page footer renders the smiley strip.",
    ],
  },
  {
    id: "login-pricing-nav-and-quick-translate-now-use-translation-keys",
    timestamp: "2026-03-21T20:58:27+01:00",
    title:
      "Login, pricing, website navigation, and quick translate now follow the shared translation system",
    summary:
      "Several high-visibility screens now read their copy from the shared translation file instead of shipping hardcoded text, so English and German interface switching covers more of the product consistently.",
    userHighlights: [
      "The login flow now switches labels, actions, and reset-password messaging with the selected app language.",
      "Website navigation and the pricing page now follow the shared English and German translation setup.",
      "The quick translate page now uses translated labels for headings, prompts, and target-language actions.",
    ],
    technicalNotes: [
      "Wired `login.tsx`, `NavbarWebsite.tsx`, `pricing.tsx`, and `/app/quick_translate` to `useTranslation()`.",
      "Added variable substitution for the password-reset alert and a dedicated translation key for the quick-translate `Auto` label.",
      "Kept the legal and static-content pages out of this pass so the migration can continue incrementally without bundling a much larger copy rewrite.",
    ],
  },
  {
    id: "translation-keys-added-for-untranslated-ui-copy",
    timestamp: "2026-03-21T20:53:54+01:00",
    title:
      "Translation keys are now prepared for more untranslated app and website copy",
    summary:
      "The shared translation file now includes a new batch of English and German keys for login, pricing, website navigation, quick translate, and generic error messaging, so those surfaces can be moved off hardcoded copy safely.",
    userHighlights: [
      "Core screens now have a larger translation-key foundation behind them, including login and pricing copy.",
      "Common fallback messaging such as a generic error state now has a shared translation entry.",
      "This is groundwork for switching more hardcoded interface text to the app’s translation hook.",
    ],
    technicalNotes: [
      "Added a new `general_error` key that matches existing frontend usage in helper and quick-translate components.",
      "Added grouped translation entries for login, website navigation, pricing, and the `/app/quick_translate` route in both `GERMAN` and `ENGLISH`.",
      "Kept this pass focused on the shared translation dictionary so follow-up component wiring can reuse stable key names.",
    ],
  },
  {
    id: "daily-text-settings-no-longer-inherit-from-older-daily-texts",
    timestamp: "2026-03-21T20:49:54+01:00",
    title:
      "Daily texts now keep using your last regular generator settings instead of drifting from older daily texts",
    summary:
      "Creating a daily text no longer reuses a previous daily text as the settings seed for the next one. This prevents daily texts from recursively inheriting earlier daily text settings, which could make a language space drift away from your last regular generator setup.",
    userHighlights: [
      "Daily texts now follow your latest regular text settings more reliably.",
      "A previous daily text no longer becomes the hidden template for the next daily text.",
      "This specifically prevents language-specific drift in things like length, level, or text type.",
    ],
    technicalNotes: [
      "Changed daily-practice seed selection in `create_practice_text_for_user_language()` to exclude `DAILY_NUDGE` texts, not just struggle-practice texts.",
      "Kept the existing same-language-first fallback order, but now only across non-daily, non-struggle texts.",
      "Added a backend regression test proving that a new daily text inherits settings from the latest manual text instead of the latest earlier daily text.",
    ],
  },
  {
    id: "app-language-is-now-separate-from-explanation-language",
    timestamp: "2026-03-21T18:52:35+01:00",
    title: "App language is now separate from your explanation language",
    summary:
      "Changing the app interface language no longer changes the language used for word translations, grammar notes, and explanations. You can now control those two preferences separately in settings.",
    userHighlights: [
      "A new `App language` setting now controls menus, buttons, and interface copy only.",
      "A separate `Explanations and translations` setting now controls vocabulary translations, grammar notes, and helper explanations.",
      "Switching the interface to English no longer silently changes your learning support language.",
    ],
    technicalNotes: [
      "Added a dedicated `app_language` user setting and migrated existing users so their current interface language is preserved.",
      "Updated the translation hook, settings page, and command bar language shortcut to read and write `app_language` instead of `mother_tongue`.",
      "Added backend API regression coverage and a Playwright settings-flow test to ensure app-language changes no longer mutate mother-tongue translation behavior.",
    ],
  },
  {
    id: "word-highlights-now-use-the-weather-card-blue",
    timestamp: "2026-03-21T18:42:30+01:00",
    title: "Word highlights now use the same blue as the weather card",
    summary:
      "Highlighted words in texts and vocabulary cards now use the weather card’s sky-blue highlight color instead of the older green treatment.",
    userHighlights: [
      "Highlighted words now feel visually consistent with the blue weather-card style.",
      "The same blue treatment is used in generated text, titles, and vocabulary-card sentence previews.",
      "Highlight behavior stays the same; only the color system changed.",
    ],
    technicalNotes: [
      "Replaced the green highlight fill in generated-text body spans with a weather-card-inspired sky-blue gradient.",
      "Updated title-word highlight rendering in `GeneratedTextDetailsTextPane.tsx` to use the same blue treatment.",
      "Updated `highlightVocabularyWordInSentence.tsx` so card sentence previews share the same weather-card blue highlight style.",
    ],
  },
  {
    id: "continue-text-button-visible-without-saved-vocabulary",
    timestamp: "2026-03-21T18:39:43+01:00",
    title:
      "Continue text now stays visible even before you save any vocabulary",
    summary:
      "The continue action on generated-text details no longer disappears when a text has no saved vocabulary yet. You can now continue directly from the text context even on a fresh text where no words have been added.",
    userHighlights: [
      "Fresh texts without saved vocabulary now still show the `Continue text` action.",
      "You no longer need to save a word first just to keep reading forward.",
      "The selected-word continuation flow still works the same once vocabulary exists.",
    ],
    technicalNotes: [
      "Removed the generated-text details CTA visibility guard that depended on `vocabularies.length > 0`.",
      "Kept the adaptive label logic so the same button still switches between selected-word and context-only continuation states.",
      "Added a Playwright regression covering the no-saved-vocabulary state on generated-text details.",
    ],
  },
  {
    id: "topic-ideas-now-open-faster-from-the-generator",
    timestamp: "2026-03-21T18:31:32+01:00",
    title: "Topic ideas now appear faster after tapping the idea button",
    summary:
      "The topic-idea helper now returns queued suggestions before doing refill work, so tapping the idea button should feel much faster when ideas are already available.",
    userHighlights: [
      "The idea button should now respond faster in normal use.",
      "Ready suggestions are returned immediately instead of waiting on new background generation.",
      "The topic queue still refills, but no longer blocks the click.",
    ],
    technicalNotes: [
      "Changed backend topic-idea lookup to read existing pending suggestions before any top-up work.",
      "Moved suggestion-queue refill onto a Celery task so queue maintenance no longer sits on the request path.",
      "Switched queue top-up classification to bootstrap classification and added regression coverage for the fast existing-suggestion path.",
    ],
  },
  {
    id: "topic-ideas-now-stay-shorter-in-the-input",
    timestamp: "2026-03-21T18:13:33+01:00",
    title: "Topic ideas now stay shorter and fit the topic field more cleanly",
    summary:
      "The idea helper now fills the topic input with much shorter phrase-style suggestions instead of longer sentence-like prompts, so the generated topic is easier to scan and edit inside the field.",
    userHighlights: [
      "Suggested topics are now noticeably shorter.",
      "Ideas should fit the topic field more comfortably at a glance.",
      "The helper still keeps ideas specific, but now in a compact phrase style.",
    ],
    technicalNotes: [
      "Tightened backend topic-idea prompting to require compact 2-5 word ideas instead of sentence-length suggestions.",
      "Added backend validation so overly long AI ideas are rejected and replaced by shorter fallback topics.",
      "Updated fallback topic banks and added backend plus Playwright regression coverage for concise idea lengths.",
    ],
  },
  {
    id: "context-only-text-continuation-without-selected-vocabulary",
    timestamp: "2026-03-21T18:02:49+01:00",
    title: "You can now continue a text even when no vocabulary is selected",
    summary:
      "The continue action on generated-text details no longer depends on selected vocabulary. If words are selected, the next chapter uses them. If none are selected, the app now continues the text from its existing context instead of leaving the continuation path framed around vocabulary only.",
    userHighlights: [
      "The chapter button now adapts its label between `Continue with words` and `Continue text`.",
      "Clearing all selected vocabulary no longer leaves you stuck with a vocabulary-only continuation concept.",
      "Continuing without selected words now intentionally follows the current text context and story flow.",
    ],
    technicalNotes: [
      "Added a frontend label-state resolver for the generated-text continuation CTA and wired it into both desktop and mobile button surfaces.",
      "Updated chapter-generation prompt construction so empty selected-word lists explicitly trigger context-only continuation instructions instead of emitting an empty required-words prompt.",
      "Added regression coverage in Playwright for the checkbox-to-zero-selection CTA label switch and backend tests for empty generated-word chapter creation.",
    ],
  },
  {
    id: "daily-text-topics-now-respect-level-maturity",
    timestamp: "2026-03-21T18:00:17+01:00",
    title:
      "Daily texts now avoid beginner-style topic framing for higher levels",
    summary:
      "Daily generated texts now use stronger level-aware topic guardrails, so higher-level learners are less likely to receive childish or beginner-style titles such as a 'first English adventure'.",
    userHighlights: [
      "Daily texts now aim for adult, real-world situations instead of generic learner-journey framing.",
      "Higher-level users are less likely to see beginner-style daily titles.",
      "Your own accepted topics now guide daily inspiration more than earlier automatic daily titles.",
    ],
    technicalNotes: [
      "Updated daily-text guidance in `tasks.py` so topic framing must match both CEFR difficulty and scenario maturity.",
      "Changed daily inspiration lookup to prefer durable `TopicInterestSignal` topics and to exclude previous automatic daily titles from fallback history.",
      "Stopped pre-committing daily titles from the placeholder `daily learning text ...` topic and added backend regression tests for the new title flow.",
    ],
  },
  {
    id: "plural-vocabulary-cards-keep-their-number",
    timestamp: "2026-03-21T17:23:20+01:00",
    title:
      "Plural vocabulary cards now stay plural instead of being rewritten to singular",
    summary:
      "Vocabulary cards now keep plural nouns in their plural card form when the clicked word in the text is plural. The card still adds the correct article, but it no longer rewrites entries like `chiots` into `le chiot` while leaving the translation plural.",
    userHighlights: [
      "Plural noun cards now keep the plural source label, such as `les chiots`.",
      "Plural translations now stay plural too, such as `die Welpen`.",
      "Singular noun cards still use the matching singular article and singular translation.",
    ],
    technicalNotes: [
      "Updated the vocabulary translation prompt so plural noun clicks must preserve plural number on both the source card label and `translation_word`.",
      "Changed frontend vocabulary-word selection to prefer `source_plural_form` for noun entries whose grammar metadata says plural.",
      "Added backend and frontend regression tests for plural noun card reconstruction and singular noun fallback behavior.",
    ],
  },
  {
    id: "multi-word-vocabulary-selections-now-share-one-sentence-translation",
    timestamp: "2026-03-21T17:08:56+01:00",
    title:
      "Vocabulary cards from the same sentence now keep one shared sentence translation",
    summary:
      "When you save multiple words from the same sentence in one text, the resulting vocabulary cards now show the same translated sentence context instead of letting each card drift into its own sentence variant.",
    userHighlights: [
      "Saving several words from one sentence now keeps the translated sentence consistent across those cards.",
      "The shared translation is scoped to that sentence occurrence in that text, so the same raw sentence in another text can still have a different translation.",
      "Word-level meanings and grammar notes still stay specific to each selected vocabulary item.",
    ],
    technicalNotes: [
      "Added a dedicated shared sentence-translation model linked from vocabulary rows created out of generated-text selections.",
      "Reused the shared sentence translation by generated text, chapter, sentence bounds, source language, and target language instead of deduplicating globally by raw sentence text.",
      "Updated translation completion and serializer output so sibling vocabulary cards converge on the same translated sentence while preserving per-word `translation_details`.",
    ],
  },
  {
    id: "text-creation-no-longer-waits-on-topic-classification-or-daily-list-refresh",
    timestamp: "2026-03-20T06:22:07+01:00",
    title:
      "Text creation now returns immediately instead of waiting on extra classification and refresh work",
    summary:
      "Creating a text no longer waits for semantic topic classification inside the request itself, and the daily-text flow no longer waits for a list refresh before navigating. This reduces avoidable delay before the new text opens, especially on slower environments.",
    userHighlights: [
      "Manual text creation now prioritizes creating the text first and enriches topic intelligence afterwards.",
      "Daily text creation no longer pauses on a list refresh before opening the new text.",
      "This reduces extra waiting around the moment a new text starts.",
    ],
    technicalNotes: [
      "Changed topic-interest signal creation to use a fast bootstrap classification in-request and moved semantic upgrade to an async Celery task.",
      "Enqueued the topic-signal classification upgrade from `GeneratedTextViewSet.perform_create` after the text is already created.",
      "Removed the awaited list invalidation from the daily-card create flow so navigation can happen immediately after the create response.",
    ],
  },
  {
    id: "generate-flow-no-longer-blocked-by-auto-topic-prefetch",
    timestamp: "2026-03-19T22:47:54+01:00",
    title:
      "Opening the generate page no longer starts background topic-idea work that can delay text creation",
    summary:
      "The compact idea helper no longer auto-prefetches a topic as soon as the generate page opens. This avoids background helper work competing with the real create-text request on slower or more constrained environments, especially on mobile.",
    userHighlights: [
      "Generating a text is now prioritized over loading helper ideas in the background.",
      "The idea button still works, but only requests a topic once you actually tap it.",
      "This specifically protects the main text-creation flow from feeling delayed on mobile.",
    ],
    technicalNotes: [
      "Removed automatic topic-idea prefetching from `GenerateTextCard.tsx` and kept the helper request user-triggered.",
      "Added a Playwright regression that asserts the generate page does not call the topic-ideas endpoint until the idea button is clicked.",
      "This change is frontend-only and isolates the helper feature from the critical create-text request path.",
    ],
  },
  {
    id: "idea-button-return-flow-regression-test",
    timestamp: "2026-03-19T22:38:14+01:00",
    title:
      "Added a regression test for returning to the generator after opening a text",
    summary:
      "The generate-text flow now includes an end-to-end regression test that covers creating a text, opening its details page, returning to the main screen, and immediately using the compact idea helper again.",
    userHighlights: [
      "The return-to-generator flow is now explicitly protected by an automated browser test.",
      "This specifically guards against the idea button getting stuck in a loading state after navigation.",
      "The test exercises the real UI path instead of only backend suggestion logic.",
    ],
    technicalNotes: [
      "Added a Playwright regression in `reeprise-generation.spec.ts` for the details-page return flow and idea-button reusability.",
      "Updated repository guidance in `AGENTS.md` to require route-transition/state-transition regression coverage for user-facing changes when relevant.",
      "Executed the targeted Playwright scenario locally alongside the usual frontend checks.",
    ],
  },
  {
    id: "idea-button-no-longer-blocked-by-background-prefetch",
    timestamp: "2026-03-19T22:34:54+01:00",
    title:
      "The idea button no longer gets stuck in loading state after returning from a generated text",
    summary:
      "Returning from a generated text to the main page no longer makes the compact idea helper feel frozen while a background topic prefetch is still running. The card now keeps background idea loading separate from the button’s active click state.",
    userHighlights: [
      "The main generate-text card should feel responsive again after navigating back from a story.",
      "The `Idee` button no longer shows `Lädt` just because a silent background refill is happening.",
      "Background idea preloading still happens, but it no longer blocks the visible control.",
    ],
    technicalNotes: [
      "Split topic-idea background prefetching from the foreground button request state in `GenerateTextCard.tsx`.",
      "Kept the cached next-idea behavior while preventing the prefetch mutation from driving the button’s disabled/loading UI.",
      "No backend changes were needed for this fix.",
    ],
  },
  {
    id: "smart-topic-ideas-and-interest-map",
    timestamp: "2026-03-19T21:34:14+01:00",
    title:
      "Smart topic ideas now learn from your interests, and a new Interest Map shows your strong and weak areas",
    summary:
      "The small `Idee` helper on the generate-text card no longer depends on a fixed frontend list. It now pulls backend-generated topic ideas based on your accepted interests, and the new Interest Map page turns that same interest history into a visual landscape of strong areas, vocabulary depth, and blind spots.",
    userHighlights: [
      "The `Idee` button now serves personalized topic suggestions instead of reusing a tiny built-in pool.",
      "Accepted idea topics and your own manually chosen topics now shape future topic ideas together.",
      "The new Interest Map page shows which topic worlds you read a lot, where your vocabulary is deeper, and which adjacent areas are still underexplored.",
    ],
    technicalNotes: [
      "Added backend persistence for topic-interest signals and queued topic suggestions, plus a new `/generated_texts/topic-ideas/next/` endpoint.",
      "Updated generated-text creation so accepted idea topics are tracked separately from manual topics and can improve future recommendations.",
      "Added `/api/reeprise/interest-map/`, a new frontend route, and a first visualization layer that aggregates topic signals, text exposure, vocabulary usage, and recommendation areas.",
    ],
  },
  {
    id: "generate-text-topic-suggestions-for-blank-moments",
    timestamp: "2026-03-19T20:52:35+01:00",
    title: "Generate text now has a compact idea button for blank moments",
    summary:
      "The generate-text card now keeps topic help compact: a small inline idea button can instantly drop a random prompt into the field when you want something to read but do not want to think of a topic first.",
    userHighlights: [
      "You now get a tiny idea helper directly in the topic row instead of a large suggestion block.",
      "One tap fills the topic field with a random prompt immediately.",
      "The card stays visually light while still helping when your mind is blank.",
    ],
    technicalNotes: [
      "Added a small frontend topic-suggestion source and random-picker helper used by `GenerateTextCard.tsx`.",
      "Wired the inline helper to populate the existing topic field through the current form flow, without changing the backend API.",
      "Added translation keys for all new user-facing suggestion labels and a Vitest regression test for the random-picker helper.",
    ],
  },
  {
    id: "vocabulary-base-form-visible-without-more-grammar",
    timestamp: "2026-03-18T22:29:19+01:00",
    title: "Base forms are now visible without opening More grammar",
    summary:
      "Vocabulary cards now show a separate `Base form` field directly in the main card content instead of hiding it behind `More grammar`, so verb infinitives and other base forms stay visible without mixing them into the grammar toggle.",
    userHighlights: [
      "You can now see `Base form` immediately when it exists, without opening `More grammar`.",
      "The `More grammar` toggle is back to controlling grammar details only.",
      "This is especially useful for verb forms where the seen form and the infinitive are both important.",
    ],
    technicalNotes: [
      "Moved `Base form` rendering out of the expanded grammar sections in preview, trainer, progress, and generated-text vocabulary cards.",
      "Removed `Base form` from the generated-text card logic that decides whether the grammar toggle should appear.",
      "No backend changes were needed; this is a frontend rendering fix.",
    ],
  },
  {
    id: "vocabulary-verbs-keep-seen-form-as-main-word",
    timestamp: "2026-03-18T22:25:53+01:00",
    title:
      "Inflected verbs now stay as the main word while the base form gets its own field",
    summary:
      "Vocabulary cards no longer replace inflected non-nouns like `brille` with their dictionary form as the main label. Instead, the seen form stays primary and the base form is shown separately when available.",
    userHighlights: [
      "Verb forms you actually clicked in the text now stay visible as the main word on the card.",
      "Base forms like infinitives are still available, but in their own section instead of replacing the seen form.",
      "Noun cards keep their canonical article form behavior, so this change is specific to non-nouns.",
    ],
    technicalNotes: [
      "Changed frontend vocabulary-word selection so only noun-like entries promote the canonical dictionary form to the main label.",
      "Added a shared `base form` helper and surfaced it in preview, trainer, progress, and generated-text vocabulary cards.",
      "Added a regression test that keeps `brille` as the primary word while exposing `briller` as the separate base form.",
    ],
  },
  {
    id: "vocabulary-target-nouns-now-request-canonical-articles",
    timestamp: "2026-03-18T22:16:28+01:00",
    title:
      "New noun translations now request canonical articles on the translated side too",
    summary:
      "Freshly generated vocabulary entries now explicitly ask for canonical definite articles on translated noun labels as well, so cards should more consistently teach pairs like `le chat` and `die Katze` instead of dropping the translated article.",
    userHighlights: [
      "New noun cards should now more often include the article on both the source and translated side.",
      "Translated noun labels are now meant to stay in dictionary form instead of sentence-shaped forms.",
      "English targets still stay article-free, and non-nouns are unaffected.",
    ],
    technicalNotes: [
      "Extended the vocabulary translation prompt so `translation_word` must include the canonical definite article for nouns in article languages.",
      "Added prompt regression coverage for the new target-side article rule and for avoiding context-shaped translated articles.",
      "No migration or UI contract changes were required because the existing structured translation payload already supports article-bearing target labels.",
    ],
  },
  {
    id: "vocabulary-canonical-source-forms-with-articles",
    timestamp: "2026-03-18T21:53:30+01:00",
    title:
      "New vocabulary cards can separate canonical word forms from the exact text you clicked",
    summary:
      "Freshly generated vocabulary entries now store both the exact source form seen in the text and a reusable canonical source form, so noun cards can teach entries like `le chat` or `die Katze` while still preserving context-specific forms such as `chat` or `den Katzen` from the sentence.",
    userHighlights: [
      "New cards can now show the learnable base form with article for languages that teach nouns that way.",
      "When the clicked text is inflected or context-shaped, the card can still show the exact seen form separately.",
      "Plural and context notes can appear on the card without replacing the canonical base form.",
    ],
    technicalNotes: [
      "Added structured `translation_details` storage on `Vocabulary` for new entries, including source surface form, canonical dictionary form, lemma, plural form, context hint, target translation, and grammar fields.",
      "Changed translation generation to request strict JSON so source-language vocabulary labeling and target-language glossing are stored separately.",
      "Updated vocabulary preview, trainer, generated-text details, and progress views to prefer canonical source forms while keeping legacy entries on their old fallback path.",
    ],
  },
  {
    id: "word-translation-lexical-vs-context-separation",
    timestamp: "2026-03-18T21:43:18+01:00",
    title:
      "Word translations now ask the AI to keep dictionary meaning separate from sentence grammar",
    summary:
      "Vocabulary translation generation now explicitly asks for a reusable standalone word meaning on the first line and reserves context-shaped grammar for the translated sentence line, reducing cases where cards showed phrase fragments or expanded sentence-only renderings as if they were the word’s base meaning.",
    userHighlights: [
      "Word cards should more often show the actual learnable word meaning instead of a sentence-shaped phrase.",
      "Context-specific renderings such as case-marked or expanded phrases are now meant to stay in the translated sentence line.",
      "This should reduce confusing outputs like teaching a full contextual phrase when the saved item is only a single word.",
    ],
    technicalNotes: [
      "Rewrote the translation prompt contract to distinguish lexical word translation from sentence-level rendering explicitly.",
      "Added a concrete `matin` / `jeden Morgen` example inside the prompt so the model sees the expected separation directly.",
      "Added a backend regression test that asserts the prompt now forbids absorbing neighboring context words into the primary translation line.",
    ],
  },
  {
    id: "vocabulary-pronunciation-autoplay-resets-playhead",
    timestamp: "2026-03-18T20:54:13+01:00",
    title: "Vocabulary autoplay now restarts clips from the beginning reliably",
    summary:
      "When a newly generated pronunciation clip starts automatically, playback now resets to the beginning of the word first, which avoids the brief tail-end blip that could happen before immediate stop.",
    userHighlights: [
      "Automatically started pronunciation clips should now begin from the start of the word more reliably.",
      "The first auto-play after generation is less likely to produce only a tiny end-of-word sound.",
      "Manual replay still works as before and now also resets cleanly after an ended clip.",
    ],
    technicalNotes: [
      "Updated the shared pronunciation button to pause, reset `currentTime`, and reload the audio element before auto-play on a freshly available clip.",
      "Also reset the playhead before manual replay when the previous playback had already ended.",
      "This change is limited to frontend playback-state handling; no backend audio generation changes were required.",
    ],
  },
  {
    id: "vocabulary-pronunciation-pending-word-toast-removed",
    timestamp: "2026-03-18T20:51:14+01:00",
    title: "Pending pronunciation buttons no longer show a toast on tap",
    summary:
      "Pronunciation buttons for still-loading vocabulary no longer pop a toast when tapped; they now stay non-interactive until the word is ready while keeping their explanatory label.",
    userHighlights: [
      "Pending pronunciation buttons no longer interrupt the flow with a toast.",
      "The control still stays unavailable until the vocabulary item has finished loading.",
      "Ready vocabulary items keep the normal pronunciation generation and playback behavior.",
    ],
    technicalNotes: [
      "Removed the snackbar feedback from the shared pronunciation button’s pending-word branch.",
      "Restored the non-interactive pending state for vocabulary items whose own generation has not yet completed.",
      "Kept the existing accessibility label/title text that explains pronunciation becomes available once the word is ready.",
    ],
  },
  {
    id: "vocabulary-pronunciation-pending-word-toast-feedback",
    timestamp: "2026-03-18T20:48:58+01:00",
    title:
      "Pending vocabulary pronunciation buttons now explain why they cannot play yet",
    summary:
      "When a vocabulary item is still loading, tapping its pronunciation button now shows an immediate explanatory message instead of acting like a dead control.",
    userHighlights: [
      "Tapping pronunciation on a still-loading word now gives feedback instead of feeling broken.",
      "The actual pronunciation generation still waits until the vocabulary item is ready.",
      "Ready vocabulary items keep the normal spinner/play/pause behavior.",
    ],
    technicalNotes: [
      "Changed the shared pronunciation button so the pending-word state is clickable again.",
      "Instead of silently doing nothing, that state now shows a snackbar using the existing waiting-for-word translation key.",
      "The button remains truly disabled only while pronunciation generation itself is actively in progress.",
    ],
  },
  {
    id: "vocabulary-pronunciation-disabled-until-word-ready",
    timestamp: "2026-03-18T20:46:35+01:00",
    title:
      "Pronunciation buttons now wait for vocabulary generation to finish first",
    summary:
      "If a vocabulary item is still loading its translation/details, the pronunciation control now stays disabled instead of starting a pronunciation request too early and getting stuck in a spinner state.",
    userHighlights: [
      "You can only request pronunciation once the vocabulary item itself has finished loading.",
      "The button no longer enters a stuck loading state just because the word card was still pending.",
      "A tooltip/label now explains that pronunciation becomes available after the word is ready.",
    ],
    technicalNotes: [
      "Extended the shared pronunciation button to read the vocabulary item’s own `status` field in addition to pronunciation state.",
      "Blocked pronunciation requests while vocabulary status is not yet `COMPLETE`, unless an audio clip already exists.",
      "Added a dedicated translation key for the waiting-for-word state.",
    ],
  },
  {
    id: "vocabulary-pronunciation-button-now-shows-play-when-ready",
    timestamp: "2026-03-18T20:32:40+01:00",
    title:
      "Vocabulary pronunciation buttons now switch to a clear play icon when audio is ready",
    summary:
      "The pronunciation control now changes from its generation icon and loading spinner into a real play symbol as soon as the clip is available, making it clearer that the next tap starts playback.",
    userHighlights: [
      "Before audio exists, the button still looks like a pronunciation/generation control.",
      "While audio is being prepared, the button still shows the loading spinner.",
      "Once the clip is ready, the icon now becomes play, and while playing it becomes pause.",
    ],
    technicalNotes: [
      "Updated the shared `VocabularyPronunciationButton` state rendering without changing button layout or styling.",
      "Ready-but-not-playing state now renders a play icon instead of falling back to the generic pronunciation icon.",
      "Playing state now renders pause, while generation and loading behavior remain unchanged.",
    ],
  },
  {
    id: "vocabulary-pronunciation-now-uses-ash-voice",
    timestamp: "2026-03-18T20:27:46+01:00",
    title:
      "Vocabulary pronunciation now uses the same base voice as regular text audio",
    summary:
      "Vocabulary pronunciation clips now use the `ash` voice, matching the base voice used for normal single-voice text audio while still keeping language-specific pronunciation instructions.",
    userHighlights: [
      "Word pronunciation and regular text audio now share the same underlying voice choice.",
      "Non-English vocabulary still keeps native-language pronunciation guidance in the prompt.",
      "Podcast audio remains unchanged and still uses its separate multi-voice setup.",
    ],
    technicalNotes: [
      "Changed the vocabulary pronunciation voice profiles in `reeprise/tasks.py` so all supported languages now resolve to `ash`.",
      "Kept the per-language native-speaker instruction text intact so accent/pronunciation steering still happens through the TTS instructions.",
      "Updated the backend regression test to assert the new `ash` voice expectation for vocabulary pronunciation profiles.",
    ],
  },
  {
    id: "topic-previews-now-stay-expanded-up-to-twelve-words",
    timestamp: "2026-03-18T20:21:13+01:00",
    title: "Topic previews now stay fully visible up to twelve words",
    summary:
      "Topic and prompt previews on generated text cards and text details now remain untrimmed for substantially longer inputs, so medium-length prompts are easier to scan without opening the full prompt dialog.",
    userHighlights: [
      "Topic previews now stay fully visible until they exceed 12 words.",
      "Medium-length prompts should need the full-prompt action less often on both the card and the text details page.",
      "Very long prompts still collapse with an ellipsis and keep the full-prompt option.",
    ],
    technicalNotes: [
      "Raised the shared word-based topic-preview truncation threshold from 4 words to 12 words in `ReepriseTextCard.tsx`.",
      "Applied the same 12-word truncation limit in `GeneratedTextDetailsTextPane.tsx` so card and details behavior stay aligned.",
      "The full-prompt action remains conditional on actual truncation, so it only appears for prompts longer than the new threshold.",
    ],
  },
  {
    id: "first-person-main-character-inputs-now-treated-as-perspective",
    timestamp: "2026-03-18T20:14:02+01:00",
    title:
      "First-person main-character inputs are now treated as perspective, not as names",
    summary:
      "When users choose or type first-person inputs such as `Ich`, `I`, `moi`, or `yo`, generation now interprets that as a perspective signal instead of a literal character name, so texts and titles should stay in first person without inventing a character called `Ich` or `Moi`.",
    userHighlights: [
      "Choosing a first-person option should now lead to `I`/`je`/`ich`-style narration instead of a literally named protagonist.",
      "Typed first-person inputs are now handled the same way as select-based ones.",
      "Regular names like `Mia` or `Paul` still stay literal character names.",
    ],
    technicalNotes: [
      "Added backend normalization in `reeprise/tasks.py` that distinguishes between first-person perspective inputs and actual named characters before prompt construction.",
      "Applied the normalized main-character guidance consistently to title generation, body generation, topic guidance, and chapter continuation prompts.",
      "Added regression tests covering first-person pronoun handling and named-character preservation in the prompt layer.",
    ],
  },
  {
    id: "titles-now-commit-before-body-streaming",
    timestamp: "2026-03-18T19:53:59+01:00",
    title: "Generated text titles now appear earlier and stay fixed",
    summary:
      "Text generation now commits the final title before the body starts streaming, so users should see a stable title much earlier instead of waiting for enough streamed body content to infer one.",
    userHighlights: [
      "Generated texts should now reveal their title earlier during creation.",
      "Once the title appears, it should stay the same instead of changing later.",
      "The story body still streams in progressively, but title display no longer depends on parsing enough of the streamed text payload.",
    ],
    technicalNotes: [
      "Backend generation now calls a dedicated title-from-prompt step before the body stream begins and persists that committed title on the `GeneratedText` record while status is still pending.",
      "Body generation now runs in a body-only mode that is told to fit the already chosen title, instead of returning a fresh `title/story` payload for the main creation flow.",
      "Frontend pending-title display now switches from `Generating title…` to the committed title as soon as the polled `topic` value diverges from the cached raw prompt, and backend regression tests cover the committed-title flow.",
    ],
  },
  {
    id: "vocabulary-pronunciation-louder-normalized-audio",
    timestamp: "2026-03-18T19:42:19+01:00",
    title:
      "Vocabulary pronunciation clips are now saved at a louder listening level",
    summary:
      "Single-word pronunciation audio is now normalized to a noticeably louder output level before it is stored, so vocabulary clips should be easier to hear on phones and laptop speakers.",
    userHighlights: [
      "Vocabulary pronunciation clips should now sound louder without needing as much device-volume compensation.",
      "The change targets the saved audio itself, so it applies across the different vocabulary surfaces that reuse the same clip.",
      "Story and chapter audio behavior stay unchanged in this adjustment.",
    ],
    technicalNotes: [
      "Added a dedicated post-processing normalization step for vocabulary pronunciation audio in `reeprise/tasks.py`.",
      "The pronunciation pipeline now runs the generated clip through `ffmpeg` loudness normalization with a stronger target suited to short single-word audio.",
      "If normalization fails locally, the pipeline still falls back to saving the original generated clip.",
    ],
  },
  {
    id: "inline-generated-text-titles-arrive-with-first-ai-response",
    timestamp: "2026-03-17T21:38:13+01:00",
    title: "New text titles now arrive with the first AI response more often",
    summary:
      "Regular generated texts now ask the AI for both the title and the story in one structured response, which should make titles appear sooner instead of waiting for a separate fallback title-generation step after the full body finishes.",
    userHighlights: [
      "Story, dialogue, and knowledge texts should now show their generated title sooner in many cases.",
      "The app still keeps a fallback path if the AI returns only the story or malformed structured output.",
      "Podcast generation keeps the same structured title-and-story behavior it already used.",
    ],
    technicalNotes: [
      "Changed the shared generated-text output contract in `reeprise/tasks.py` so non-podcast texts now request JSON with `title` and `story`, matching the existing parser path.",
      "Kept `_extract_story_and_title_from_model_output(...)` and `_generate_title_from_story(...)` fallback logic so malformed or story-only responses still recover.",
      "Added backend regression tests covering the new inline-title fast path and the structured-output prompt contract for standard story generation.",
    ],
  },
  {
    id: "dev-startup-waits-for-redis-before-launching-services",
    timestamp: "2026-03-17T21:29:03+01:00",
    title:
      "Local startup now waits for Redis before launching the rest of the stack",
    summary:
      "The development launcher now starts Redis first and waits for a successful ping before bringing up Django, Celery, and the UI, which avoids local generated-text failures caused by backend services booting before Redis is ready.",
    userHighlights: [
      "After starting local development, text generation should no longer depend on restarting the stack a second time just because Redis was still booting.",
      "Generated-text, websocket, and background-task flows now have a more reliable local startup sequence.",
      "The Redis pane still starts first, but the rest of the stack now waits for a confirmed `PONG` before continuing.",
    ],
    technicalNotes: [
      "Changed `scripts/develop.sh` to launch the Redis Docker container before the other panes.",
      "Added a blocking readiness loop that polls `docker exec redis redis-cli ping` until Redis returns `PONG`.",
      "Only after Redis is confirmed ready does the script start Django, the Vite UI, Celery Beat, and the Celery Worker.",
    ],
  },
  {
    id: "struggle-word-text-create-removes-redundant-toast",
    timestamp: "2026-03-17T21:16:12+01:00",
    title:
      "Tricky-word text creation no longer shows a redundant start toast before redirect",
    summary:
      "When you generate a text from tricky words, the app now relies on the immediate redirect to the new text details page instead of also showing a separate start toast first.",
    userHighlights: [
      "Generating a tricky-word text now opens the new text directly without the extra `started` toast.",
      "The details page remains the place to follow generation progress after the redirect.",
      "Validation and failure feedback for the tricky-word flow still stay in place.",
    ],
    technicalNotes: [
      "Removed the success toast from the `handleGenerateStruggleText` path in `GenerateTextCard.tsx`.",
      "Kept the existing mutation, local-storage persistence, banner-hide behavior, and direct navigation to `/app/reeprise/$generatedTextId` unchanged.",
      "Scoped the change to the tricky-word creation flow only.",
    ],
  },
  {
    id: "generated-text-create-redirect-removes-redundant-toast",
    timestamp: "2026-03-17T20:42:30+01:00",
    title:
      "Text creation no longer shows a redundant `Generating Story` toast before redirect",
    summary:
      "Because the main generate-text flow now opens the new text details page immediately, the temporary `Generating Story` toast has been removed from that flow to avoid duplicate feedback during navigation.",
    userHighlights: [
      "Tapping generate text now redirects straight to the new text without showing the extra `Generating Story` toast first.",
      "The details page itself remains the main place to follow generation progress.",
      "Other creation feedback and error handling stay unchanged.",
    ],
    technicalNotes: [
      "Removed the immediate success-path toast from the active submit handler in `GenerateTextCard.tsx`.",
      "Kept the redirect-to-details behavior added to the main create flow, so the route transition now serves as the primary acknowledgement.",
      "Scoped the cleanup to the live Reeprise create card rather than unused legacy components.",
    ],
  },
  {
    id: "generated-text-create-redirect-to-details",
    timestamp: "2026-03-17T20:36:38+01:00",
    title: "Newly created texts now open their own details page immediately",
    summary:
      "Creating a text from the main Reeprise form now takes you straight to that text's details page, so you can follow generation progress and interact with the new text without staying on the overview screen.",
    userHighlights: [
      "After tapping generate text, the app now opens the new text directly instead of leaving you on the main list.",
      "The redirect also works while the text is still generating, so you can watch it progress from the details page.",
      "Daily practice and struggle-word creation keep their existing direct-to-details behavior.",
    ],
    technicalNotes: [
      "Updated the primary success path in `GenerateTextCard.tsx` to reuse the existing TanStack Router navigation pattern already used by other text-creation flows.",
      "Kept existing create-mutation cache priming and invalidation behavior so the details route can open immediately on the returned text ID.",
      "Preserved form-default persistence and draft cleanup before navigating to `/app/reeprise/$generatedTextId`.",
    ],
  },
  {
    id: "chapter-highlights-now-read-from-persisted-generation-words",
    timestamp: "2026-03-17T20:29:30+01:00",
    title:
      "Generated chapter highlights now come from chapter-owned selection data",
    summary:
      "Continued texts now keep their selected-word highlights by storing the generation word list on the chapter itself, so highlight behavior no longer depends on temporary page state surviving the navigation into the new chapter.",
    userHighlights: [
      "Words used to generate a continued text now stay highlighted even after reloading or reopening that chapter.",
      "The chapter no longer depends on leftover checkbox state from the previous page view to know which words were used.",
      "This makes the continuation flow more stable and predictable.",
    ],
    technicalNotes: [
      "Added persisted `generated_words` metadata to `Chapter` and returned it through the chapter serializer.",
      "Changed chapter creation and background generation to read the selected words from the saved chapter record instead of passing them only through transient client/task state.",
      "Updated generated-text details to highlight from `chapter.generated_words` and added a backend regression test covering persistence, enqueueing, and retrieval.",
    ],
  },
  {
    id: "generated-chapter-keeps-selected-word-highlights-after-navigation",
    timestamp: "2026-03-17T20:21:59+01:00",
    title: "Generated chapters now keep selected-word highlights after opening",
    summary:
      "When you generate a continued text from selected vocabulary, the new chapter now keeps those chosen words highlighted after the app navigates into the chapter instead of dropping the highlight state on arrival.",
    userHighlights: [
      "The words used to generate the new chapter stay visually highlighted when the chapter opens.",
      "The highlight survives the chapter navigation instead of disappearing immediately.",
      "This makes it easier to confirm which selected vocabulary was woven into the continuation.",
    ],
    technicalNotes: [
      "Stopped relying only on the live checkbox selection state for chapter highlights in `reeprise/$generatedTextId.tsx`.",
      "Added a one-shot pending chapter highlight payload that is attached to the freshly created chapter before navigation and consumed by the matching chapter view.",
      "Added a focused frontend unit test for the chapter highlight resolution rule.",
    ],
  },
  {
    id: "descriptive-topics-now-converted-to-target-language-concepts",
    timestamp: "2026-03-17T20:12:31+01:00",
    title:
      "Descriptive topic input should now be understood and rewritten in the learning language",
    summary:
      "Generated texts are now instructed to treat the topic field as semantic input first, so normal descriptive words like `Hundewiese` should be understood as a concept and expressed with the natural term in the target language instead of being echoed verbatim inside the generated text.",
    userHighlights: [
      "If you type a descriptive topic in another language, the generated text should now use the natural wording of that idea in the learning language.",
      "Raw topic words are less likely to leak directly into the text when they are not real names or brands.",
      "Names, brands, and other exact identifiers should still keep their original wording when that is the correct interpretation.",
    ],
    technicalNotes: [
      "Strengthened topic guidance in `reeprise/tasks.py` so the model must interpret topic input as meaning first rather than as text to preserve.",
      "Added an explicit rule that descriptive terms like `Hundewiese` should not reappear verbatim in the target-language text and should instead be replaced with the natural target-language concept.",
      "Extended backend prompt tests to cover this descriptive-topic translation behavior.",
    ],
  },
  {
    id: "title-selected-vocabulary-now-generates-chapters",
    timestamp: "2026-03-17T20:10:04+01:00",
    title: "Title-selected vocabulary is now included in chapter generation",
    summary:
      "Words added from a generated text title now count immediately when you create a new text from vocabulary, so chapter generation uses those title selections instead of silently ignoring them until the vocabulary list refresh catches up.",
    userHighlights: [
      "Words you add from the title now influence the next generated chapter right away.",
      "You no longer need to wait for the vocabulary sidebar to refresh before those title words are used.",
      "Body-word selection behavior stays the same, but chapter generation now includes both body and title selections reliably.",
    ],
    technicalNotes: [
      "Stopped deriving chapter-generation words only from the fetched vocabulary query in `reeprise/$generatedTextId.tsx`.",
      "Added an optimistic selected-word map so freshly created title vocabulary can be sent to `generate_chapter` before the list query refetch completes.",
      "Added a focused frontend unit test covering optimistic inclusion and deduplication of selected generation words.",
    ],
  },
  {
    id: "topic-input-normalized-instead-of-copied-literally",
    timestamp: "2026-03-17T20:07:45+01:00",
    title:
      "Topic input is now treated more as meaning guidance than literal wording",
    summary:
      "Generated texts are now instructed to follow the meaning of the topic field without blindly reusing unusual raw wording, so descriptive inputs like `HundeWiese` should be normalized into natural target-language phrasing while real names and brands keep their exact wording.",
    userHighlights: [
      "Oddly formatted topic input should no longer be copied into titles and text just because it was typed that way.",
      "Descriptive topic phrases are now meant to be rewritten into natural wording in the learning language.",
      "Proper names, brands, and similar exact identifiers are still supposed to stay unchanged when that is the correct reading.",
    ],
    technicalNotes: [
      "Extended generated-text topic guidance in `reeprise/tasks.py` to block literal reuse of camel case, fused compounds, and awkward raw topic wording unless the topic is clearly a proper noun, brand, or name.",
      "Changed the request wording inside the generation prompt from a direct topic request to raw meaning guidance, reducing pressure on the model to echo user input verbatim.",
      "Applied the same normalization rule to chapter continuation prompts and added backend regression tests for the `HundeWiese`-style case.",
    ],
  },
  {
    id: "vocabulary-translation-fragment-fallback",
    timestamp: "2026-03-16T21:27:41+01:00",
    title:
      "Vocabulary cards now avoid showing broken translation fragments as standalone meanings",
    summary:
      "When a vocabulary translation only yields a context-bound fragment like a trailing compound stem, the app now falls back to the sentence-level translation instead of presenting that fragment as if it were a complete standalone meaning.",
    userHighlights: [
      "Vocabulary cards no longer show obvious broken fragments like a trailing-hyphen translation as the main meaning.",
      "In those edge cases, the card now uses the fuller translated sentence so the visible translation stays understandable.",
      "The same fallback applies consistently across text details, trainer, and vocabulary progress views.",
    ],
    technicalNotes: [
      "Added a shared primary-translation resolver on top of normalized vocabulary translation details.",
      "Introduced a narrow fragment heuristic for obvious non-standalone translations such as leading or trailing hyphen forms.",
      "Updated vocabulary UIs to avoid duplicating the translated sentence when it is already used as the main translation label, and added a frontend regression test for the fragment fallback.",
    ],
  },
  {
    id: "pronunciation-button-self-polling-reliability",
    timestamp: "2026-03-16T21:18:34+01:00",
    title:
      "Pronunciation buttons now track audio readiness more reliably on their own",
    summary:
      "The pronunciation control now polls its own vocabulary entry after generation starts, which avoids stuck loading states and makes the ready-to-play transition more dependable across pages and mobile browsers.",
    userHighlights: [
      "Pronunciation requests are less likely to stay stuck in loading when the surrounding page cache is slow to refresh.",
      "The button can now detect when the clip becomes ready even if the parent page does not re-render immediately.",
      "Mobile pronunciation flow is more stable because the audio button no longer depends only on page-level query timing.",
    ],
    technicalNotes: [
      "Moved readiness tracking into the shared pronunciation button via local pronunciation status/audio state.",
      "Added lightweight polling of `/api/reeprise/vocabulary/<id>/` while pronunciation is pending and no audio URL is present.",
      "Kept parent-query invalidation in place, but no longer rely on it as the only source of readiness updates.",
    ],
  },
  {
    id: "pronunciation-autoplay-when-audio-becomes-ready",
    timestamp: "2026-03-16T21:03:21+01:00",
    title:
      "Pronunciation clips now try to start automatically once generation finishes",
    summary:
      "After you request a vocabulary pronunciation, the app now attempts to start playback automatically as soon as the generated audio file becomes available, reducing the need for a second tap when the browser allows it.",
    userHighlights: [
      "Requested pronunciation clips now try to begin playing automatically once loading finishes.",
      "If the browser allows delayed playback, the extra second tap is no longer needed.",
      "Browsers that block async autoplay still fall back to the existing manual play button.",
    ],
    technicalNotes: [
      "Restored best-effort autoplay in the shared pronunciation button by tracking an explicit `shouldAutoplayWhenReady` intent across the generation round-trip.",
      "The button now attempts `audio.play()` once a generated audio URL appears, then clears loading state whether playback succeeds or the browser blocks it.",
      "The fallback remains non-destructive: if autoplay is denied, the clip stays ready for manual replay.",
    ],
  },
  {
    id: "text-details-read-marker-text-scrub",
    timestamp: "2026-03-16T21:00:35+01:00",
    title: "Story audio now includes a draggable read marker in the text",
    summary:
      "The text details page now shows a small read marker above the currently estimated spoken word, so you can drag it through the story and jump playback closer to the section you want to hear.",
    userHighlights: [
      "During story playback, the currently read word now stays visually marked in the text.",
      "A small marker appears above that word and can be dragged through the story to reposition playback.",
      "Word tapping for vocabulary still works; the new marker is the dedicated audio-scrub control.",
    ],
    technicalNotes: [
      "Extended the generated-text details text renderer with a separate read-along state layered on top of existing vocabulary highlights.",
      "Mapped audio time to rendered word positions using the existing text-span DOM so playback can be repositioned from the story body without backend timestamp dependencies.",
      "Added a frontend regression test for word-boundary extraction and active read-along word marking.",
    ],
  },
  {
    id: "iphone-pronunciation-loading-state-stability",
    timestamp: "2026-03-16T20:54:28+01:00",
    title:
      "Pronunciation buttons now stay in loading state until audio is actually ready",
    summary:
      "Pronunciation generation now keeps its loading state visible until the audio file is available, which avoids a misleading quick spinner drop on slower devices and mobile browsers like iPhone Safari.",
    userHighlights: [
      "After requesting pronunciation, the button now keeps showing loading until the clip is really ready.",
      "Vocabulary cards on text details and other vocabulary views now refresh their pronunciation state more reliably.",
      "The button no longer assumes the clip can auto-play after background generation.",
    ],
    technicalNotes: [
      "Added local optimistic waiting state inside the shared pronunciation button so UI does not depend only on the short enqueue request lifetime.",
      "Cleared that waiting state only when pronunciation becomes `COMPLETE`/`FAILED` or an audio URL is present.",
      "Extended pronunciation mutation invalidation to include generated-text details queries in addition to vocabulary list queries.",
    ],
  },
  {
    id: "text-details-vocabulary-cards-pronunciation-button",
    timestamp: "2026-03-16T20:42:55+01:00",
    title: "Text details vocabulary cards now include pronunciation replay",
    summary:
      "The pronunciation button is now also available on vocabulary cards inside the text details page, so saved words can be replayed there without leaving the reading flow.",
    userHighlights: [
      "Vocabulary cards on text details now show the same pronunciation button as the main vocabulary views.",
      "You can generate and replay a word’s pronunciation directly from the text details sidebar/cards.",
      "Using the pronunciation button does not trigger the card’s jump/select behavior.",
    ],
    technicalNotes: [
      "Reused the shared `VocabularyPronunciationButton` inside the generated-text details vocabulary card header.",
      "Wrapped the pronunciation control in the existing `data-vocabulary-control` guard so card-level click handlers ignore audio interactions.",
      "No backend changes were required for this follow-up; it uses the same pronunciation API added previously.",
    ],
  },
  {
    id: "vocabulary-on-demand-pronunciation-audio",
    timestamp: "2026-03-16T20:27:24+01:00",
    title: "Saved vocabulary can now generate pronunciation audio on demand",
    summary:
      "Added words now show a small audio button next to the vocabulary item so you can request a pronunciation clip when needed and replay it once it is ready.",
    userHighlights: [
      "Saved vocabulary entries now include an audio icon directly next to the word.",
      "The first tap generates a pronunciation clip for that word on demand.",
      "Once the clip is ready, the same button plays and pauses the pronunciation.",
    ],
    technicalNotes: [
      "Extended `Vocabulary` with stored pronunciation audio and a dedicated pronunciation generation status.",
      "Added authenticated vocabulary speech and pronunciation-generation endpoints plus a background task that synthesizes standalone word audio.",
      "Updated vocabulary list polling and shared vocabulary UI rows so the button can reflect pending, ready, and playback states.",
    ],
  },
  {
    id: "translation-prompt-close-but-idiomatic-alignment",
    timestamp: "2026-03-16T20:08:25+01:00",
    title:
      "Sentence translations now stay closely mappable without becoming literal-sounding",
    summary:
      "Translations are now instructed to stay closely aligned with the original sentence so learners can map words, grammar, and meaning across both versions more reliably, while still avoiding literal target-language phrasing that would sound wrong or teach the wrong instinct.",
    userHighlights: [
      "Translations should now be easier to compare with the source sentence word by word.",
      "Sentence structure and information order are meant to stay close enough that you can check which part matches which part.",
      "If a literal rendering would sound wrong in the target language, the translator is now told to make only the smallest necessary adjustment instead of teaching an unnatural phrase.",
    ],
    technicalNotes: [
      "Refactored translation prompt construction in `reeprise/tasks.py` into a dedicated helper so prompt policy can be tested directly.",
      "Added an explicit learner-mapping rule: preserve meaning, structure, specificity, and sentence order where the target language allows it, so users can map source words and grammar onto the translation more confidently.",
      "Added a counter-rule that blocks false friends, literal calques, and wrong-sounding target-language phrasing, so closeness does not come at the cost of correctness.",
      "Added backend regression tests covering this close-but-idiomatic translation contract and the deterministic sentence-translation guidance.",
    ],
  },
  {
    id: "generated-text-prompt-quality-rewrite",
    timestamp: "2026-03-16T19:21:06+01:00",
    title:
      "Generated learning texts now aim for more natural and native-sounding phrasing",
    summary:
      "The AI text-generation prompt was rewritten to reduce awkward translated-sounding sentences, relax overly rigid wording constraints, and favor clearer idiomatic learner content across stories, dialogues, podcasts, and daily practice texts.",
    userHighlights: [
      "Generated texts should now read more naturally instead of sounding forced or overly translated.",
      "Topics are still followed, but the AI is less likely to insert topic words in unnatural ways just to satisfy the prompt.",
      "Length guidance is now more flexible, which should improve rhythm and sentence flow.",
    ],
    technicalNotes: [
      "Refactored backend prompt construction in `reeprise/tasks.py` into modular prompt-builder helpers instead of one large mixed instruction block.",
      "Removed contradictory topic instructions, added explicit native-sounding quality rules, and changed rigid exact word-count enforcement into an acceptable target range.",
      "Added backend regression tests to cover the new prompt policy, podcast JSON output contract, and struggle-word prompt handling.",
    ],
  },
  {
    id: "generated-text-page-loop-fixes-and-e2e-smoke-pass",
    timestamp: "2026-03-15T09:15:13+01:00",
    title:
      "Fixed reeprise page render loops and verified generated-text creation end to end",
    summary:
      "The reeprise home page and generated-text details page no longer fall into React update-depth loops during text creation, and the create-text flow is now verified with a passing Playwright smoke test.",
    userHighlights: [
      "The main text page no longer gets stuck in a render loop before submission.",
      "Opening a generated text detail page no longer retriggers the old vocabulary-selection loop.",
      "The critical flow from creating a text to opening its completed details page now passes as an automated smoke check.",
    ],
    technicalNotes: [
      "Made `GenerateTextCard` form default hydration idempotent by hydrating once per defaults snapshot instead of reapplying values on every render.",
      "Guarded the empty-state reset logic in `VocabularyPreviewCard` so it does not keep resetting already-empty state on every render when no vocabulary is available.",
      "Guarded `selectedVocabularyIds` synchronization in `$generatedTextId.tsx` and tightened the new Playwright smoke test to seed valid create-form defaults and submit the correct form.",
    ],
  },
  {
    id: "generated-text-smoke-test-and-critical-flow-policy",
    timestamp: "2026-03-15T08:51:36+01:00",
    title:
      "Generated text creation now has a dedicated smoke test and repo policy",
    summary:
      "The repo now treats generated-text creation as a critical flow that must be verified with an executed regression test, helping catch broken background generation earlier during development.",
    userHighlights: [
      "The most important text-generation flow now has automated smoke coverage.",
      "Development checks are now expected to verify real generation behavior, not just builds.",
      "Broken worker or Redis setups should be caught earlier before UI changes are considered done.",
    ],
    technicalNotes: [
      "Added a Playwright smoke test that logs in, creates a generated text, polls the generated-text API until completion, and opens the details page.",
      "Updated `AGENTS.md` with a critical-flow testing policy for generated-text creation and a required first-pass check of Django, Celery, Beat, Redis, and text status when generation is stuck.",
      "This change is intended to catch failures like `PENDING` texts caused by unavailable background workers.",
    ],
  },
  {
    id: "toast-duration-standardized-two-seconds",
    timestamp: "2026-03-15T08:06:41+01:00",
    title: "Standardized toast timing to a shorter transient duration",
    summary:
      "The app toast now uses a standard short success duration again, so brief confirmations disappear faster and feel closer to common UX patterns.",
    userHighlights: [
      "Short confirmations like `Word added` now fade out faster.",
      "The toast still remains visible long enough to read at a glance.",
      "This change only affects timing, not toast styling or placement.",
    ],
    technicalNotes: [
      "Reduced the `SnackBar.tsx` auto-close timer from 2600ms to 2000ms.",
      "This keeps the toast aligned with common transient success-toast timing.",
    ],
  },
  {
    id: "agents-german-umlaut-copy-rule",
    timestamp: "2026-03-15T08:03:53+01:00",
    title: "Added a repo rule to always use real German umlauts in copy",
    summary:
      "The repository instructions now explicitly require real German characters in user-facing text, so copy should use `ü`, `ö`, `ä`, and `ß` whenever the correct spelling needs them.",
    userHighlights: [
      "German UI copy should now consistently use proper spellings like `hinzugefügt`.",
      "ASCII fallback forms like `ue`, `oe`, `ae`, or `ss` are now explicitly discouraged when the proper character is required.",
      "This is an instruction change for future edits rather than a product behavior change.",
    ],
    technicalNotes: [
      "Updated `AGENTS.md` with a dedicated German copy rule for umlauts and `ß`.",
      "The rule applies to future user-facing text changes across the repo.",
    ],
  },
  {
    id: "word-added-toast-german-umlaut-fix",
    timestamp: "2026-03-15T08:02:42+01:00",
    title: "Corrected the German `Word added` toast wording",
    summary:
      "The German vocabulary-success toast now uses the proper umlaut spelling in `Wort hinzugefügt` instead of the ASCII fallback.",
    userHighlights: [
      "German toast copy now reads `Wort hinzugefügt`.",
      "The English toast stays `Word added`.",
      "This only changes wording, not toast behavior.",
    ],
    technicalNotes: [
      "Updated the `word_added_toast` German translation value in `translations.json`.",
      "No component logic or toast timing changed in this adjustment.",
    ],
  },
  {
    id: "generated-text-title-highlight-no-outline-ring",
    timestamp: "2026-03-15T08:00:03+01:00",
    title:
      "Title-word highlights now use the same fill-only style as body text",
    summary:
      "Highlighted vocabulary in generated text titles now matches the main reading text exactly, with no extra outline ring added on headline clicks.",
    userHighlights: [
      "Headline words now highlight with the same simple green fill as words in the story body.",
      "Clicking a title word no longer adds an extra outline stroke around that word.",
      "The title and body now feel visually identical when showing saved vocabulary.",
    ],
    technicalNotes: [
      "Removed the title-only flash ring state from `GeneratedTextDetailsTextPane`.",
      "Title words now keep only the persistent fill highlight, matching the saved-vocabulary treatment used in the main text.",
      "No backend changes.",
    ],
  },
  {
    id: "generated-text-title-click-no-first-body-word-highlight",
    timestamp: "2026-03-15T07:48:55+01:00",
    title: "Title clicks no longer highlight the first word in the body text",
    summary:
      "Adding vocabulary from a generated text headline no longer leaks into positional body highlights, so clicking any headline word keeps the effect scoped to the headline.",
    userHighlights: [
      "Clicking the first word in a title no longer highlights the first word of the story.",
      "Headline vocabulary still gets added correctly and keeps its headline highlight.",
      "Body highlights now only come from words that were actually added from the body text.",
    ],
    technicalNotes: [
      "Filtered title-origin vocabulary entries out of `usages`-based body highlight positions in `GeneratedTextDetailsTextPane`.",
      "Used the resolved title sentence as the boundary for deciding whether a saved vocabulary belongs to the headline or the reading body.",
      "Applied the same exclusion in both chapter and non-chapter text rendering paths.",
    ],
  },
  {
    id: "generated-text-title-highlights-persist-after-refresh",
    timestamp: "2026-03-15T07:47:47+01:00",
    title: "Title-word highlights now persist after page refresh",
    summary:
      "Words added from a generated text title now stay highlighted after reloading the page, matching the persistence users already expect from saved vocabulary inside the body text.",
    userHighlights: [
      "Words you added from the title stay visibly highlighted after a refresh.",
      "The title keeps the same saved-vocabulary visual state as the text body.",
      "Refreshing no longer makes title-added words look as if they were never added.",
    ],
    technicalNotes: [
      "Rebuilt title highlights in `GeneratedTextDetailsTextPane` from saved vocabulary records instead of relying only on local click state.",
      "Matched title-origin vocabulary by comparing saved `sentence` values against the resolved generated title.",
      "Also restored the persisted `title-only` classification in the details route so refreshes do not reintroduce body-highlight side effects.",
    ],
  },
  {
    id: "generated-text-title-highlight-style-matched",
    timestamp: "2026-03-14T08:24:26+01:00",
    title: "Title-word highlights now match text-word highlights",
    summary:
      "Words added from the generated text title now use the same green highlight treatment as added vocabulary inside the main text, so both interaction zones feel consistent.",
    userHighlights: [
      "Added vocabulary highlights now look the same in the title and the text body.",
      "Title clicks still behave the same and remain title-local.",
      "The green highlight fill no longer looks visually different just because the word was clicked in the title.",
    ],
    technicalNotes: [
      "Adjusted title-word button classes in `GeneratedTextDetailsTextPane.tsx` to use the same conditional background logic as body highlight spans.",
      "Stopped forcing `bg-transparent` in highlighted title states so the green fill can render identically.",
      "No backend changes.",
    ],
  },
  {
    id: "word-added-toast-shared-parent-handler",
    timestamp: "2026-03-14T08:18:56+01:00",
    title:
      "`Word added` toast now fires from the shared add-vocabulary handler",
    summary:
      "The vocabulary success toast is now triggered from the common parent callback instead of the individual title/text click handlers, so `Word added` appears consistently no matter where the user taps.",
    userHighlights: [
      "Tapping a word in the title still shows `Word added`.",
      "Tapping a word in the body text now shows the same `Word added` toast.",
      "The toast behavior is now consistent across both add-word entry points.",
    ],
    technicalNotes: [
      "Removed direct snackbar calls from the title/body add-word code paths inside `GeneratedTextDetailsTextPane.tsx`.",
      "Moved the toast trigger into the shared `onVocabularyCreate(...)` handler in `reeprise/$generatedTextId.tsx`.",
      "This centralizes add-word feedback behind one callback so title and text interactions cannot drift apart.",
    ],
  },
  {
    id: "generated-text-title-click-no-body-mis-highlight",
    timestamp: "2026-03-14T08:17:58+01:00",
    title: "Title clicks now stop causing wrong body-word highlights",
    summary:
      "Adding vocabulary from the generated text title now keeps the title interaction local, so the reading area no longer highlights an unrelated body word after a title click.",
    userHighlights: [
      "Clicking a title word still adds that word to your vocabulary list.",
      "The clicked title word keeps its title highlight behavior.",
      "The main text no longer highlights a mismatched body word after a title-based vocabulary add.",
    ],
    technicalNotes: [
      "Split generated-text vocabulary creation events by source: `title` versus `text`.",
      "Updated the details page to exclude title-origin vocabulary IDs from body-word highlight derivation.",
      "Body highlights still respond normally for vocabulary created from direct text-span clicks.",
    ],
  },
  {
    id: "word-added-toast-mobile-visibility-tune",
    timestamp: "2026-03-14T08:15:30+01:00",
    title:
      "Compact `Word added` toast now sits higher and stays visible longer",
    summary:
      "The compact vocabulary-success toast is now positioned near the top on mobile and remains visible a bit longer, so the short `Word added` confirmation is easier to notice.",
    userHighlights: [
      "On mobile, `Word added` now appears near the top-center instead of low in the screen.",
      "The toast still stays compact with no extra helper text.",
      "The message remains visible slightly longer before fading out.",
    ],
    technicalNotes: [
      "Updated `SnackBar.tsx` to anchor the snackbar near the top on mobile while keeping the lower-right placement on larger screens.",
      "Removed the width-constraining wrapper behavior and switched the message pill to a natural-content layout.",
      "Extended the auto-close timer from 2000ms to 2600ms to improve visibility for very short success messages.",
    ],
  },
  {
    id: "compact-word-added-toast",
    timestamp: "2026-03-14T08:11:24+01:00",
    title: "Vocabulary add feedback is now a compact `Word added` toast",
    summary:
      "Adding a vocabulary word from generated text now shows a much smaller success toast with only the final confirmation message, instead of a larger styled card with extra labels.",
    userHighlights: [
      "The toast is now compact and only says `Word added`.",
      "It appears after the word was actually added, not before the request finishes.",
      "Mobile still keeps the toast above the sticky bottom action area.",
    ],
    technicalNotes: [
      "Simplified `SnackBar.tsx` into a small rounded weather-tinted pill while preserving the mobile-safe placement and z-index.",
      "Replaced the previous pre-request `Generating Vocabulary.` notifications in `GeneratedTextDetailsTextPane.tsx` with a post-success `word_added_toast` notification.",
      "Removed the now-unused secondary snackbar translation keys and added `word_added_toast` to `translations.json`.",
    ],
  },
  {
    id: "snackbar-matched-daily-card-layout",
    timestamp: "2026-03-14T08:08:01+01:00",
    title: "Success toast now matches the daily card layout more closely",
    summary:
      "The success toast now follows the same wide sky-card composition as the daily text card, with capsule status copy, serif message styling, and integrated cloud shapes.",
    userHighlights: [
      "The toast now looks like a compact version of the daily card instead of a separate widget style.",
      "Status feedback is reduced to a simple `All worked` capsule and the actual action message.",
      "Mobile still keeps the toast above the sticky bottom action area.",
    ],
    technicalNotes: [
      "Reworked `SnackBar.tsx` from a centered badge layout into a wide weather-card panel with left status pill, serif content, and right-side timestamp accent.",
      "Adjusted cloud placement and overlay treatment to mirror the daily card structure more directly.",
      "Added translation key `snackbar_now` for the small time accent label.",
    ],
  },
  {
    id: "weather-card-inspired-success-toast",
    timestamp: "2026-03-14T08:02:41+01:00",
    title: "Success toasts now borrow the daily weather-card look",
    summary:
      "Feedback toasts now use the same bright-sky and cloud language as the daily text card, with a simpler success message and mobile-safe placement above sticky actions.",
    userHighlights: [
      "Toasts now feel visually connected to the daily text card instead of using a separate brand-style bubble.",
      "The toast no longer shows `Uxhulu`; it just confirms the action in a cleaner success layout.",
      "On mobile, the toast still stays above the fixed bottom action area so the message remains readable.",
    ],
    technicalNotes: [
      "Updated `SnackBar.tsx` to use a weather-card-inspired sky gradient, cloud clusters, and a compact success badge with check icon.",
      "Removed the extra brand label and replaced it with a minimal `All worked` confirmation line under the main message.",
      "Kept the snackbar at `z-40` with safe-area-aware bottom spacing so it clears the mobile chapter-generation CTA.",
    ],
  },
  {
    id: "generated-text-details-story-stream-restored",
    timestamp: "2026-03-14T08:01:23+01:00",
    title:
      "Generated text details now stream story text again without JSON flashes",
    summary:
      "The generated text details page now keeps the safe header/body phases and restores the live reading effect by streaming partial story text directly from the structured `story` field.",
    userHighlights: [
      "Story text now grows live again while generation is running.",
      "The main title still stays on `Titel wird erstellt …` until a real title is ready.",
      "The reading area still never shows raw JSON syntax or `title` payload text.",
    ],
    technicalNotes: [
      "Adjusted the generated-text resolver to allow partial extraction of the structured `story` string while a payload is still incomplete.",
      "Kept title parsing strict so incomplete title transport data does not leak into the header.",
      "Safe streaming still hides raw JSON framing and only renders decoded story characters.",
    ],
  },
  {
    id: "cloud-toast-snackbar-refresh",
    timestamp: "2026-03-14T07:55:33+01:00",
    title: "Toasts now float in a cloud-style bubble above mobile actions",
    summary:
      "Feedback toasts now use a softer cloud-like design and sit above sticky mobile controls, so status messages stay visible without clashing with the reading layout.",
    userHighlights: [
      "Toast messages now appear in a rounded cloud-style bubble that matches the app's lighter editorial look.",
      "On mobile, the toast now clears the sticky bottom action area instead of getting hidden behind it.",
      "The updated toast stays centered on small screens and moves back to the lower-right corner on larger screens.",
    ],
    technicalNotes: [
      "Redesigned `SnackBar.tsx` with layered circular shapes and a light sky-tinted gradient instead of the previous plain rectangular slab.",
      "Raised the snackbar stacking order to `z-40` and added mobile-safe bottom padding so it clears the fixed chapter-generation CTA.",
      "Removed shadow-based styling from the toast refresh to stay aligned with repository frontend guidance.",
    ],
  },
  {
    id: "generated-text-details-explicit-stream-phases",
    timestamp: "2026-03-14T07:49:22+01:00",
    title: "Generated text details now use explicit safe streaming phases",
    summary:
      "The generated text details page now renders generation through explicit title/story phases so the header no longer falls back to the topic and the body never shows raw JSON transport data.",
    userHighlights: [
      "While a title is not ready yet, the page now keeps the neutral `Titel wird erstellt …` header instead of showing the typed topic as the main title.",
      "The body stays empty until a clean story is available, then streams only story text.",
      "JSON `title` and `story` payload text no longer flashes inside the reading area during generation.",
    ],
    technicalNotes: [
      "Added a shared `resolveGeneratedTextContent(...)` helper with explicit phases: `pending_no_title`, `pending_title_only`, `pending_story`, and `complete`.",
      "Updated `GeneratedTextDetailsTextPane` to resolve one header state and one body state instead of independently mixing topic, websocket chunks, and final saved output.",
      "Pending-state story rendering now reads only a closed `story` string field from structured payloads; otherwise it renders nothing until the content is safe.",
    ],
  },
  {
    id: "generated-text-details-stable-generation-copy",
    timestamp: "2026-03-14T07:18:36+01:00",
    title:
      "Generated text details now hide raw JSON and keep a stable title while loading",
    summary:
      "The generated text details page now uses the same loading copy as the text cards, never exposes raw JSON during streaming, and transitions smoothly into the finished text.",
    userHighlights: [
      "The details header now shows `Titel wird erstellt …` until a real generated title is available.",
      "Users no longer see raw JSON or code fences flash in the main text while generation is still in progress.",
      "The loading-to-finished transition now keeps the same title/body rendering path for a smoother read.",
    ],
    technicalNotes: [
      "Extracted shared generated-text parsing helpers so cards and the details page resolve AI title and story content the same way.",
      "Updated the details page to derive its title from parsed AI output instead of the generic prompt topic during pending state.",
      "Suppressed incomplete JSON/code-fence payloads in streamed text parsing so only valid story content is rendered.",
    ],
  },
  {
    id: "daily-and-tricky-text-origins-separated",
    timestamp: "2026-03-14T06:37:56+01:00",
    title: "Daily texts and tricky-word texts now stay separate",
    summary:
      "The daily learning card now creates its own daily text instead of reusing the tricky-word practice flow, so both cards can support different learning goals.",
    userHighlights: [
      "The daily card now opens a separate daily learning text just for that nudge.",
      "The tricky-word card keeps its own practice texts focused on hard words.",
      "Reading one card no longer marks the other card as already generated.",
    ],
    technicalNotes: [
      "Added explicit `GeneratedText.origin` values so the app can distinguish manual, daily, and tricky-word texts without guessing from content.",
      "Changed `/generated_texts/daily-practice/` to create `DAILY_NUDGE` texts that use recent interests and vocabulary context instead of the struggle-word generator.",
      "Updated `DailyPracticeCallout` to filter daily-card history by origin and added backend regression tests for same-day separation.",
    ],
  },
  {
    id: "daily-callout-keep-all-generated-daily-cards-neutral-history",
    timestamp: "2026-03-03T21:09:56+01:00",
    title:
      "Daily callout now keeps all generated daily cards as neutral history",
    summary:
      "All generated daily cards now remain visible in the top daily section, while only one main card keeps the weather-driven style and the rest are neutral compact history cards.",
    userHighlights: [
      "Older generated daily cards no longer disappear from the daily section.",
      "Only one main card uses the full weather/daylight visual treatment.",
      "All other daily cards are shown in compact neutral style and stay clickable.",
    ],
    technicalNotes: [
      "Reworked `DailyPracticeCallout` to build a sorted list of all daily-practice texts and render non-featured items in a neutral compact list.",
      "Introduced `featuredDailyTextId` to ensure exactly one weather-styled main card at a time.",
      "Updated `dailyTextPreviousBadge` copy for multi-item history context.",
    ],
  },
  {
    id: "daily-callout-keep-yesterday-minimal-card",
    timestamp: "2026-03-03T21:04:02+01:00",
    title:
      "Restructured daily callout to keep yesterday's card in minimal mode",
    summary:
      "When no new daily text is done yet today, yesterday's daily text now remains visible as a compact neutral card below the active daily card.",
    userHighlights: [
      "Yesterday's daily text no longer disappears from the top area.",
      "The previous card is intentionally compact and neutral.",
      "You can still open yesterday's text directly from this minimal card.",
    ],
    technicalNotes: [
      "Added previous-day daily-text detection in `DailyPracticeCallout` using dated daily-practice heuristics.",
      "Rendered a new compact neutral card branch for the latest pre-today daily text when today is not done.",
      "Added translation key `dailyTextPreviousBadge` for the compact card label.",
    ],
  },
  {
    id: "directional-srs-vocabulary-trainer",
    timestamp: "2026-03-02T20:49:20+01:00",
    title: "Vocabulary trainer now tracks SRS in both directions",
    summary:
      "The trainer now stores separate spaced-repetition progress for Target→Native and Native→Target, and introduces reverse cards only after forward progress is stable.",
    userHighlights: [
      "Your answers now count for the exact card direction you practiced.",
      "Reverse direction (Native→Target) appears gradually after you build a solid forward streak.",
      "Trainer cards now show a clear direction label so each prompt is unambiguous.",
    ],
    technicalNotes: [
      "Backend: `VocabularyUserProgress` now stores `direction`; review actions persist streak/last_tested per direction; trainer state now stores active `review_direction`.",
      "Trainer queue logic now selects due cards direction-aware, unlocks reverse cards after a forward streak threshold, and returns per-card `review_direction`.",
      "Frontend trainer sends `review_direction` in review mutations and renders headers/subheaders from per-card direction data.",
    ],
  },
  {
    id: "weather-location-smart-search-dropdown",
    timestamp: "2026-03-02T20:38:15+01:00",
    title: "Added smart weather-location search with dropdown suggestions",
    summary:
      "Settings now provides city autocomplete via backend geocoding suggestions, so users can pick valid locations faster and auto-fill country code.",
    userHighlights: [
      "Typing at least 2 characters in the city field now opens a location dropdown.",
      "Selecting a suggestion fills both city and country code automatically.",
      "Supports common variants like Vienna/Wien through geocoding-backed search.",
    ],
    technicalNotes: [
      "Backend: added authenticated `GET /api/reeprise/weather/location-suggestions/` endpoint with Open-Meteo geocoding, optional country filter, fallback, and caching.",
      "Frontend: added `useWeatherLocationSuggestions` hook and integrated dropdown UI in `settings.tsx`.",
      "Added i18n keys for suggestion loading and empty states in `translations.json`.",
    ],
  },
  {
    id: "settings-weather-location-controls-ui",
    timestamp: "2026-03-02T20:29:29+01:00",
    title: "Added weather location controls in Settings",
    summary:
      "Users can now set city and country code in Settings so the daily card weather theme uses their chosen location.",
    userHighlights: [
      "New settings section for daily-card weather location.",
      "Save city and 2-letter country code directly from the app.",
      "Inline saving, success, and error feedback for location updates.",
    ],
    technicalNotes: [
      "Updated `settings.tsx` with controlled `weather_city` and `weather_country_code` inputs wired to `useUserSettingsUpdate`.",
      "Added new translation keys in `translations.json` for weather-location labels, hints, and status messages.",
      "Country code input is normalized to uppercase and constrained to 2 characters before PATCH.",
    ],
  },
  {
    id: "daily-done-style-today-vs-past-scope-fix",
    timestamp: "2026-03-02T19:24:50+01:00",
    title:
      "Scoped done-card styling by day (today keeps weather, past goes neutral)",
    summary:
      "The done-state daily card now keeps the weather/daylight look on the same day, and only falls back to neutral styling once it is no longer today.",
    userHighlights: [
      "Heute-erledigt still matches today’s daily weather design.",
      "Only non-current daily cards lose weather styling and appear neutral.",
      "The active new daily card remains the only weather-driven daily prompt each day.",
    ],
    technicalNotes: [
      "In `DailyPracticeCallout`, added `isDoneCardForToday` based on `doneCardText.created_at` and local done marker date.",
      "Done-card theme now conditionally uses `activeThemeClasses` for today and neutral classes for non-today states.",
    ],
  },
  {
    id: "daily-done-card-no-live-weather-theme",
    timestamp: "2026-03-02T19:22:50+01:00",
    title: "Done-state daily card no longer uses live weather styling",
    summary:
      "The completed daily card now keeps a fixed neutral style, while only the active daily card reflects current weather/daylight mood.",
    userHighlights: [
      "Heute-erledigt card now looks consistent and no longer changes with hourly weather.",
      "Only the active daily prompt card uses live weather visualization.",
      "Avoids the impression that past daily cards all carry changing weather moods.",
    ],
    technicalNotes: [
      "In `DailyPracticeCallout`, split theme mapping into `activeThemeClasses` (weather/daylight-aware) and `doneThemeClasses` (fixed neutral/day).",
      "Applied neutral theme classes to the done-card render branch in `reeprise/index.tsx`.",
    ],
  },
  {
    id: "daily-done-card-title-preview-by-marker-id-fix",
    timestamp: "2026-03-02T19:19:30+01:00",
    title: "Fixed done-card fallback copy when daily text already exists",
    summary:
      "The done-state daily card now resolves title and preview text via the stored daily text id, preventing fallback copy from showing after the text was generated.",
    userHighlights: [
      "Done-card now more reliably shows the real generated title.",
      "Done-card preview now more reliably shows the beginning of the generated text with `...` when needed.",
      "Fallback `Morgen wartet ...` text is now only used when the actual text record is not available yet.",
    ],
    technicalNotes: [
      "Updated `DailyPracticeCallout` in `reeprise/index.tsx` to prioritize `openTextId` lookup in `generatedTexts` before topic-based daily-text heuristics.",
      "This avoids false negatives when the backend replaces the generic daily topic with the final generated title.",
    ],
  },
  {
    id: "daily-card-daylight-phase-mood-visualization",
    timestamp: "2026-03-02T19:11:03+01:00",
    title: "Daily card now reflects daylight mood (dawn/day/dusk/night)",
    summary:
      "The weather-adaptive daily card now also adjusts atmosphere by local daylight phase, so visuals match sunrise, daytime, sunset, and night mood.",
    userHighlights: [
      "The same weather theme now looks different in dawn, day, dusk, and night.",
      "Morning card mood is warmer at sunrise, calmer at dusk, and darker at night.",
      "Done-state and active daily card both use the same daylight-aware visual behavior.",
    ],
    technicalNotes: [
      "Backend weather-theme endpoint now includes `daylight_phase` derived from local current time and sunrise/sunset from Open-Meteo daily data.",
      "Frontend `useDailyWeatherTheme` DTO extended with `daylight_phase` typing and consumed in `DailyPracticeCallout`.",
      "Updated daily-card class mapper in `reeprise/index.tsx` to combine weather theme layers with daylight-phase mood overlays.",
    ],
  },
  {
    id: "weather-adaptive-daily-card-v1-implementation",
    timestamp: "2026-03-02T18:59:27+01:00",
    title:
      "Implemented weather-adaptive daily card v1 (city-based, hourly refresh)",
    summary:
      "The daily card now adapts its visual mood to local weather using a backend weather-theme endpoint, with Vienna fallback and hourly client refresh.",
    userHighlights: [
      "Daily card now switches among clear/cloudy/rain/snow/night/neutral visual variants while keeping the same core card design language.",
      "Weather theme refreshes every hour and when the page regains focus.",
      "Daily card is now visible from morning hours (from 06:00 local time) instead of only later in the day.",
    ],
    technicalNotes: [
      "Backend: added `GET /api/reeprise/generated_texts/weather-theme/` using Open-Meteo via server-side proxy with caching and graceful neutral fallback.",
      "Backend: added `UserSetting` fields `weather_city` and `weather_country_code` (migration `0073_usersetting_weather_location`) and exposed them via `UserSettingSerializer`.",
      "Frontend: added `useDailyWeatherTheme` hook with hourly/focus refetch and integrated theme class mapping into `DailyPracticeCallout` while preserving existing layout/animation language.",
    ],
  },
  {
    id: "weather-planning-morning-visibility-requirement",
    timestamp: "2026-03-02T18:43:46+01:00",
    title: "Weather card planning now requires morning visibility",
    summary:
      "The planning now explicitly states that the daily card must be visible in the morning in the user’s local timezone.",
    userHighlights: [
      "Daily card availability is now clearly defined for morning usage.",
      "Timezone handling is explicitly required for visibility timing.",
      "Weather theming must not block or delay morning card display.",
    ],
    technicalNotes: [
      "Updated `planning_daily_weather_card.md` scope and added `Visibility Timing` section.",
      "No runtime code logic changed in this update.",
    ],
  },
  {
    id: "weather-planning-animation-style-guardrails-added",
    timestamp: "2026-03-02T18:42:01+01:00",
    title: "Weather planning updated with animation consistency guardrails",
    summary:
      "The weather-adaptive card plan now explicitly requires animation style consistency, using the current cloud drift motion as baseline across weather themes.",
    userHighlights: [
      "Ensures weather variants feel like one coherent design system in motion.",
      "Prevents flashy or mismatched animation styles between weather states.",
      "Keeps reduced-motion accessibility behavior consistent across variants.",
    ],
    technicalNotes: [
      "Added `Animation Consistency Guardrails` section to `planning_daily_weather_card.md`.",
      "Defined baseline cloud animation approach and weather-specific motion guidance (clear/cloudy/rain/snow/night).",
    ],
  },
  {
    id: "weather-planning-visual-style-guardrails-added",
    timestamp: "2026-03-02T18:41:16+01:00",
    title:
      "Weather card planning now enforces existing visual style consistency",
    summary:
      "The weather-adaptive daily-card plan now explicitly requires all weather variants to keep the current card design language and only vary atmospheric mood layers.",
    userHighlights: [
      "Prevents each weather state from feeling like a different component.",
      "Keeps typography, spacing, structure, and interaction consistent across weather themes.",
      "Ensures weather visuals stay in the same recognizable product style.",
    ],
    technicalNotes: [
      "Added `Visual Consistency Guardrails` section to `planning_daily_weather_card.md`.",
      "Guardrails define stable card shell/layout and limit weather changes to gradients/overlays/subtle motion.",
    ],
  },
  {
    id: "weather-planning-initial-location-vienna",
    timestamp: "2026-03-01T21:34:02+01:00",
    title: "Weather planning updated with Vienna as first location",
    summary:
      "The weather-adaptive daily-card planning now explicitly sets Vienna as the initial/default rollout location for V1.",
    userHighlights: [
      "Initial city for rollout and QA is now clearly defined as Vienna.",
      "Helps align implementation, testing, and demo behavior.",
      "User-specific location support remains planned for later phases.",
    ],
    technicalNotes: [
      "Updated `planning_daily_weather_card.md` with an `Initial Location (V1)` section specifying Vienna (`AT`).",
      "No runtime code behavior changed yet.",
    ],
  },
  {
    id: "weather-adaptive-daily-card-planning-doc",
    timestamp: "2026-03-01T21:33:04+01:00",
    title: "Added planning document for weather-adaptive daily card",
    summary:
      "A new technical planning document defines architecture and rollout for weather-driven daily card theming with hourly refresh and safe fallbacks.",
    userHighlights: [
      "Plan covers location strategy, weather source, UI theme mapping, refresh cadence, and fallback behavior.",
      "Production-safe V1 focuses on city-based weather via backend proxy (no live GPS required).",
      "Includes clear phased rollout and QA checklist.",
    ],
    technicalNotes: [
      "Added `planning_daily_weather_card.md` at repository root.",
      "Document includes proposed backend API contract and frontend update strategy for hourly weather theming.",
    ],
  },
  {
    id: "daily-card-read-cta-inline-style-aligned",
    timestamp: "2026-03-01T21:28:47+01:00",
    title: "Daily `Jetzt lesen` CTA aligned to inline cue style",
    summary:
      "The active daily card CTA now uses the same subtle inline style as the done-card open cue, replacing the large filled button look.",
    userHighlights: [
      "Daily and done card actions now feel visually consistent.",
      "No oversized filled button on the active daily card.",
      "Action still stays clear through inline text plus arrow cue.",
    ],
    technicalNotes: [
      "Restyled active daily-card action button classes in `reeprise/index.tsx` to inline text treatment.",
      "Added chevron icon to non-loading CTA state while preserving existing click behavior.",
    ],
  },
  {
    id: "daily-done-open-cue-non-button-inline",
    timestamp: "2026-03-01T21:26:33+01:00",
    title: "Done-card open indicator changed to non-button inline cue",
    summary:
      "The done-state card no longer uses a button-like open indicator; it now uses a lightweight inline cue while keeping the full card tappable.",
    userHighlights: [
      "Cleaner look without a secondary button feel.",
      "Whole card remains the main interaction target.",
      "Open affordance is still visible via subtle inline label + arrow.",
    ],
    technicalNotes: [
      "Replaced bordered/pill `dailyTextDoneOpen` hint with plain inline text cue in `reeprise/index.tsx`.",
      "No behavior changes to click/keyboard navigation.",
    ],
  },
  {
    id: "daily-done-text-cleanup-for-natural-preview",
    timestamp: "2026-03-01T21:26:08+01:00",
    title: "Cleaned up done-card title and sentence preview",
    summary:
      "Done-card text extraction was refined to avoid odd-looking raw fragments and produce cleaner, natural-looking title/preview text.",
    userHighlights: [
      "Heading now trims noisy characters and overly long titles.",
      "Sentence preview now removes raw prefixes and clips at word boundaries.",
      "Raw JSON-like payload artifacts are no longer shown as preview text.",
    ],
    technicalNotes: [
      "Enhanced daily-card parsing/sanitization helpers in `reeprise/index.tsx` for title and first-sentence extraction.",
      "Added safe clipping rules and JSON-like suppression fallback for done-card teaser content.",
    ],
  },
  {
    id: "daily-done-card-tappable-surface-open-cue",
    timestamp: "2026-03-01T21:24:39+01:00",
    title: "Done-state daily card switched from large button to tappable card",
    summary:
      "The large `Öffnen` button was replaced by a cleaner interaction: the whole done-state card is now tappable, with a subtle open indicator.",
    userHighlights: [
      "No oversized button in the done-state card anymore.",
      "A tap/click anywhere on the card opens the text.",
      "A small visual `Open` cue still communicates the action clearly.",
    ],
    technicalNotes: [
      "Made done-state daily card container keyboard/tap interactive (`role`, `tabIndex`, Enter/Space handling) in `reeprise/index.tsx`.",
      "Replaced CTA button with a compact open hint chip using `dailyTextDoneOpen` + chevron icon.",
    ],
  },
  {
    id: "daily-done-description-from-generated-text-first-sentence",
    timestamp: "2026-03-01T21:23:55+01:00",
    title: "Done-card description now uses first sentence from generated text",
    summary:
      "Instead of static copy, the done-state daily card now displays the first sentence of the generated text (trimmed with `...` when long).",
    userHighlights: [
      "Done-card description now reflects the actual generated text.",
      "Long opening sentences are shortened cleanly with `...`.",
      "Static fallback text still appears when no text sentence is available.",
    ],
    technicalNotes: [
      "Added daily-card helpers to parse story body and extract first-sentence preview in `reeprise/index.tsx`.",
      "Done-state description now prefers parsed text preview over `dailyTextDoneDescription` translation.",
    ],
  },
  {
    id: "daily-done-title-from-generated-text-and-copy-tightening",
    timestamp: "2026-03-01T21:22:31+01:00",
    title: "Heute-erledigt heading now uses generated text title",
    summary:
      "The done-state daily card now shows the actual generated text title as its heading, and the German done description was shortened.",
    userHighlights: [
      "Replaced generic `Heute erledigt` heading with the real generated title when available.",
      "German done description now reads only `Morgen wartet ein neuer auf dich.`",
      "Fallback remains stable if a parsed title is not available.",
    ],
    technicalNotes: [
      "Added local title parsing helper for daily done card and derived heading from today’s resolved daily text (`chatgpt_output` title, then topic fallback).",
      "Updated `dailyTextDoneDescription.GERMAN` translation value in `translations.json`.",
    ],
  },
  {
    id: "daily-card-white-line-removed",
    timestamp: "2026-03-01T21:20:40+01:00",
    title: "Removed white line from daily card background",
    summary:
      "The white bottom line/glow accent was removed from the daily card to keep the visual surface cleaner.",
    userHighlights: [
      "No more white line at the bottom of the daily card.",
      "Card looks calmer and more uniform.",
      "Applied consistently to both active and done card states.",
    ],
    technicalNotes: [
      "Removed the absolute bottom border/gradient layer from both daily-card render branches in `reeprise/index.tsx`.",
      "No behavior changes.",
    ],
  },
  {
    id: "daily-done-open-button-reliability-fallback",
    timestamp: "2026-03-01T21:20:17+01:00",
    title: "Fixed missing open path from Heute-erledigt card",
    summary:
      "The done-state daily card now reliably keeps an open button by using broader same-day matching and marker backfill for older stored states.",
    userHighlights: [
      "You can open today’s generated text from the Heute-erledigt card more reliably.",
      "Older done markers without a stored text id are now auto-repaired when today’s text is found.",
      "Done-state remains visually unchanged while interaction is more robust.",
    ],
    technicalNotes: [
      "Added fallback same-day practice-text detection (without strict topic-date dependency) in `DailyPracticeCallout`.",
      "When a target text id is resolved, the local daily marker is backfilled with `{ date, textId }` to stabilize future opens.",
    ],
  },
  {
    id: "daily-done-card-openability-and-style-alignment",
    timestamp: "2026-03-01T21:18:38+01:00",
    title:
      "Heute-erledigt card now keeps open access and matches daily card style",
    summary:
      "The completed daily card now follows the same sky/cloud design as the active daily card and still lets users open the generated text reliably.",
    userHighlights: [
      "Done-state card now visually matches the regular daily card.",
      "You can open today’s generated text directly from the done-state card.",
      "Open access remains stable even during list refresh timing delays.",
    ],
    technicalNotes: [
      "Extended daily done marker storage to support `{ date, textId }` (while remaining compatible with previous string values).",
      "Done-state card now resolves open target from either list match or stored marker `textId`, and reuses the same sky/cloud layout as the active card in `reeprise/index.tsx`.",
    ],
  },
  {
    id: "daily-cloud-animation-visibility-boost",
    timestamp: "2026-03-01T21:15:29+01:00",
    title: "Increased visibility of daily card cloud animation",
    summary:
      "Cloud movement on the daily card was intensified so the animation is clearly visible instead of nearly static.",
    userHighlights: [
      "Cloud drift is now easier to perceive at a glance.",
      "Motion remains smooth and calm, but no longer feels frozen.",
      "Reduced-motion users still get a static version.",
    ],
    technicalNotes: [
      "Adjusted `daily-cloud-drift-left/right` keyframes in `src/index.css` to use larger translate offsets and subtle opacity/scale variation.",
      "Shortened animation durations per cloud layer to improve perceivable movement.",
    ],
  },
  {
    id: "daily-text-card-cloud-animation",
    timestamp: "2026-03-01T21:13:35+01:00",
    title: "Added subtle cloud animation to the daily text card",
    summary:
      "The daily text card now has gentle cloud movement to make the morning surface feel more alive without being distracting.",
    userHighlights: [
      "Clouds now drift slowly across the daily card background.",
      "Animation is subtle and readability-focused.",
      "Motion is automatically disabled for users with reduced-motion preference.",
    ],
    technicalNotes: [
      "Added `daily-cloud-*` classes to daily-card cloud layers in `reeprise/index.tsx`.",
      "Implemented `daily-cloud-drift-left/right` keyframes and reduced-motion media handling in `src/index.css`.",
    ],
  },
  {
    id: "daily-and-uebungstext-button-style-aligned",
    timestamp: "2026-03-01T21:12:06+01:00",
    title: "Aligned Übungstext and daily card button style",
    summary:
      "The main CTA buttons on the Übungstext and daily cards now use the same shape and blue style for a consistent visual language.",
    userHighlights: [
      "Both cards now use the same rounded-full button shape.",
      "Color, border, hover, and disabled states are now consistent.",
      "Button typography now follows the same uppercase CTA style.",
    ],
    technicalNotes: [
      "Updated the Übungstext banner CTA classes in `GenerateTextCard.tsx` to match the daily card CTA style from `reeprise/index.tsx`.",
      "No behavior logic changes.",
    ],
  },
  {
    id: "uebungstext-placement-switched-back-to-generator-card",
    timestamp: "2026-03-01T21:11:21+01:00",
    title: "Übungstext card moved back to the generator panel",
    summary:
      "The Übungstext card placement was switched back: the generator card version is visible again and the separate top-area callout was removed.",
    userHighlights: [
      "Übungstext is now back in its original generator area.",
      "The extra card near the daily section is removed.",
      "Only one Übungstext card remains visible.",
    ],
    technicalNotes: [
      "Removed `StruggleWordsCallout` rendering and implementation from `reeprise/index.tsx`.",
      "Restored default `GenerateTextCard` usage without `showStruggleBanner={false}` override.",
    ],
  },
  {
    id: "uebungstext-duplicate-card-removed",
    timestamp: "2026-03-01T21:10:13+01:00",
    title: "Removed duplicate Übungstext card on Reeprise page",
    summary:
      "The duplicate Übungstext card was removed by keeping only the top callout near the daily card.",
    userHighlights: [
      "Only one Übungstext card is now visible on the page.",
      "Top placement next to the daily flow is preserved.",
      "Generator form stays cleaner without repeated callout content.",
    ],
    technicalNotes: [
      "Added `showStruggleBanner` prop to `GenerateTextCard` (default `true`).",
      "Set `showStruggleBanner={false}` in `reeprise/index.tsx` to avoid rendering the in-card duplicate.",
    ],
  },
  {
    id: "uebungstext-card-no-cloud-shapes",
    timestamp: "2026-03-01T21:09:25+01:00",
    title: "Removed cloud shapes from Übungstext card design",
    summary:
      "The Übungstext cards now keep the blue style but no longer include cloud-form decorations.",
    userHighlights: [
      "Cleaner Übungstext card look with less visual ornament.",
      "Blue color direction stays consistent with the current theme.",
      "Both Übungstext surfaces now follow the same no-cloud style.",
    ],
    technicalNotes: [
      "Removed cloud shape overlay markup from the Reeprise `StruggleWordsCallout`.",
      "Removed cloud shape overlay markup from the `GenerateTextCard` struggle banner block.",
    ],
  },
  {
    id: "uebungstext-callout-added-near-daily-card",
    timestamp: "2026-03-01T21:08:33+01:00",
    title: "Übungstext card also added to the daily-card area",
    summary:
      "The Übungstext callout now appears in the same top Reeprise section as the daily card, so users can access it immediately without opening the generator panel.",
    userHighlights: [
      "Übungstext is now visible next to the morning daily flow.",
      "One tap still creates and opens the generated practice text.",
      "Visual style remains aligned with the blue sky/cloud design.",
    ],
    technicalNotes: [
      "Added `StruggleWordsCallout` to `reeprise/index.tsx` and rendered it below `DailyPracticeCallout`.",
      "Hooked it to `useStruggleWords` + `useGeneratedTextCreate` and reused the per-language 24h hide gate from localStorage.",
    ],
  },
  {
    id: "uebungstext-card-sky-cloud-style",
    timestamp: "2026-03-01T21:07:23+01:00",
    title: "Übungstext card redesigned to match the sky/cloud visual style",
    summary:
      "The practice-text card in the generator now follows the same calm blue-sky direction as the daily card, with a compact cloud-themed surface.",
    userHighlights: [
      "Übungstext card now feels visually consistent with the morning daily card.",
      "Color tone is calmer and less noisy.",
      "Action button style now matches the blue visual direction.",
    ],
    technicalNotes: [
      "Restyled the struggle-word callout block in `GenerateTextCard.tsx` with sky gradient and layered cloud shapes.",
      "Kept existing behavior and logic unchanged; this was a styling-only update without drop shadows.",
    ],
  },
  {
    id: "daily-text-title-duplicate-removed",
    timestamp: "2026-03-01T21:06:33+01:00",
    title: "Removed duplicate `Text des Tages` labeling",
    summary:
      "Only the morning daily card now uses the `Text des Tages` title; the separate struggle-word card has distinct wording again.",
    userHighlights: [
      "No more second card with the same `Text des Tages` headline.",
      "Exercise card is now clearly labeled as `Übungstext`.",
      "Card descriptions are now distinct and less confusing.",
    ],
    technicalNotes: [
      "Updated translation keys `struggle_words_title` and `struggle_words_description` in `translations.json`.",
      "No logic or routing changes.",
    ],
  },
  {
    id: "daily-text-card-blue-sky-cloud-redesign",
    timestamp: "2026-03-01T21:05:41+01:00",
    title:
      "Daily morning card redesigned to realistic blue-sky cloud direction",
    summary:
      "The first daily card now uses a calmer sky-blue visual system with layered cloud forms instead of a multi-color palette.",
    userHighlights: [
      "Card now feels cleaner and more motivating in the morning.",
      "Background follows a realistic blue-sky gradient with soft cloud structure.",
      "Primary action now matches the blue direction for a consistent look.",
    ],
    technicalNotes: [
      "Replaced warm gradient and accent classes in `DailyPracticeCallout` with sky-blue palette classes.",
      "Added absolute layered cloud shape elements (radial gradients + rounded forms) without drop shadows.",
    ],
  },
  {
    id: "daily-text-copy-positive-german-tone",
    timestamp: "2026-03-01T21:04:28+01:00",
    title: "Daily text German description changed to positive wording",
    summary:
      "The daily card description in German now avoids negative framing and uses a more encouraging tone.",
    userHighlights: [
      "Removed wording that could feel corrective or discouraging.",
      "Daily card now reads as a positive next-step invitation.",
      "Tone remains clear that this is your daily text.",
    ],
    technicalNotes: [
      "Updated German values for `dailyTextDescription` and aligned `struggle_words_description` in `translations.json`.",
      "No behavior or layout logic changed.",
    ],
  },
  {
    id: "daily-text-card-warm-morning-palette",
    timestamp: "2026-03-01T21:03:53+01:00",
    title: "Daily text morning card shifted to a warmer, less dark style",
    summary:
      "The first card users see in the morning now avoids the previous black-heavy look and uses a softer, more inviting color direction.",
    userHighlights: [
      "The daily card now uses a warm sunrise-like background instead of cool gray.",
      "Date/weekday accents now feel lighter and more editorial.",
      "The main action button is no longer black and feels more inviting to start reading.",
    ],
    technicalNotes: [
      "Updated `DailyPracticeCallout` container, text, and badge color classes in `reeprise/index.tsx`.",
      "Changed CTA from slate/black styling to a warm rose accent while keeping behavior unchanged.",
    ],
  },
  {
    id: "daily-text-copy-text-des-tages",
    timestamp: "2026-03-01T21:03:13+01:00",
    title: "Daily card copy updated to `Text des Tages`",
    summary:
      "The morning daily-text card now uses clearer German wording so it is immediately understood as the daily text.",
    userHighlights: [
      "Headline changed from `Dein Text für heute` to `Text des Tages`.",
      "Description now explicitly says this is your daily text.",
      "Wording stays warm but now communicates the daily rhythm more clearly.",
    ],
    technicalNotes: [
      "Updated `dailyTextHeading` and `dailyTextDescription` German translations in `translations.json`.",
      "Aligned legacy `struggle_words_title` and `struggle_words_description` German strings to the same wording.",
    ],
  },
  {
    id: "daily-text-done-state-latched-per-day",
    timestamp: "2026-03-01T21:02:27+01:00",
    title: "Daily text done-state now stays latched for the day",
    summary:
      "The compact `done for today` card no longer flips back to the full create card during short data refresh transitions.",
    userHighlights: [
      "Once today is marked done, the compact done card stays stable.",
      "Temporary list/query refresh timing no longer causes card state bouncing.",
      "The card still resets normally for the next day.",
    ],
    technicalNotes: [
      "Added a day+language-scoped `doneLatched` UI state in `DailyPracticeCallout`.",
      "Latched state is reset only when the day or language bucket changes, and is promoted to `true` whenever done conditions become true.",
    ],
  },
  {
    id: "daily-text-done-state-flicker-fix",
    timestamp: "2026-03-01T20:59:59+01:00",
    title: "Fixed daily text done-state flicker after creation",
    summary:
      "The collapsed `done for today` daily-text card now remains stable instead of briefly switching back to the full creation card.",
    userHighlights: [
      "After creating today’s text, the compact done-state stays visible consistently.",
      "State now survives route changes and short list refresh delays.",
      "A fresh full card still appears on the next day.",
    ],
    technicalNotes: [
      "Moved daily-done marker from volatile component state to localStorage-backed state.",
      "Marker matching now covers both current language-space and resolved generation language to avoid transient mismatches.",
    ],
  },
  {
    id: "daily-text-card-collapses-to-done-state",
    timestamp: "2026-03-01T20:48:06+01:00",
    title: "Daily text card now collapses into a done-for-today state",
    summary:
      "After creating today’s daily text, the large card now collapses into a compact completion state instead of disappearing entirely.",
    userHighlights: [
      "You now get a clear `done for today` confirmation once your daily text is created.",
      "A compact card remains visible and can open today’s daily text directly.",
      "On the next day, the full creation card appears again automatically.",
    ],
    technicalNotes: [
      "Replaced the previous hide-on-create behavior in `DailyPracticeCallout` with a conditional compact completion surface.",
      "Added new translation keys for done-state heading, description, and open action in German and English.",
    ],
  },
  {
    id: "daily-text-heading-explicitly-today",
    timestamp: "2026-03-01T20:45:22+01:00",
    title: "Daily text heading now explicitly says it is for today",
    summary:
      "The callout headline was refined so users can immediately recognize the card as today’s daily text.",
    userHighlights: [
      "German headline now reads `Dein Text für heute`.",
      "English headline now reads `Your story for today`.",
      "Warm tone remains while daily intent is now explicit.",
    ],
    technicalNotes: [
      "Updated only `dailyTextHeading` translation values in `translations.json`.",
      "No component logic or layout changes.",
    ],
  },
  {
    id: "daily-text-callout-warm-non-school-copy",
    timestamp: "2026-03-01T20:44:48+01:00",
    title: "Daily text card copy rewritten to feel warmer and less school-like",
    summary:
      "The daily text callout wording was rewritten to sound more personal and inviting, with less technical and classroom-like phrasing.",
    userHighlights: [
      "Headline now reads as a personal invitation instead of a utility label.",
      "Description now feels softer and more human while keeping the same meaning.",
      "CTA now uses a more natural reading-focused wording.",
    ],
    technicalNotes: [
      "Updated existing `dailyTextHeading`, `dailyTextDescription`, and `dailyTextCta` entries in translations without changing keys or component logic.",
      "Applied in both German and English translation values.",
    ],
  },
  {
    id: "daily-text-callout-compact-tone-down",
    timestamp: "2026-03-01T20:43:59+01:00",
    title: "Daily text callout scaled down to a calmer compact style",
    summary:
      "The newly redesigned morning callout was intentionally reduced in visual intensity and size for a cleaner first-view balance.",
    userHighlights: [
      "Card now uses a more compact footprint with smaller headline and tighter spacing.",
      "Date/weekday row remains, but with lighter visual emphasis.",
      "CTA remains clear while feeling less dominant.",
    ],
    technicalNotes: [
      "Reduced typography scale, spacing, and border weight in `DailyPracticeCallout`.",
      "Removed oversized decorative ticket-cut accents and simplified surface treatment.",
    ],
  },
  {
    id: "daily-text-callout-morning-first-impression-redesign",
    timestamp: "2026-03-01T20:42:28+01:00",
    title:
      "Daily text morning callout redesigned as a standout first-impression card",
    summary:
      "The morning `Ein Text für dich …` callout was redesigned to feel more collectible and editorial, with a stronger reading invitation and built-in weekday/date context.",
    userHighlights: [
      "The card now presents weekday and date at the top for stronger daily rhythm.",
      "Headline and CTA hierarchy were rebuilt so the card feels like a featured daily drop.",
      "Visual treatment now uses a unique border + ticket-cut motif without drop shadows.",
    ],
    technicalNotes: [
      "Reworked `DailyPracticeCallout` layout in `reeprise/index.tsx` with new grid structure, typographic hierarchy, and responsive CTA behavior.",
      "Added locale-aware weekday/date formatting derived from UI language (`de-DE`/`en-US`) and applied a shadow-free gradient + border visual system.",
    ],
  },
  {
    id: "daily-text-callout-editorial-openai-color-restyle",
    timestamp: "2026-03-01T20:39:06+01:00",
    title:
      "Daily text callout redesigned with editorial layout and fresh color style",
    summary:
      "The `Ein Text für dich …` daily text callout now uses an editorial text-details-inspired structure with a modern color-forward treatment.",
    userHighlights: [
      "Heading and description now follow a cleaner magazine-style hierarchy.",
      "The card now uses soft atmospheric color gradients and subtle depth.",
      "The CTA button is now a stronger rounded accent action while keeping the same behavior.",
    ],
    technicalNotes: [
      "Restyled `DailyPracticeCallout` container with layered radial gradients, soft shadow, and decorative blur shapes.",
      "Updated callout typography, divider rhythm, and CTA styling in `reeprise/index.tsx` without changing translations or business logic.",
    ],
  },
  {
    id: "speech-timestamps-migration-compat-safety",
    timestamp: "2026-03-01T20:36:29+01:00",
    title: "Added migration safety guard for speech timestamp columns",
    summary:
      "Database startup is now more resilient when local migration history and schema drift differ for speech timestamp fields.",
    userHighlights: [
      "Reduced risk of local crashes caused by mixed migration states.",
      "Story generation setup is more stable after branch switching.",
      "No visible UI behavior changes.",
    ],
    technicalNotes: [
      "Added an idempotent compatibility migration that ensures `speech_timestamps` exists on `reeprise_generatedtext` and `reeprise_chapter` in SQLite environments.",
      "Migration checks actual table columns first and only applies `ALTER TABLE` when a column is missing.",
    ],
  },
  {
    id: "text-details-audio-readalong-highlight-rollback",
    timestamp: "2026-03-01T20:31:36+01:00",
    title: "Audio read-along highlighting was rolled back",
    summary:
      "The recently added audio-synced text highlighting on text details was removed again.",
    userHighlights: [
      "Text no longer auto-highlights during audio playback.",
      "Audio playback and chapter-specific audio selection continue to work as before.",
      "Rollback was applied to avoid shipping low-quality highlighting behavior.",
    ],
    technicalNotes: [
      "Removed frontend audio-time-driven text highlight wiring and related highlight computation.",
      "Removed backend timestamp-generation/storage plumbing introduced for audio word highlighting.",
    ],
  },
  {
    id: "chapter-specific-audio-playback-in-text-details",
    timestamp: "2026-03-01T20:18:24+01:00",
    title: "Audio playback now follows the active chapter",
    summary:
      "When you generate and open a new chapter from selected vocabulary, the audio player now loads that chapter’s own speech instead of replaying the first/base text audio.",
    userHighlights: [
      "Chapter audio now matches the chapter you are currently viewing.",
      "Switching chapter or opening another text no longer reuses stale audio playback state.",
      "Pressing play requests speech generation for the current chapter context.",
    ],
    technicalNotes: [
      "Added chapter-aware speech support in backend: `Chapter` now stores `speech`, text-to-speech accepts `chapter_number`, and streaming endpoint reads `?chapter=<counter>`.",
      "Updated text-details player to build chapter-specific audio URLs, request speech with chapter context, and reset player state when text/chapter changes.",
    ],
  },
  {
    id: "text-details-chapter-buttons-moved-into-text-column-and-scaled-down",
    timestamp: "2026-03-01T20:09:25+01:00",
    title: "Chapter buttons moved into text column and resized smaller",
    summary:
      "Previous/next chapter controls now sit directly in the main text column and use a smaller visual footprint for a cleaner article layout.",
    userHighlights: [
      "Vorheriges/Nächstes Kapitel now appear at the top of the main text column.",
      "Buttons are smaller and less dominant while still easy to tap/click.",
      "Right sidebar no longer carries its own separate chapter button.",
    ],
    technicalNotes: [
      "Removed standalone mobile top-row and sidebar chapter-button placements; added shared button row above the text/audio area.",
      "Reduced chapter button typography, spacing, and icon gaps in `LastChapterNavigationTile` and `NextChapterNavigationTile`.",
    ],
  },
  {
    id: "text-details-chapter-buttons-top-padding",
    timestamp: "2026-03-01T20:08:32+01:00",
    title: "Added top padding to previous/next chapter buttons",
    summary:
      "Both chapter navigation controls now include additional top padding for more balanced vertical spacing.",
    userHighlights: [
      "Vorheriges Kapitel button now has extra top breathing room.",
      "Nächstes Kapitel button now matches the same top spacing.",
      "Applied consistently to active and disabled states.",
    ],
    technicalNotes: [
      "Updated shared chapter button class sets in `LastChapterNavigationTile` and `NextChapterNavigationTile` to include top padding.",
      "No behavior, routing, or translation changes.",
    ],
  },
  {
    id: "text-details-next-chapter-to-vertiefen-spacing",
    timestamp: "2026-03-01T20:08:00+01:00",
    title: "Added spacing between Next Chapter and Vertiefen on desktop",
    summary:
      "The desktop layout now includes clear vertical spacing between the `Nächstes Kapitel` control and the `Vertiefen` section.",
    userHighlights: [
      "The two sections no longer touch visually.",
      "Desktop right-column rhythm is cleaner and easier to scan.",
      "No behavior changes to chapter navigation or helper panel.",
    ],
    technicalNotes: [
      "Added desktop bottom margin on the `NextChapterNavigationTile` wrapper before `LanguageHelperPanel` in generated text details route.",
      "No API, logic, or translation changes.",
    ],
  },
  {
    id: "text-details-vertiefen-collapse-empty-chat-gap",
    timestamp: "2026-03-01T19:54:31+01:00",
    title: "Vertiefen no longer leaves a large empty gap before first message",
    summary:
      "When the helper chat is empty, the intermediate conversation area now collapses so the input sits directly under the Vertiefen title.",
    userHighlights: [
      "Reduced vertical whitespace between `Vertiefen` and the input field when no messages exist.",
      "Conversation area appears only once there are messages or loading state.",
      "No change to ask/send behavior.",
    ],
    technicalNotes: [
      "Added conditional rendering for the message rail in `LanguageHelperPanel` based on message/loading presence.",
      "Retained existing message, action, and loading UI once conversation content exists.",
    ],
  },
  {
    id: "text-details-vertiefen-send-button-alignment-fix",
    timestamp: "2026-03-01T19:53:32+01:00",
    title: "Vertiefen send button alignment refined",
    summary:
      "The `Senden` control in Vertiefen was adjusted for cleaner pill geometry and centered label alignment.",
    userHighlights: [
      "Send button shape now appears visually balanced within the input row.",
      "Label is centered more consistently inside the rounded control.",
      "No behavior changes to sending messages.",
    ],
    technicalNotes: [
      "Adjusted helper send-button dimensions from fixed width to min-width plus horizontal padding and explicit line-height normalization.",
      "Kept existing interaction and disabled-state behavior.",
    ],
  },
  {
    id: "text-details-vertiefen-remove-all-divider-lines",
    timestamp: "2026-03-01T19:52:51+01:00",
    title: "Vertiefen section divider lines removed",
    summary:
      "All horizontal divider lines were removed from the Vertiefen helper section for a cleaner, lighter layout.",
    userHighlights: [
      "No horizontal separator lines remain in the Vertiefen panel.",
      "The section now appears visually cleaner and less cluttered.",
      "Helper behavior and messaging remain unchanged.",
    ],
    technicalNotes: [
      "Removed remaining border styling from the helper message rail wrapper in `LanguageHelperPanel`.",
      "No logic, API, or translation changes.",
    ],
  },
  {
    id: "text-details-vertiefen-single-divider-line",
    timestamp: "2026-03-01T19:52:25+01:00",
    title: "Vertiefen section now uses a single divider line",
    summary:
      "The helper section divider was simplified from two horizontal lines to one for a cleaner editorial layout.",
    userHighlights: [
      "Removed one of the two horizontal lines in `Vertiefen`.",
      "Section now feels less busy while preserving structure.",
      "All helper interactions remain unchanged.",
    ],
    technicalNotes: [
      "Changed message rail container from `border-y` to `border-t` in `LanguageHelperPanel`.",
      "No behavioral or API changes.",
    ],
  },
  {
    id: "text-details-vertiefen-editorial-restyle",
    timestamp: "2026-03-01T19:50:09+01:00",
    title: "Vertiefen section now follows the editorial text-details style",
    summary:
      "The `Vertiefen` helper panel on text details now uses the same newspaper-like visual language as the surrounding layout.",
    userHighlights: [
      "Panel heading now uses compact editorial metadata styling.",
      "Message area now sits inside subtle divider rails for cleaner article rhythm.",
      "Input and send controls are now visually aligned with the outlined pill/button style used across text details.",
    ],
    technicalNotes: [
      "Restyled `LanguageHelperPanel` container, message bubbles, loading state, confirmation card typography, and input action controls.",
      "No helper behavior, API contract, or translation key changes.",
    ],
  },
  {
    id: "text-details-vocabulary-card-editorial-restyle",
    timestamp: "2026-03-01T19:49:04+01:00",
    title: "Text-details vocabulary cards now match editorial page styling",
    summary:
      "Vocabulary cards on text details now use the same newspaper-like visual language as the rest of the page, with quieter surfaces, serif hierarchy, and subtle controls.",
    userHighlights: [
      "Vocabulary cards now appear as clean white editorial panels instead of heavy gray blocks.",
      "Sentence and grammar areas use thin divider sections for better article-like rhythm.",
      "More grammar and edit controls now match the compact outlined style used across text details.",
    ],
    technicalNotes: [
      "Restyled `VocabularyItem` shell, sentence/grammar sections, grammar chips, and control buttons in the generated-text details route.",
      "No behavior, data, or translation changes.",
    ],
  },
  {
    id: "text-details-generate-new-text-cta-editorial-restyle",
    timestamp: "2026-03-01T19:47:05+01:00",
    title: "Generate-new-text action now matches editorial text-details style",
    summary:
      "The `Neuen Text aus Vokabeln generieren` action on text details now uses the same compact, newspaper-like visual language as the rest of the page.",
    userHighlights: [
      "Desktop action is now presented in a subtle bordered section with a compact pill button.",
      "Mobile sticky action now aligns with content gutters and uses the same editorial button styling.",
      "Action behavior is unchanged; only visual design was updated.",
    ],
    technicalNotes: [
      "Restyled both desktop and mobile `btn_Generate_new_Text` surfaces in generated text details route to a border-based, uppercase tracked editorial CTA pattern.",
      "No translation, backend, or routing logic changes.",
    ],
  },
  {
    id: "text-details-mobile-chapter-nav-side-padding",
    timestamp: "2026-03-01T19:46:14+01:00",
    title: "Mobile chapter navigation now aligns with page side gutters",
    summary:
      "The mobile row for `Vorheriges Kapitel` and `Nächstes Kapitel` now includes side padding so controls no longer sit flush against the viewport edges.",
    userHighlights: [
      "Chapter navigation buttons now align with the same horizontal gutters as text content.",
      "Touch targets keep the same behavior but feel better placed on small screens.",
      "Visual rhythm between nav controls and article body is now consistent.",
    ],
    technicalNotes: [
      "Added horizontal padding to the mobile-only chapter navigation wrapper in generated text details route.",
      "No behavior, routing, or state changes.",
    ],
  },
  {
    id: "text-details-chapter-nav-editorial-restyle",
    timestamp: "2026-03-01T19:45:03+01:00",
    title: "Chapter navigation now matches text-details editorial UI",
    summary:
      "The `Vorheriges Kapitel` and `Nächstes Kapitel` controls on text details now use the same compact, border-based visual language as the rest of the page.",
    userHighlights: [
      "Previous/next chapter controls now appear as subtle rounded pills instead of plain text rows.",
      "Active and disabled states are clearer and more consistent with surrounding metadata controls.",
      "The chapter navigation feels integrated with the article-like layout on mobile and desktop.",
    ],
    technicalNotes: [
      "Restyled `LastChapterNavigationTile` and `NextChapterNavigationTile` to use consistent compact border-pill classes and uppercase tracking.",
      "No behavior change to routing/search params; update is visual only.",
    ],
  },
  {
    id: "text-details-remove-published-label",
    timestamp: "2026-03-01T19:43:46+01:00",
    title: "Text details date row now hides the Published label",
    summary:
      "The text-details metadata row now shows only the date and time, without the extra `Published/Veröffentlicht` label.",
    userHighlights: [
      "Removed the `Veröffentlicht`/`Published` caption above text date metadata.",
      "Date and time remain visible in the same position under the title.",
      "The header area now looks cleaner and less repetitive.",
    ],
    technicalNotes: [
      "Removed rendering of `text_details_published_label` in `GeneratedTextDetailsTextPane` while keeping existing locale-aware date/time formatting.",
      "No backend or API changes.",
    ],
  },
  {
    id: "generated-text-strip-title-story-for-visual-and-audio",
    timestamp: "2026-03-01T19:43:06+01:00",
    title:
      "Generated texts now remove Title/Story wrappers before display and speech",
    summary:
      "Generated text content now strips leading `Title`/`Story` wrapper labels at save-time so the reading view and audio playback contain only the actual story body.",
    userHighlights: [
      "Text details no longer shows raw `Title:` or `Story:` wrapper labels.",
      "Audio playback no longer reads `Title` or `Story` out loud before the real text.",
      "Applies to plain labeled outputs in addition to JSON-style model responses.",
    ],
    technicalNotes: [
      "Backend model-output extraction now handles plain `Title:` + `Story:` formats and returns clean story body plus parsed title.",
      "Completion flow now persists cleaned `chatgpt_output` after wrapper removal and includes regression test coverage for plain labeled payloads.",
    ],
  },
  {
    id: "text-details-audio-timeline-knob",
    timestamp: "2026-03-01T19:41:28+01:00",
    title: "Text details audio timeline now has a draggable knob",
    summary:
      "The audio progress line on text details now includes a visible circular handle so users can scrub more precisely without changing the existing player layout.",
    userHighlights: [
      "A small circle now shows the current playback position on the timeline.",
      "You can drag the circle to replay or skip to exact moments more easily.",
      "The rest of the audio player design stays the same.",
    ],
    technicalNotes: [
      "Converted timeline wrapper to a relative track container and added a positioned handle tied to playback progress percentage.",
      "Wired handle mouse/touch start events into existing seek/scrub logic while preserving click-to-seek behavior on the line.",
    ],
  },
  {
    id: "text-details-audio-progress-line-scrub",
    timestamp: "2026-03-01T19:39:39+01:00",
    title: "Text details audio progress line is now interactive",
    summary:
      "The audio progress line on text details now supports click and drag scrubbing so listeners can quickly replay or skip sections without changing the player layout.",
    userHighlights: [
      "Click anywhere on the progress line to jump to that moment in the audio.",
      "Drag across the line to scrub backward or forward like modern streaming players.",
      "The existing compact editorial audio design stays unchanged.",
    ],
    technicalNotes: [
      "Added timeline seek handling based on pointer position against the rendered progress line bounds.",
      "Implemented mouse and touch scrubbing listeners that update `audio.currentTime` and keep UI progress state in sync.",
    ],
  },
  {
    id: "text-details-audio-editorial-player-layout",
    timestamp: "2026-03-01T18:11:52+01:00",
    title: "Text details audio player now uses a compact editorial layout",
    summary:
      "The text-details audio section has been redesigned from a large card into a slimmer, article-style metadata row for a cleaner reading experience.",
    userHighlights: [
      "Play controls now appear in a compact row that fits better with article typography.",
      "Playback now shows a subtle time readout and slim progress line while listening.",
      "Audio speed can be toggled inline without opening additional UI.",
    ],
    technicalNotes: [
      "Replaced oversized audio container/button styles with border-y editorial meta row styling in generated text details route.",
      "Added lightweight playback metadata state (`currentTime`, `duration`, `playbackRate`) and bound it to audio element events for time/progress/speed display.",
    ],
  },
  {
    id: "generated-title-headline-normalization-and-topic-echo-guard",
    timestamp: "2026-03-01T18:01:17+01:00",
    title:
      "Generated text titles now start uppercase and avoid topic-only headers",
    summary:
      "Generated text headers are now enforced as headline-style titles: they always start uppercase and are no longer allowed to just repeat the typed topic.",
    userHighlights: [
      "Generated titles now consistently begin with an uppercase letter.",
      "If the model returns a title that only mirrors your topic, the system rewrites it into a content-based title.",
      "Text headers better reflect the actual generated story content.",
    ],
    technicalNotes: [
      "Backend completion flow now checks title/topic similarity and treats exact topic echoes as invalid final titles.",
      "When needed, backend requests a story-based AI title and falls back to derived content titles, then applies headline normalization before persisting `topic`.",
    ],
  },
  {
    id: "ui-language-date-time-and-brand-copy-consistency",
    timestamp: "2026-03-01T17:29:29+01:00",
    title: "UI language now controls generated-text date/time formatting",
    summary:
      "Generated-text date/time displays now follow the app UI language, and the language-switch subtitle copy is fully translated.",
    userHighlights: [
      "When app UI is German, generated-text date/time metadata now formats in German style.",
      "Text-details publication metadata also follows app UI language selection.",
      "The `with Uxhulu` subtitle in language switcher is now translatable.",
    ],
    technicalNotes: [
      "Extended `useTranslation` return payload with current UI language and used it to map locale (`de-DE`/`en-US`) in generated-text surfaces.",
      "Replaced hardcoded `with Uxhulu` in app route with translation key `language_switch_with_brand`.",
    ],
  },
  {
    id: "generated-title-vs-prompt-separation",
    timestamp: "2026-03-01T17:23:27+01:00",
    title:
      "Text headers now use generated titles while prompt preview stays user-typed",
    summary:
      "Text card headers and prompt previews are now separated: headers use generated titles, while preview lines keep the original typed prompt.",
    userHighlights: [
      "If you type `rose` as topic, the preview can keep `rose` while header shows a generated title.",
      "Header no longer needs to reuse prompt text when no explicit title was returned.",
      "The same prompt preview behavior is available on text details.",
    ],
    technicalNotes: [
      "Backend now always derives a title from story content when no explicit title exists and persists it as `topic`.",
      "Frontend caches original create-form topic per generated text id and uses that cache for prompt preview lines.",
    ],
  },
  {
    id: "text-details-article-meta-date-time",
    timestamp: "2026-03-01T17:20:55+01:00",
    title: "Text details now shows article-style publication date and time",
    summary:
      "The text details header now includes an article-style publication metadata row with generation date and time.",
    userHighlights: [
      "Date and time now appear directly below the text title.",
      "Presentation uses a subtle editorial style to support an article-like reading vibe.",
      "Works for existing and new generated texts using stored `created_at`.",
    ],
    technicalNotes: [
      "Added formatted publication metadata block in `GeneratedTextDetailsTextPane` with locale-aware date/time formatting.",
      "Added translation key `text_details_published_label`.",
    ],
  },
  {
    id: "prompt-action-label-discreet",
    timestamp: "2026-03-01T17:19:37+01:00",
    title: "Prompt action label is now more discreet",
    summary:
      "The prompt expansion action now uses a shorter, less intrusive label.",
    userHighlights: [
      "Changed button wording from `Vollen Prompt` to `Mehr` (EN: `More`).",
      "This keeps the UI calmer while retaining access to full prompt content.",
      "No behavior changes to the full prompt modal.",
    ],
    technicalNotes: [
      "Updated translation key `text_card_open_full_prompt` only.",
      "No layout or backend changes.",
    ],
  },
  {
    id: "text-details-topic-preview-and-full-prompt",
    timestamp: "2026-03-01T17:18:51+01:00",
    title: "Text details now shows topic preview with full prompt option",
    summary:
      "The text details header now includes a compact topic preview line and lets you open the complete prompt when truncated.",
    userHighlights: [
      "Topic preview is now visible in text details below the header.",
      "Long topics are shortened to four words with `...` for readability.",
      "You can open a modal to read the full prompt text.",
    ],
    technicalNotes: [
      "Added topic-preview row and full-prompt modal behavior to `GeneratedTextDetailsTextPane`.",
      "Reused existing prompt action translation keys from text-card prompt UI.",
    ],
  },
  {
    id: "text-card-header-no-story-sentence-fallback",
    timestamp: "2026-03-01T17:17:28+01:00",
    title: "Text card header no longer falls back to first story sentence",
    summary:
      "The text card header now uses only actual title sources and no longer derives a heading from the story body.",
    userHighlights: [
      "First line/sentence of story text no longer appears in the card header.",
      "Header remains reserved for title content.",
      "Topic preview behavior remains unchanged.",
    ],
    technicalNotes: [
      "Removed story-based title derivation fallback in `ReepriseTextCard`.",
      "Header resolution now uses parsed title only, then existing fallback behavior.",
    ],
  },
  {
    id: "text-card-open-full-prompt-modal",
    timestamp: "2026-03-01T17:14:27+01:00",
    title: "Text cards now include an Open full prompt action",
    summary:
      "Long topic prompts can now be opened in full from the card via a dedicated action.",
    userHighlights: [
      "When a prompt is truncated in the topic line, you now get an `Open full prompt` button.",
      "Clicking it opens a modal with the complete prompt text.",
      "Works on both standard and podcast text cards.",
    ],
    technicalNotes: [
      "Added local prompt modal state to `ReepriseTextCard` and wired open actions where topic preview truncation is active.",
      "Introduced new translation keys for open/full/close prompt labels.",
    ],
  },
  {
    id: "text-card-topic-line-persistent",
    timestamp: "2026-03-01T17:13:01+01:00",
    title: "Text card topic line now stays visible after title resolves",
    summary:
      "The small topic/prompt line is now persistent and no longer disappears once the generated title appears.",
    userHighlights: [
      "Topic stays visible as secondary context at all times.",
      "Generated title still remains the primary header.",
      "Long topics continue to use 4-word ellipsis in the secondary line.",
    ],
    technicalNotes: [
      "Removed conditional hiding logic for topic line in `ReepriseTextCard`; topic now renders whenever present.",
      "No backend changes.",
    ],
  },
  {
    id: "text-card-topic-preview-word-truncation",
    timestamp: "2026-03-01T17:12:06+01:00",
    title: "Text card topic preview now truncates long prompts",
    summary:
      "Long topic prompts shown in the small secondary line are now shortened after four words and displayed with ellipsis.",
    userHighlights: [
      "Secondary topic text remains compact and readable for very long prompts.",
      "Prompt preview now shows first four words followed by `...` when needed.",
      "Full prompt is still available on hover via tooltip.",
    ],
    technicalNotes: [
      "Added shared topic-preview truncation helper in `ReepriseTextCard` and applied it to all secondary topic placements.",
      "No backend changes.",
    ],
  },
  {
    id: "text-card-stable-topic-placement-during-generation",
    timestamp: "2026-03-01T17:11:13+01:00",
    title: "Text cards keep prompt/topic in secondary line during generation",
    summary:
      "While a new text is still generating, the user prompt/topic now stays in the small secondary topic slot instead of temporarily appearing as the main header.",
    userHighlights: [
      "No visual jump from header to secondary line when the generated title appears.",
      "Header now shows a neutral `Generating title…` placeholder until a real title is available.",
      "Topic placement stays stable from first render.",
    ],
    technicalNotes: [
      "Adjusted card title-selection logic to use a generation placeholder when status is pending and no parsed/derived title exists.",
      "Added translation key `text_card_generating_title` for localized placeholder text.",
    ],
  },
  {
    id: "text-card-title-fallback-from-story",
    timestamp: "2026-03-01T17:08:47+01:00",
    title:
      "Text cards now derive a title from story when model title is absent",
    summary:
      "Newly generated texts now still show a strong header title even when the model output does not contain an explicit `title` field.",
    userHighlights: [
      "Cards no longer fall back to showing only the prompt as the main heading for new entries.",
      "The original prompt remains visible as secondary metadata when different from the displayed title.",
      "Title hierarchy remains consistent across standard and podcast cards.",
    ],
    technicalNotes: [
      "Added client-side title derivation from the first meaningful story lines as fallback after explicit title parsing.",
      "Display logic now uses: parsed model title -> derived story title -> prompt topic.",
    ],
  },
  {
    id: "text-card-ai-title-primary-prompt-secondary",
    timestamp: "2026-03-01T17:05:31+01:00",
    title: "Text cards now prioritize AI title over prompt",
    summary:
      "Generated text cards now show the AI-derived title as the main header and render the original user prompt/topic in a smaller secondary line.",
    userHighlights: [
      "Card header now emphasizes the generated title for clearer scanning.",
      "Your original prompt/topic is still visible but visually lighter and less dominant.",
      "Preview text stays visible under the new title hierarchy.",
    ],
    technicalNotes: [
      "Added title extraction from strict JSON, loose JSON-like payloads, and `Title:` lines directly in card rendering.",
      "Applied the same title/prompt hierarchy to both standard and podcast text-card layouts, including the tilted preview card.",
    ],
  },
  {
    id: "generated-text-json-wrapper-cleanup",
    timestamp: "2026-03-01T17:03:55+01:00",
    title: "Generated texts now strip JSON-like wrapper content",
    summary:
      "Texts that arrive in `title/story` JSON-like format are now normalized so only the story body is shown in the text view.",
    userHighlights: [
      'Raw payload fragments like `"title": ... "story": ...` no longer appear in generated text output.',
      "Existing affected texts also render cleaner in text details via frontend normalization.",
      "Title extraction still works when provided by the model.",
    ],
    technicalNotes: [
      "Backend completion now normalizes strict JSON, fenced JSON, and loose JSON-like key/value outputs before persisting `chatgpt_output`.",
      "Text-details stream normalization now extracts `story` from loose JSON-like payloads for resilient rendering.",
    ],
  },
  {
    id: "text-details-more-grammar-toggle-reliability",
    timestamp: "2026-03-01T16:58:35+01:00",
    title: "Text details: More grammar toggle now reacts reliably",
    summary:
      "The More grammar action on text-details vocabulary cards now toggles reliably and is only shown when extra grammar content exists.",
    userHighlights: [
      "The card no longer treats grammar-button clicks as jump-to-text clicks.",
      "More grammar appears only when there is additional grammar to expand.",
      "No-change clicks are avoided for entries that only have basic grammar fields.",
    ],
    technicalNotes: [
      "Added explicit click-control guards in the text-details vocabulary item to prevent parent row jump handling from interfering with control interactions.",
      "Conditionally render expanded grammar block and toggle based on actual advanced grammar availability.",
    ],
  },
  {
    id: "vocabulary-source-sentence-keeps-ending-punctuation",
    timestamp: "2026-03-01T16:55:19+01:00",
    title: "Vocabulary source sentence now keeps full ending punctuation",
    summary:
      "Saved source sentences now include terminal punctuation like `.`, `!`, or `?` when a word is added from text selection.",
    userHighlights: [
      "Source sentence lines now keep their full ending, including punctuation marks.",
      "This avoids clipped sentence context in vocabulary preview and detail cards.",
      "Existing sentence order and styling remain unchanged.",
    ],
    technicalNotes: [
      "Backend `add_vocabulary` sentence extraction now extends to terminal punctuation and trailing closing quote/bracket characters when needed.",
      "Added regression test to ensure `sentence_end_index` selections still persist the full sentence ending.",
    ],
  },
  {
    id: "text-details-vocab-card-layout-aligned",
    timestamp: "2026-03-01T16:52:29+01:00",
    title: "Text details vocabulary card now matches preview layout",
    summary:
      "The vocabulary card on text details now follows the same visual sectioning as the preview card for sentence and grammar content.",
    userHighlights: [
      "Sentence lines are shown in their own card section with the same spacing and typography pattern.",
      "Grammar is shown in a separate card section with consistent More grammar placement and styling.",
      "Existing behavior (prefix removal, source-word bolding, source-first order) remains intact.",
    ],
    technicalNotes: [
      "Restructured `VocabularyItem` in text details to use distinct sentence and grammar containers aligned with preview-card design language.",
      "No backend/API changes.",
    ],
  },
  {
    id: "srs-includes-manual-vocabulary",
    timestamp: "2026-03-01T16:49:02+01:00",
    title: "SRS trainer and stage cards now include manual vocabulary",
    summary:
      "Manual vocabulary is now counted in SRS trainer selection and stage cards, so language-space practice reflects your full vocabulary.",
    userHighlights: [
      "Vocabulary preview no longer says there is nothing to practice when manual words exist.",
      "Stage cards (Due/New/Learning/Reviewing/Mastered) now reflect manual words in the selected language space.",
      "Behavior is consistent with the vocabulary list users already see.",
    ],
    technicalNotes: [
      "Backend recent-vocabulary helper now includes manual-origin words in addition to usage-linked/photo-import words.",
      "Impacts both `/vocabulary/top_fifteen_priority/` and `/vocabulary/stage_counts/` via shared filtering.",
    ],
  },
  {
    id: "preview-sentences-below-self-rating",
    timestamp: "2026-03-01T16:39:40+01:00",
    title: "Preview card sentences moved below self-rating buttons",
    summary:
      "On the vocabulary preview card, source/translated sentence lines now appear under the Yes/Almost/No self-rating controls.",
    userHighlights: [
      "Sentence section is now positioned below the `Did you know the word?` button row.",
      "Sentence content and highlighting behavior remain unchanged.",
      "Grammar section still stays below the self-rating section.",
    ],
    technicalNotes: [
      "Reordered sentence block placement in `VocabularyPreviewCard` without API or schema changes.",
    ],
  },
  {
    id: "preview-vocabulary-pending-translation-refetch",
    timestamp: "2026-03-01T16:37:00+01:00",
    title:
      "Preview vocabulary now refreshes pending translations automatically",
    summary:
      "Newly added words in vocabulary preview no longer get stuck on the temporary generated placeholder.",
    userHighlights: [
      "If a word is still generating translation, preview auto-refreshes until final text arrives.",
      "Preview behavior now aligns with text-details vocabulary updates.",
      "The temporary `currently beeing generated` text no longer lingers after translation completes.",
    ],
    technicalNotes: [
      "Added conditional polling to the preview trainer vocabulary query while any entry is pending or still carries generation-placeholder text.",
      "No backend/API changes.",
    ],
  },
  {
    id: "vocabulary-sentence-left-align-no-source-bg",
    timestamp: "2026-03-01T16:33:23+01:00",
    title:
      "Vocabulary sentences are left-aligned and source background removed",
    summary:
      "Sentence lines are now explicitly left-aligned and the shaded background from the first sentence line has been removed.",
    userHighlights: [
      "Source sentence line no longer uses a gray background block.",
      "Both sentence lines are consistently left-aligned.",
      "Content and ordering of sentence lines stay the same.",
    ],
    technicalNotes: [
      "Updated sentence line classes in preview, trainer, generated-text card, and vocabulary progress (desktop + mobile).",
      "No backend or API changes.",
    ],
  },
  {
    id: "vocabulary-source-sentence-word-highlight",
    timestamp: "2026-03-01T16:31:47+01:00",
    title: "Vocabulary word is now bold in source sentence",
    summary:
      "The vocabulary word is now visually emphasized in the source sentence line to improve recognition while reviewing.",
    userHighlights: [
      "In the first sentence line (source sentence), the vocabulary word is bold.",
      "Highlighting appears consistently in preview, trainer, generated-text vocabulary cards, and vocabulary progress.",
      "If source sentence is missing, fallback text remains unchanged.",
    ],
    technicalNotes: [
      "Added a shared sentence-highlighting utility and applied it to source sentence rendering across vocabulary surfaces.",
      "No backend/API changes.",
    ],
  },
  {
    id: "vocabulary-source-sentence-first-order",
    timestamp: "2026-03-01T16:30:05+01:00",
    title: "Vocabulary sentence section now always starts with source sentence",
    summary:
      "Sentence sections now use fixed order again: source sentence (or fallback) first, translated sentence second.",
    userHighlights: [
      "The source sentence slot is always shown first in the section.",
      "If source sentence is missing, `Kein Ausgangssatz verfügbar.` appears first.",
      "Translated sentence still appears as the second line when available.",
    ],
    technicalNotes: [
      "Replaced first-available sentence logic with strict source-first ordering across all vocabulary surfaces.",
      "No backend or API changes.",
    ],
  },
  {
    id: "vocabulary-sentence-labels-removed",
    timestamp: "2026-03-01T16:28:39+01:00",
    title: "Vocabulary cards now show sentence text without prefixes",
    summary:
      "Sentence lines no longer display `Ausgangssatz:` or `Übersetzter Satz:` prefixes; only the sentence text is shown.",
    userHighlights: [
      "Cleaner sentence presentation across vocabulary cards and progress views.",
      "Primary and secondary sentence ordering remains unchanged.",
      "Fallback text still appears when no sentence is available.",
    ],
    technicalNotes: [
      "Removed sentence label rendering while keeping sentence-priority logic.",
      "Applied in preview, trainer, generated-text vocabulary card, and vocabulary progress (desktop + mobile).",
    ],
  },
  {
    id: "vocabulary-preview-grammar-below-self-rating",
    timestamp: "2026-03-01T16:26:33+01:00",
    title: "Vocabulary preview: grammar moved below self-rating actions",
    summary:
      "In the preview trainer card, the grammar block now appears below the `Did you know the word?` Yes/Almost/No actions.",
    userHighlights: [
      "Self-rating buttons now appear before grammar details in the preview flow.",
      "Sentence context remains visible above, so reading context still comes first.",
      "Grammar expand/collapse behavior is unchanged; only placement changed.",
    ],
    technicalNotes: [
      "Reordered the preview card layout so the grammar section renders after the self-rating action section.",
      "No backend or API changes.",
    ],
  },
  {
    id: "vocabulary-primary-sentence-priority",
    timestamp: "2026-03-01T16:25:10+01:00",
    title: "Vocabulary cards prioritize the primary learning sentence",
    summary:
      "Sentence context now highlights the first available real sentence as primary, so placeholder text does not appear before useful content.",
    userHighlights: [
      "When `Ausgangssatz` is unavailable but a sentence exists in `Übersetzter Satz`, the real sentence is shown first.",
      "The primary sentence is visually emphasized to reflect higher learning importance.",
      "The unavailable placeholder is only shown when no sentence exists at all.",
    ],
    technicalNotes: [
      "Introduced primary/secondary sentence ordering logic in preview, trainer, generated-text vocabulary cards, and vocabulary progress (desktop + mobile).",
      "Applied stronger styling to the primary sentence row and kept existing grammar separation behavior.",
    ],
  },
  {
    id: "vocabulary-translated-sentence-double-label-fix",
    timestamp: "2026-03-01T16:20:50+01:00",
    title: "Vocabulary cards: duplicate translated-sentence label removed",
    summary:
      "Translated sentence values no longer show duplicated labels like `Übersetzter Satz: Satzübersetzung: ...`.",
    userHighlights: [
      "Translated sentence lines now render once with a single label.",
      "Legacy entries that stored `Satzübersetzung:` inside the value are cleaned automatically in the UI.",
      "No data migration required to see the fix.",
    ],
    technicalNotes: [
      "Added normalization that strips known translated-sentence prefixes (`translated sentence`, `übersetzter satz`, `satzübersetzung`) from the value.",
      "Applied to both structured `translation_details.translated_sentence` and legacy newline fallback parsing.",
    ],
  },
  {
    id: "vocabulary-more-grammar-plain-toggle",
    timestamp: "2026-03-01T16:19:58+01:00",
    title: "Vocabulary grammar toggle simplified to plain text",
    summary:
      "The More grammar control now uses a plain text button without chevron or outline styling.",
    userHighlights: [
      "Removed chevron icons from the grammar expand/collapse control.",
      "Removed outline/border styling from the same control for a cleaner look.",
      "Grammar content order and expand/collapse behavior remain unchanged.",
    ],
    technicalNotes: [
      "Dropped `ChevronDownIcon` and `ChevronUpIcon` imports/usages in all vocabulary surfaces.",
      "Adjusted button classes to plain text styling while preserving toggle logic.",
    ],
  },
  {
    id: "vocabulary-more-grammar-chevron",
    timestamp: "2026-03-01T16:18:29+01:00",
    title: "Vocabulary grammar toggle now shows chevron state",
    summary:
      "The More grammar toggle now includes a chevron icon that clearly indicates expand and collapse state.",
    userHighlights: [
      "A down chevron appears with `More grammar`; an up chevron appears with `Less grammar`.",
      "The icon behavior is consistent across preview, trainer, generated-text cards, and vocabulary progress.",
      "Existing field order and grammar content remain unchanged.",
    ],
    technicalNotes: [
      "Added Heroicons `ChevronDownIcon`/`ChevronUpIcon` to all More grammar buttons.",
      "No backend or API changes; this is a frontend affordance update.",
    ],
  },
  {
    id: "vocabulary-more-grammar-collapsed-reset",
    timestamp: "2026-03-01T16:15:26+01:00",
    title: "Vocabulary cards: More grammar reset to collapsed state",
    summary:
      "More grammar now starts collapsed again across vocabulary surfaces while preserving the new sentence-first content order.",
    userHighlights: [
      "Expanded grammar no longer stays open from older saved preference.",
      "Source sentence and translated sentence remain above grammar fields.",
      "You can still open and close advanced grammar manually with the same button.",
    ],
    technicalNotes: [
      "Bumped the localStorage key for the grammar toggle from v1 to v2 to reset persisted expanded state.",
      "No API or schema changes; this is a frontend preference reset only.",
    ],
  },
  {
    id: "vocabulary-sentences-before-grammar",
    timestamp: "2026-03-01T16:13:53+01:00",
    title: "Vocabulary cards: sentence context moved above grammar",
    summary:
      "Vocabulary cards now show source and translated sentence context before grammar fields like part of speech and tense.",
    userHighlights: [
      "You now see `Ausgangssatz` and (if available) `Übersetzter Satz` before the grammar section.",
      "The fallback `Kein Ausgangssatz verfügbar.` remains in place when no source sentence exists.",
      "Order is consistent across preview, trainer, generated-text cards, and vocabulary progress.",
    ],
    technicalNotes: [
      "Reordered sentence and grammar blocks in all vocabulary UI surfaces without changing data contracts.",
      "More grammar toggle behavior is unchanged; only content order was updated.",
    ],
  },
  {
    id: "vocabulary-more-grammar-toggle-bottom",
    timestamp: "2026-03-01T16:11:26+01:00",
    title: "Vocabulary cards: More grammar moved to the bottom",
    summary:
      "The More grammar button is now placed at the bottom of vocabulary detail blocks for a clearer expand/collapse flow.",
    userHighlights: [
      "More grammar/Less grammar now appears at the bottom of vocabulary detail sections instead of near the top.",
      "Expanded grammar details keep the same content and behavior; only placement changed for readability.",
      "The new placement is consistent across preview, trainer, generated-text cards, and vocabulary progress.",
    ],
    technicalNotes: [
      "Reordered the grammar toggle markup to render after grammar and sentence details in all vocabulary surfaces.",
      "No API or data changes; this is a frontend layout adjustment only.",
    ],
  },
  {
    id: "source-sentence-unavailable-copy",
    timestamp: "2026-03-01T16:07:21+01:00",
    title: "Vocabulary source sentence fallback copy refined",
    summary:
      "The source sentence fallback label now uses neutral wording and no longer says “missing”.",
    userHighlights: [
      "Cards and lists now show “No source sentence available.” when no source sentence exists.",
      "The fallback remains clearly labeled as Source sentence for learning context.",
      "Wording is now consistent across preview, trainer, progress, and text-detail vocabulary cards.",
    ],
    technicalNotes: [
      "Renamed the translation key to `vocabulary_source_sentence_unavailable` and updated all frontend references.",
      "No behavioral change besides copy/key naming; fallback display logic remains the same.",
    ],
  },
  {
    id: "vocabulary-progress-grammar-separation",
    timestamp: "2026-03-01T16:01:30+01:00",
    title: "Vocabulary progress: separated grammar layout added",
    summary:
      "The vocabulary progress page now uses the same separated translation/grammar structure and More grammar toggle as other vocabulary surfaces.",
    userHighlights: [
      "Progress table and mobile cards now show translation and grammar as separate readable values.",
      "Core grammar remains visible by default; advanced grammar is shown with `More grammar`.",
      "Source and translated example sentences are shown as separate labeled lines.",
    ],
    technicalNotes: [
      "Vocabulary progress rows now normalize `translation_details` and safely fall back to legacy newline-based translation strings.",
      "Integrated shared localStorage-backed `More grammar` toggle key for consistent behavior across surfaces.",
    ],
  },
  {
    id: "vocabulary-grammar-separated-ui",
    timestamp: "2026-03-01T15:58:08+01:00",
    title: "Vocabulary cards: grammar fields split with a More grammar toggle",
    summary:
      "Vocabulary surfaces now separate translation details into readable fields and add a shared More grammar toggle for advanced details.",
    userHighlights: [
      "Preview card, text-detail vocabulary card, and vocabulary list now show translation and grammar in separate blocks.",
      "Core grammar stays visible by default; advanced grammar opens via `More grammar`.",
      "The toggle preference is remembered locally, so the view stays consistent while learning.",
    ],
    technicalNotes: [
      "Added a shared frontend translation-details normalizer that reads structured payloads and safely falls back to legacy newline translation strings.",
      "Extended `VocabularyDto` with optional `translation_details` and migrated UI rendering in three vocabulary surfaces to separated fields.",
    ],
  },
  {
    id: "generated-text-enqueue-failure-handling",
    timestamp: "2026-03-01T15:28:57+01:00",
    title:
      "Generate Text: failed queue jobs no longer create silent empty cards",
    summary:
      "When text generation cannot be queued, the request now completes safely and marks the new text as failed instead of leaving a silent empty pending card.",
    userHighlights: [
      "Creating a text no longer crashes when the background queue is temporarily unavailable.",
      "Failed generations are now clearly marked as failed instead of looking stuck.",
      "You can retry immediately instead of guessing whether generation is still running.",
    ],
    technicalNotes: [
      "Backend wraps `complete_generated_text.apply_async(...)` in guarded error handling during `GeneratedText` creation.",
      "On enqueue failure, the text is persisted with `status=FAILED` and a user-visible fallback message instead of returning a server error.",
    ],
  },
  {
    id: "playwright-e2e-make-targets",
    timestamp: "2026-02-27T21:17:53Z",
    title: "E2E tests: make targets for headed/headless runs",
    summary:
      "Playwright runs are now available via make targets, with a headed mode for interactive debugging.",
    userHighlights: [
      "Use `make e2e` to run tests with a visible browser.",
      "Use `make e2e-headless` for fast CI-style runs.",
    ],
    technicalNotes: [
      "Makefile now exposes two Playwright targets for headed and headless execution.",
    ],
  },
  {
    id: "secret-signup-inactive-users",
    timestamp: "2026-02-27T20:14:04Z",
    title: "Secret signup: inactive accounts by default",
    summary:
      "A private signup page lets invited users create accounts that remain inactive until manually approved.",
    userHighlights: [
      "Simple username + password signup at `/secretsignup`.",
      "Clear success screen explains activation within 24 hours.",
      "No login until the account is manually activated.",
    ],
    technicalNotes: [
      "New API endpoint creates users with `is_active = false` and runs Django password validation.",
      "Signup does not log the user in; activation remains manual.",
    ],
  },
  {
    id: "command-bar-smart-menu",
    timestamp: "2026-02-27T19:31:00Z",
    title: "Command bar: smart menu for fast actions",
    summary:
      "The burger menu is now a smart command bar that understands natural-language requests and still keeps the full side nav available.",
    userHighlights: [
      "Type what you need (e.g., change language, switch model, start daily text) and confirm in one tap.",
      "Direct actions first: change settings or open the photo picker without hunting through screens.",
      "Side navigation stays visible below the command bar so nothing is lost.",
    ],
    technicalNotes: [
      "Command bar parses German/English intents and executes settings updates, daily text creation, and profile photo uploads.",
      "Action-specific confirmations replace generic “Apply” for clearer UX.",
      "Mobile uses a full-width sheet with the command bar pinned at the top.",
    ],
  },
  {
    id: "text-details-vocab-focus-highlight",
    timestamp: "2026-02-22T20:57:55Z",
    title: "Text details: highlight selected vocab and jump to words",
    summary:
      "Words chosen for “new text from vocabulary” are now highlighted in the text, and tapping a vocab card scrolls to that word with a short flash.",
    userHighlights: [
      "Selected vocab words are highlighted directly in the generated text, matching the click-to-highlight style.",
      "Tapping a vocab card scrolls the text to that word and briefly highlights it.",
      "Unselecting a word removes its highlight so you can see what will be used next.",
    ],
    technicalNotes: [
      "Detail view now passes selected vocab words to the text renderer for highlight indexing.",
      "Text span rendering adds data attributes per word and supports a timed flash highlight with smooth scroll.",
    ],
  },
  {
    id: "daily-text-on-demand",
    timestamp: "2026-02-01T15:10:00Z",
    title: "Daily text: on-demand card with stable vocab picks",
    summary:
      "A 3pm callout lets you create a daily text per language using your due vocab and last generator settings. The text appears as a normal card; no clutter if you skip it.",
    userHighlights: [
      "At 15:00 you see a daily-text button; tapping creates today’s text for the current language and shows it in the list.",
      "Uses the same word queue you’re struggling with; if nothing is due, you’re told why.",
      "No duplicate cards: if you’ve already created today’s daily text, the callout stays hidden.",
    ],
    technicalNotes: [
      "Backend endpoint `/generated_texts/daily-practice/` now reuses any existing daily text per user/language/day and triggers generation on click.",
      "Frontend callout is 3pm-gated, language-space aware, CSRF-protected, and invalidates the paginated text list after creation.",
      "Translations consolidated into `dailyText*` keys; no inline fallbacks.",
    ],
  },
  {
    id: "knowledge-style-adult-tone",
    timestamp: "2026-02-05T09:30:00Z",
    title: "Knowledge text style: adult, factual output at all levels",
    summary:
      "Knowledge texts now stay factual and adult in tone, with A0–A2 guidance to avoid childlike language while keeping sentences simple.",
    userHighlights: [
      "Pick the new “Knowledge” style to get fact-based texts about your topic, not fiction.",
      "Beginner levels stay simple but avoid babyish wording; short sentences use clear connectors and adult contexts.",
      "Higher levels remain concise and factual, with no speculative or fluffy phrasing.",
    ],
    technicalNotes: [
      "Backend Knowledge prompt adds adult tone and factual constraints, plus A0–A2-specific guidance: short, connected sentences, no cutesy/filler language, everyday adult examples.",
      "No schema changes; frontend already exposes the Knowledge type in the generator.",
    ],
  },
  {
    id: "knowledge-no-main-character",
    timestamp: "2026-02-05T10:00:00Z",
    title: "Knowledge texts: no main character, narratives keep yours",
    summary:
      "Knowledge texts now ignore main characters, while Story/Dialog/Crime/Podcast remember your last protagonist per language. Switching to Knowledge won’t wipe it.",
    userHighlights: [
      "Knowledge text type disables the main-character field and sends none.",
      "When you return to other types, your last chosen character is prefilled (per language).",
      "You can still clear it; Knowledge won’t overwrite your saved choice.",
    ],
    technicalNotes: [
      "Frontend: main-character input is disabled for Knowledge; submissions send an empty value; stored defaults preserve the last non-Knowledge character per language.",
      "Backend: Knowledge texts force an empty main_character before generation.",
    ],
  },
  {
    id: "in-app-language-helper-mvp",
    timestamp: "2026-02-20T11:00:00Z",
    title: "In-app helper: ask, answer, and add vocab",
    summary:
      "A lightweight helper inside text details answers questions and lets you add words and example sentences directly to your vocabulary.",
    userHighlights: [
      "Chat-style helper with a single input; Enter sends on desktop.",
      "Asks for quick confirmation before adding to vocabulary so you see exactly what will be saved.",
      "Answers in the learning language for sentence/translation requests; explanations stay in your mother tongue.",
    ],
    technicalNotes: [
      "Helper uses first-question context from the generated text plus the last 5 clicked words.",
      "Agent responses follow intent rules (sentence, pronunciation, register, grammar).",
      "Uxhulu voice prompt enforces a calm editorial tone with bilingual few-shot examples.",
      "Intent routing keeps sentence/translation replies in the learning language; other answers stay in mother tongue.",
      "Agent can infer the vocab word from the last assistant phrase when the user asks to add it.",
      "New vocab additions go through the standard translation pipeline to preserve multi-line analysis.",
    ],
  },
  {
    id: "trainer-similar-words-spelling",
    timestamp: "2026-02-15T10:26:27+01:00",
    title: "Trainer: similar words by spelling",
    summary:
      "Expanded trainer cards now show spelling-similar words from your current deck, so you can jump to near matches without leaving the session.",
    userHighlights: [
      "See up to 5 near-spelling words in the expanded trainer card under “Ähnliche Wörter”.",
      "Tap a suggestion to switch the active card instantly.",
      "Only uses your current batch and language; no duplicate exact matches.",
    ],
    technicalNotes: [
      "Client-side accent-insensitive Levenshtein on the current deck; dist ≤ 2, normalized score ≥ 0.6; top 5.",
      "UI: single tab “Schreibweise”, scrollable list, reason label “sehr ähnlich geschrieben”.",
    ],
  },
  {
    id: "multi-language-spaces-stable-queues",
    timestamp: "2026-01-30T10:00:00Z",
    title: "Language spaces: per-language defaults and stable queues",
    summary:
      "Each language now has its own generator defaults and trainer queue. Switching spaces or refreshing keeps your current word and form selections intact.",
    userHighlights: [
      "Generate Text remembers level, tense, type, word count, and main character separately per language.",
      "Vocabulary preview shows a stable queue per language—even after switching spaces or refreshing, the same first word stays until you act on it.",
      "Language switcher supports all languages with distinct accents and mobile-friendly swipe/tap navigation.",
    ],
    technicalNotes: [
      "Per-language form defaults stored in localStorage keyed by language; generator hydrates on space change.",
      "Vocabulary preview persists batch snapshots and restores them per language before API data to prevent reorder on refresh.",
      "Language space selection options driven from TEXT_LANGUAGES; mobile pill bar uses per-language accent styling.",
    ],
  },
  {
    id: "trainer-session-size-picker",
    timestamp: "2026-01-20T15:20:00Z",
    title: "Trainer sessions: choose your round size",
    summary:
      "You can now pick how many words are in a round (5–50) from the “continue?” prompt, without changing your due list.",
    userHighlights: [
      "Rounds stay a fixed size; when you finish, tap the menu to choose a different size for the next round.",
      "Words remain stable within a round—no mid-session swaps even if you change the size later.",
      "Settings persist locally, so your preferred round size sticks when you come back.",
    ],
    technicalNotes: [
      "Frontend adds a session-size menu on the bundle-done prompt, stores selection in localStorage, and reinitializes batches with the new size.",
      "Batching continues to use frozen IDs per round; only the slice size changes for subsequent rounds.",
    ],
  },
  {
    id: "trainer-5-word-batches",
    timestamp: "2026-01-20T12:30:00Z",
    title: "Trainer: 5-word sessions with clear breaks",
    summary:
      "The vocabulary trainer now serves due words in fixed sets of five, then asks if you want to continue—no more endless queues without a breakpoint.",
    userHighlights: [
      "You always see a stable set of five due words; new words don’t slip in mid-session.",
      "After five answers, you get a calm “continue?” prompt to start the next five.",
      "If you leave and return, the trainer restarts at the first five due words instead of resuming mid-stream.",
    ],
    technicalNotes: [
      "Frontend freezes a per-session batch (SESSION_SIZE=5), tracks answeredCount, and shows the bundle-done state only after all five are answered.",
      "Batch state resets cleanly on refresh or language filter changes; continuation pulls the next due words without altering the server queue.",
    ],
  },
  {
    id: "podcast-audio-two-voices",
    timestamp: "2026-01-08T15:50:00Z",
    title: "Podcasts: two voices, intro/outro, reliable playback",
    summary:
      "Podcast episodes now sound like real shows: two distinct voices with a short intro/outro, leveled audio, cleaner titles, and playback that works on desktop and mobile.",
    userHighlights: [
      "Two distinct voices alternate naturally with a brief podcast-style welcome and wrap-up.",
      "Titles are generated from the episode content—no generic greetings or quotes.",
      "Podcast cards play reliably via the new speech endpoint on web and mobile.",
    ],
    technicalNotes: [
      "Backend: multi-voice TTS with loudness normalization, silence gaps, intro/outro prompt tweaks, and a dedicated /speech action; serializer returns absolute media URLs.",
      "Frontend: podcast card handles audio more robustly with hidden native controls and error handling.",
      "Title generation: prompt and post-process to produce concise, content-based headlines instead of first-line greetings.",
    ],
  },
  {
    id: "podcast-text-type",
    timestamp: "2025-12-12T09:00:00Z",
    title: "Podcast text type with audio",
    summary:
      "You can now pick “Podcast” when generating content: a 2-speaker, ~1-minute script is written from your topic and runs through text-to-speech so you can listen back.",
    userHighlights: [
      "New “Podcast” option alongside Story/Dialog/Krimi in the generator.",
      "Script uses your topic and supplied words, keeps two voices, and stays concise (~1 minute).",
      "Audio is generated so you can replay the episode from its card.",
    ],
    technicalNotes: [
      "Backend adds PODCAST text type, tailored prompt, and podcast-friendly TTS instructions.",
      "Frontend exposes the new type in the generator; choices and migrations updated.",
    ],
  },
  {
    id: "vocab-progress-fast-counts",
    timestamp: "2025-12-11T22:10:00Z",
    title: "Vocabulary progress shows live counts instantly",
    summary:
      "Stage cards now load their counts directly from the server instead of slowly accruing as you scroll, so you see the real numbers upfront.",
    userHighlights: [
      "Fällig/Lernen/Review counts appear immediately without waiting for list pagination.",
      "Photo-import vocab still has its own tab, but is included in the main counts when you’re on “Alle”.",
    ],
    technicalNotes: [
      "Backend adds a `stage_counts` action aligned to trainer filters (language/origin/max streak/text age) and SRS delays to return counts in one request.",
      "Frontend uses the stage-counts hook for stage badges (client reduction as fallback) and applies the correct language filter even for photo imports.",
    ],
  },
  {
    id: "photo-vocab-highlight-coverage-4",
    timestamp: "2025-12-11T18:20:00Z",
    title: "Photo vocab import: sharper highlights, zero-drop fallback",
    summary:
      "Image preprocessing now boosts contrast/sharpness for faint marks, and the extractor is forced to return at least as many entries as highlights it counts.",
    userHighlights: [
      "Faint highlights are clearer to the model thanks to contrast and sharpness boosts.",
      "If the model is unsure, it still returns a low-confidence entry rather than skipping a highlight.",
      "One entry per distinct highlight remains—phrases stay intact, separate marks stay separate.",
    ],
    technicalNotes: [
      "Image pipeline adds autocontrast + mild contrast/sharpness enhancement before resize (2000px, LANCZOS).",
      "Prompt now requires the model to verify its entry count >= counted highlights and to over-include with low confidence if uncertain.",
    ],
  },
  {
    id: "photo-vocab-translation-grammar",
    timestamp: "2025-12-11T18:40:00Z",
    title: "Photo vocab translations: enforce grammatical sentences",
    summary:
      "Translations now ask the model to fix agreement/tense and avoid literal calques, with deterministic decoding to reduce odd phrasing.",
    userHighlights: [
      "Sentence translations aim to be grammatical and idiomatic instead of literal word-for-word copies.",
      "Lower randomness reduces quirky outputs when translating highlighted sentences.",
    ],
    technicalNotes: [
      "Translation prompt now explicitly repairs agreement/tense/prepositions while staying faithful to meaning.",
      "Temperature fixed at 0 and max_tokens capped for stable, deterministic outputs.",
    ],
  },
  {
    id: "photo-vocab-highlight-coverage-3",
    timestamp: "2025-12-11T15:10:00Z",
    title: "Photo vocab import: one entry per highlight, no merges or drops",
    summary:
      "Each distinct highlight now returns exactly one entry in reading order—even multi-word spans stay intact—with added safeguards against accidental omissions.",
    userHighlights: [
      "One entry per mark: if you highlight a phrase, it stays together; separate highlights stay separate.",
      "Zero-drop policy remains: faint or hard-to-read marks still show up with low confidence instead of vanishing.",
      "Higher max tokens in the vision reply avoid truncated lists on dense pages.",
    ],
    technicalNotes: [
      "Highlight mode no longer splits spans; it keeps the marked text as-is with a deterministic token_id and ordered results, and asks the model to count highlights then return at least that many entries.",
      "Prompt explicitly forbids merging distinct highlights and keeps the over-inclusion safety net; vision max_tokens at 2600.",
      "Image preprocessing continues to use high-res autocontrast pipeline for clearer marks.",
    ],
  },
  {
    id: "photo-vocab-highlight-articles",
    timestamp: "2025-12-11T17:05:00Z",
    title: "Photo vocab import: keep articles inside highlights",
    summary:
      "Highlighted phrases are kept verbatim—including articles and prepositions—so marks like “le rétroviseur” stay intact instead of being shortened.",
    userHighlights: [
      "Marked spans are returned exactly as highlighted, preserving articles/determiners when they’re underlined or colored.",
      "Still one entry per distinct highlight with zero-drop behavior on faint marks.",
    ],
    technicalNotes: [
      "Highlight prompt now explicitly preserves the full marked span (no head-noun shortening) while maintaining the count-and-include safeguard.",
      "No changes to image pipeline or token limits; applies only to extraction rules.",
    ],
  },
  {
    id: "photo-vocab-highlight-coverage-2",
    timestamp: "2025-12-11T12:30:00Z",
    title: "Photo vocab import: zero-drop for highlighted words",
    summary:
      "Highlighted-only imports now insist on returning every marked word, even tiny prepositions or faint strokes, and keep higher-resolution images for the model.",
    userHighlights: [
      "Marked words are no longer skipped: underlined/highlighted spans are split into individual words and included even when contrast is low.",
      "Tiny two-letter words that are marked are kept instead of being filtered out.",
      "Higher-res uploads (up to 2000px) give the model clearer text for consistent extractions.",
    ],
    technicalNotes: [
      "Prompt now forbids omission, requires per-word outputs for multi-word highlights, includes short words, and removes implicit count limits.",
      "Max tokens for the vision response raised to avoid truncation on longer sentences; temp remains 0 for determinism.",
      "Image preprocessing keeps aspect ratio and bumps max dimension to 2000px with LANCZOS to preserve fine annotations.",
    ],
  },
  {
    id: "photo-vocab-highlight-coverage",
    timestamp: "2025-12-11T08:00:00Z",
    title: "Photo vocab import keeps every highlighted word",
    summary:
      "Uploads that contain highlighted or underlined words now return the full set reliably, even when the handwriting is faint or the lighting is uneven.",
    userHighlights: [
      "Highlighted-only imports no longer drop random words; the extractor returns every marked token instead of a partial list.",
      "Faint or low-contrast marks are still included with a low-confidence flag instead of being skipped.",
      "Keeping the original aspect ratio and higher resolution preserves tiny annotations and pen underlines.",
    ],
    technicalNotes: [
      "Vision prompt now explicitly forbids skipping marked words and asks for best-effort guesses with low confidence instead of omission.",
      "Image preprocessing retains aspect ratio at up to 1400px using LANCZOS downscaling, removes destructive in-place overwrite, and encodes JPEG at higher quality.",
      "These changes make repeated uploads deterministic and keep all marked tokens available for selection.",
    ],
  },
  {
    id: "struggle-words-practice-texts",
    timestamp: "2025-12-07T09:10:00Z",
    title: "Practice texts for tricky words",
    summary:
      "Struggling vocabulary now triggers a dedicated practice text: we pick recent hard words, generate a short title and story, highlight the words in context, and surface a distinct card to revisit it.",
    userHighlights: [
      "A new banner offers an “Übungstext” when you have recent struggling words; after generation it hides for a day.",
      "The practice text card appears in your main list with a subtle badge, so you can always return to it.",
      "On the text detail page, the exact struggling words used are listed and highlighted in the text.",
    ],
    technicalNotes: [
      "Backend: `GeneratedText` stores `struggle_words`; generation pulls up to 8 low-streak vocab (last 30 days) in the selected language, injects them into the prompt, and derives a short title from the body.",
      "Async generation no longer hits the ORM; all struggle vocab is gathered synchronously, fixing SynchronousOnlyOperation errors and avoiding empty texts.",
      "Frontend: practice-text banner fetches struggle words daily and hides for 24h after use; detail view highlights the stored struggle words (filtered by text language); main cards show an “Übungstext” badge without special borders; form selections are remembered for the next practice text.",
    ],
  },
  {
    id: "photo-vocab-auto-translation",
    timestamp: "2025-12-05T13:00:00Z",
    title: "Photo vocab import: smarter translations & tags",
    summary:
      "Photo imports now auto-route translations per token, show the learning-language word with the mother-tongue translation, and land you on the photo tab after import.",
    userHighlights: [
      "Words extracted from photos show the learning-language term in “Wort” with the mother-tongue translation (and grammar info) in “Übersetzung”.",
      "Language badges reflect the actual source (e.g., French stays French); mixed-language imports can still be previewed before import.",
      "After finishing an import, the vocab list opens directly on the “Photo” tab.",
    ],
    technicalNotes: [
      "Finalize/preview auto-select targets per token (mother tongue vs. learning language), normalize language codes, and store target vs. source consistently.",
      "Vocabulary DTOs expose translation_language; the UI filters/badges via word_language for correct tagging.",
      "Success link carries ?tab=photo so the vocab list opens on the photo-import view.",
    ],
  },
  {
    id: "photo-vocabulary-import-ui",
    timestamp: "2024-05-28T12:00:00Z",
    title: "Photo vocabulary import (UX workflow)",
    summary:
      "Uploading a picture now launches a guided import flow: you choose whether to write a story or extract vocabulary, watch the AI read the photo, and cherry-pick the detected words before they land in a dedicated list.",
    userHighlights: [
      "Right after selecting a photo you decide between “Generate text” and “Import vocabulary,” so stories are never triggered by accident.",
      "The import view shows a real-time status card, each detected word with its sentence, and tools to select all, clear, or skip entries.",
      "Imported words appear in the new “Photo imports” tab inside the vocabulary list, making them easy to revisit.",
    ],
    technicalNotes: [
      "GenerateTextCard now polls the `/photo_vocabulary_imports/` endpoints, handles intent modals, status states, and lets users finalize token IDs.",
      "Result payloads are normalized client-side (deterministic token IDs, per-word sentence/language) before batching a finalize call.",
      "The vocabulary list API/UI supports an `origin=PHOTO_IMPORT` filter, exposed via a tab that reuses the infinite-query pipeline.",
    ],
  },
  {
    id: "photo-vocabulary-import-backend",
    timestamp: "2024-05-28T10:00:00Z",
    title: "Photo vocabulary import (backend groundwork)",
    summary:
      "Image uploads now branch into a dedicated vocabulary-import pipeline: the server extracts highlighted words or entire lists, tags the language, and serves them in a reviewable batch.",
    userHighlights: [
      "Uploading a picture asks whether you want to generate a story or import vocabulary, so no accidental text generations.",
      "Words imported from photos carry their original sentence context and appear in a dedicated list, making it easier to study book excerpts or word lists.",
      "Failed imports surface clear status/error messages so you know when the AI could not read a page.",
    ],
    technicalNotes: [
      "Backend adds `PhotoVocabularyImport` with Celery processing: OpenAI vision parses the photo, outputs JSON with word, sentence, language, and deterministic `token_id`.",
      "New REST endpoints let the client poll import status and finalize selected words; vocabulary entries now track `origin` and keep a pointer to the import batch.",
      "Translations work without `VocabularyUsage` thanks to a fallback that feeds the stored sentence directly into the translation task.",
    ],
  },
  {
    id: "spaced-repetition-response-time",
    timestamp: "2024-05-27T14:30:00Z",
    title: "Spaced Repetition respects thinking time",
    summary:
      "The trainer now records how long you think about a word before revealing it. Fast answers are rewarded; hesitant ones keep the word in a tougher state.",
    userHighlights: [
      "Only the time before the first tap is captured; you can take your time to pick Ja/Fast/Nein once the answer is visible.",
      "Ja: quick or steady answers advance the streak, slow clicks keep the word at the current difficulty.",
      "Fast and Nein push the word back sooner if you hesitated for a long time, focusing the queue on shaky vocabulary.",
    ],
    technicalNotes: [
      "Frontend: each card starts `responseTimer` on mount, locks it on the first tap, and sends `response_time_ms` with every Ja/Fast/Nein mutation.",
      "Backend: the DRF actions bucketize latency (≤2.5s, 2.5–8s, ≥8s) and mutate `winning_streak`/`last_tested` via helper functions.",
      "Effect: slow Ja responses stop incrementing the streak; slow Nein responses backdate `last_tested` more aggressively; fast Fast clicks leave streak untouched.",
    ],
  },
];

export const formatDateTime = (iso: string) =>
  new Intl.DateTimeFormat(undefined, {
    dateStyle: "long",
    timeStyle: "short",
  }).format(new Date(iso));

export const formatReleaseNumber = (entryNumber: number) =>
  entryNumber.toString().padStart(3, "0");

function ReleaseCard({
  translate,
  ...entry
}: ReleaseCardEntry & {
  translate: (key: string) => string;
}) {
  const releaseNumber = formatReleaseNumber(entry.entryNumber);
  return (
    <div
      className="rounded-[2rem] border border-slate-200 bg-white/95"
      data-testid="release-card"
    >
      <div className="p-5 sm:p-6">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2 text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
          <span>{formatDateTime(entry.timestamp)}</span>
          <span
            className="h-1 w-1 rounded-full bg-slate-300"
            aria-hidden="true"
          />
          <span data-testid="release-entry-number">
            {`${translate("product_updates_entry_label")} ${releaseNumber}`}
          </span>
        </div>
        <h3 className="mt-4 text-xl font-semibold leading-tight text-slate-900">
          {entry.title}
        </h3>
        <p className="mt-3 text-sm leading-relaxed text-slate-600">
          {entry.summary}
        </p>
        <div className="mt-5 grid gap-4">
          <ul className="space-y-1 text-sm text-slate-600 list-disc pl-5">
            {entry.userHighlights.map((bullet) => (
              <li key={bullet}>{bullet}</li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}

function SpacedRepetitionLogPage() {
  const { translate } = useTranslation();
  const latestTimestamp =
    releaseEntries[0]?.timestamp ?? new Date().toISOString();
  const latestEntryNumber = releaseEntries.length;
  return (
    <div className="w-full px-4 py-6 sm:px-8 lg:px-12">
      <div className="mx-auto max-w-4xl space-y-10">
        <header className="space-y-3">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
            {translate("product_updates_eyebrow")}
          </p>
          <div className="grid gap-4 rounded-[2rem] border border-slate-200 bg-gradient-to-br from-white via-slate-50 to-amber-50/60 p-5 sm:grid-cols-[minmax(0,1fr)_11rem] sm:p-6">
            <div className="space-y-4">
              <div className="space-y-2">
                <h1 className="text-3xl font-bold tracking-tight text-slate-900">
                  {translate("product_updates_title")}
                </h1>
                <p className="max-w-2xl text-sm leading-relaxed text-slate-600">
                  {translate("product_updates_description")}
                </p>
              </div>
              <div className="flex flex-wrap gap-5 text-sm text-slate-500">
                <p>{translate("product_updates_status_active")}</p>
                <p>
                  {`${translate("product_updates_last_update_label")} ${formatDateTime(
                    latestTimestamp,
                  )}`}
                </p>
              </div>
            </div>
            <div className="self-start rounded-[1.5rem] border border-slate-300 bg-white/90 p-4">
              <p className="text-[0.65rem] font-semibold uppercase tracking-[0.24em] text-slate-500">
                {translate("product_updates_current_label")}
              </p>
              <p
                className="mt-2 text-2xl font-semibold leading-none tracking-tight text-slate-900"
                data-testid="latest-release-number"
              >
                {`#${formatReleaseNumber(latestEntryNumber)}`}
              </p>
              <p className="mt-2 text-sm text-slate-600">
                {translate("product_updates_current_hint")}
              </p>
            </div>
          </div>
        </header>

        <section className="space-y-4">
          <div className="flex items-center gap-2">
            <CheckCircleIcon className="h-5 w-5 text-emerald-500" />
            <h2 className="text-xl font-semibold text-slate-900">
              {translate("product_updates_section_title")}
            </h2>
          </div>
          <p className="text-sm text-slate-600">
            {translate("product_updates_section_description")}
          </p>
          <div className="space-y-4">
            {releaseEntries.map((entry, index) => (
              <ReleaseCard
                key={entry.id}
                {...entry}
                entryNumber={releaseEntries.length - index}
                translate={translate}
              />
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}

export const Route = createFileRoute("/app/product_logbook")({
  component: SpacedRepetitionLogPage,
});
